import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Bug } from 'lucide-react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { canInvokeChannel, type ClientCapabilities } from '@volley/crucible-client';
import { useIpcQuery, useIpcMutation } from '../hooks/useIpc';
import {
  INITIAL_PROMPT_MESSAGE_ID_PREFIX,
  type AgentAskUserQuestionPush,
  type AgentSendAttachment,
  type AgentStreamChunk,
  type InterviewRecoverResult,
  type InlineCheckpoint,
  type InstallState,
  type ProjectRuntimeCapability,
  type ProjectRuntimeSnapshot,
  type ProtoHubSource,
} from '@volley/crucible-ipc-types/ipc-channels';
import type { ProtectedMainFallbackInfo } from '@volley/crucible/core/art-commit.js';
import type {
  AssetReviewItem,
  AssetReviewSnapshot,
  DesignBuildPlanSeed,
} from '@volley/crucible-ipc-types';
import { LoadingOverlay } from '../components/LoadingOverlay';
// #663 — one-shot clip pinned to the deploy overlay: plays the instant a
// build is sent to Bifrost ("your game is shipping to the player platform
// now"), then falls back to the loading loop for the rest of the deploy.
// Handled by Vite's asset pipeline, same as the LoadingOverlay clips.
import crucibleBifrost from '../../assets/videos/crucible_bifrost.mp4';
import { BuildReviewOverlay } from '../components/BuildReviewOverlay';
import { NorthStarConsumerSurface, TvSteeringSurface } from '../components/TvSteeringSurface';
import {
  IntegratedSoulInterview,
  type IntegratedSoulInterviewSnapshot,
} from '../components/IntegratedSoulInterview';
import {
  ScaffoldFirstGenreChip,
  shouldShowGenreChip,
  isScaffoldFirstInference,
} from '../components/ScaffoldFirstGenreChip';
import {
  readPersistedScaffoldFirst,
  pickDurableGenreInference,
  readCreatePrompt,
} from '../components/scaffold-first-storage';
import { buildRecastRouteState } from './NewGame';
import type { ScaffoldFirstInference } from '@volley/crucible/core/index.js';
import { TemplateUpdateBanner } from '../components/TemplateUpdateBanner';
import { AutoSeededToast } from '../components/AutoSeededToast';
import { InlineToast } from '../components/ui/toast';
import { Button } from '../components/ui/button';
import {
  UpdateTemplateModal,
  synthesiseConflictResolutionPrompt,
} from '../components/UpdateTemplateModal';
import { DesignWorkspace } from './design/DesignWorkspace';
import { ArtWorkspace } from '../components/ArtWorkspace';
import { DeployFailureCard } from '../components/DeployFailureCard';
import { useDeployFailure } from '../hooks/useDeployFailure';
import { DeployStatusCard, deployStatusHasContent } from '../components/DeployStatusCard';
import { useDeployStatus } from '../hooks/useDeployStatus';
import { useDeployUpToDate, invalidateDeployUpToDate } from '../hooks/useDeployUpToDate';
import { useAssetReviewSurface } from '../hooks/useAssetReviewSurface';
import { unflattenIpcError } from '@volley/crucible-ipc-types/error-types';
import {
  shouldReEnqueueDependencyRequest,
  shouldReEnqueueTemplateFileOverrideRequest,
  type ChatEffect,
} from '../chat/state';
import { useChatModule } from '../chat/useChatModule';
import { useElapsedTick } from '../hooks/useElapsedTick';
// ChatPanel + its sub-components extracted to `../chat/ChatPanel`
// (plan 001 Step 5.4). Imported for the Workspace render + logic that
// still lives here; test-facing helpers re-exported below.
import {
  autoBuildStatusFromRunState,
  ChatPanel,
  ChatPanelPlaceholder,
  hasFreshRunningActivitySnapshot,
} from '../chat/ChatPanel';
import type { ChatMessage, MessageBlock, ToolCallEntry } from '../chat/types';
// Pure tool-call classifier + label helpers extracted to
// `../chat/tool-call-classification` (plan 001 Step 5.1). Imported for the
// in-file chat UI that still lives here (ToolCallCard, the New-UI rows, etc.)
// AND re-exported below so existing `../pages/Workspace` test imports keep working.
import {
  buildGameInterviewEditLabel,
  buildToolCallFriendlyLabel,
  groupNewUiToolCallsForDisplay,
  isBashDidYouMeanError,
  isBashPolicyDenialFailure,
  isEditMatchFailedFailure,
  isEditNoopFailure,
  isEditReplaceAllRecoverableFailure,
  isGlobTooManyResultsError,
  isGrepMissingPathFailure,
  isPedagogicalToolError,
  isReadFileNotFoundFailure,
  isReadTooLargeError,
  isRecoverableToolError,
  pedagogicalToolErrorLabel,
  recoverableToolErrorLabel,
  toolSubjectText,
  toolVerbPast,
  toolVerbPresent,
  type NewUiToolCallDisplaySegment,
} from '../chat/tool-call-classification';
// Chat message rendering components extracted to
// `../chat/ChatMessageComponents` (plan 001 Step 5.2). Imported for the
// in-file chat UI (ChatPanel) that still lives here; re-exported below for
// existing `../pages/Workspace` test imports.
import {
  composerAnswerErrorMessage,
  decideComposerSend,
  MessageContent,
} from '../chat/ChatMessageComponents';
import { parseMarkdownQuestion } from '../chat/markdown-question';
import { ArtifactPane } from '../components/ArtifactPane';
import { ChatErrorBoundary, ChatSurfaceContext } from '../components/ChatErrorBoundary';
import { DependencyApprovalModal } from '../components/DependencyApprovalModal';
import { TemplateFileOverrideModal } from '../components/TemplateFileOverrideModal';
import type { PreviewPhaseGate } from '../components/PreviewLaunchButton';
import { PreviewModule, type PreviewModuleOverrides } from '../preview/PreviewModule';
import { WorkspaceRightPanel } from '../components/WorkspaceRightPanel';
import { DiagnosticsTab } from '../components/DiagnosticsTab';
import { usePreviewModule } from '../preview/usePreviewModule';
import { InstallStateChip } from '../components/InstallStateChip';
import { PhaseStrip } from '../components/PhaseStrip';
import { BuildPartialBanner } from '../components/BuildPartialBanner';
import { usePhaseProgress } from '../hooks/usePhaseProgress';
import { useFoundryGameStateSnapshot } from '../hooks/useFoundryGameStateSnapshot';
import {
  isPreviewPhaseReadyForAutoStart,
  previewPhaseAutoStartEvidenceKey,
} from '../workspace/preview-state';
import { useInterviewStatus, useStartOverInterview } from '../hooks/useBuildGameInterview';
import { usePhaseEventsStore } from '../stores/phase-events';
import { useInterviewState } from '../hooks/useBuildGameInterview';
import {
  formatInterviewRecoveryError,
  isCloudRuntimeUpdateRequired,
  recoverInterviewWithBackoff,
} from '../workspace/interview-recovery';
import { PlanModeView } from '../components/PlanModeView';
import { PlanWorkspace, type PlanSaveWitness } from '../components/PlanWorkspace';
import { CentreColumnWithBanner } from '../components/CentreColumnWithBanner';
import { BackToPlanConfirmModal } from '../components/BackToPlanConfirmModal';
import { ActivityCheckpointMessage } from '../components/ActivityCheckpointMessage';
import { WorkspaceHeader } from '../components/WorkspaceHeader';
import { PreviewDevLoopBanner } from '../components/PreviewDevLoopBanner';
import { FilesPanel } from '../components/WorkspaceFilesPanel';
import {
  NorthStarRightRail,
  WorkspaceChatSurface,
  type NorthStarRailPanels,
} from '../components/WorkspaceChatSurface';
import { FurnaceErrorBanner } from '../components/FurnaceErrorBanner';
import { VmDebugPane } from '../components/VmDebugPane';
import { ProtoHubSimulatorSurface } from '../components/ProtoHubSimulatorSurface';
import { useMaintainerMode } from '../hooks/useMaintainerMode';
import { useActivitySnapshot } from '../hooks/useActivitySnapshot';
import { useGameStatusLite } from '../hooks/useGameStatusLite';
import { useFeatureFlags, type FeatureFlagsState } from '../hooks/useFeatureFlags';
import { useClientCapabilities, useCrucibleClient } from '../transport/context';
import { projectQueryKey } from '../transport/project-query-key';
import { useHostBridge } from '../transport/host-bridge';
import { canDisplayOnHub, canPublishRun } from '@volley/crucible-client';
import { useVmConnectionState } from '../hooks/useVmConnectionState';
import { computeFurnaceBuildState, type FurnaceBuildState } from '../workspace/furnace-build-state';
import {
  classifyFurnaceError,
  scrubInfrastructureUrls,
  scrubSerialisedError,
  type FurnaceErrorView,
} from '../workspace/furnace-error-mapping';
import { useInterviewStatePoll } from '../hooks/useInterviewStatePoll';
import {
  applyWorkspacePublishProgress,
  routePostDeployProtoHub,
} from '../workspace/publish-progress-policy';
import type { PhaseProgressUpdate } from '@volley/crucible-ipc-types/ipc-channels';
import type {
  BuildGameInterviewState,
  PhaseBDecision,
} from '@volley/crucible-ipc-types/build-game-types';
import {
  getEffectiveInterviewDepth,
  getEffectiveInterviewSubPhase,
  getEffectivePlan,
} from '@volley/crucible-ipc-types/build-game-types';
import { savedPlanMatchesBuilt } from '../components/plan-state';
import {
  deriveWorkspaceRunState,
  hasFreshPhaseBProgressRecord,
  isCompletionVerificationRetryRunState,
  resolvePendingPhaseBDecision,
  type InitialKickoffFailureReason,
  type InitialKickoffState,
  type InitialKickoffStatus,
  type WorkspaceRunKind,
  type WorkspaceRunState,
} from '../workspace/run-state';
import { useActivityLiveness } from '../hooks/useActivityLiveness';
import {
  readCreateHandoffModeFromRouteState,
  readCreateInitialPromptFromRouteState,
  readCreateRuntimePendingFromRouteState,
  readDeferredDesignHandoffFromRouteState,
  resolveCreateHandoffModeForWorkspaceEntry,
} from './create-handoff';
import { probeAdaptiveIntakeOnWorkspaceMount } from '../workspace/adaptive-intake-mount';
import {
  deriveDeployActionState,
  deriveDeployBuildReadiness,
  evaluateDeployReadiness,
  hasActiveLegacyDeployRebuild,
  hasDirectLegacyDeployBuildLiveness,
  isFreshLegacyDeployHandoff,
  refusedDeployStateUpdates,
  resolveDeployReadinessStatus,
  type DeployBlockReason,
  type DeployState,
} from '../workspace/deploy-action-state';
import { deployFailureCopy } from '../workspace/deploy-failure-copy';
import { buildDeployFailureBugReport } from '../workspace/deploy-bug-report';
import { useAppFrame } from '../AppFrameContext';
import { DeployConfirmModal } from '../components/DeployConfirmModal';
import {
  localBuildUnavailableReason,
  useLocalOperationalReadiness,
} from '../local-operational-readiness';
import {
  centreChatSurfaceVisibleFor,
  derivePreviewLaunchPillState,
  derivePreviewPaneBlockedState,
  initialWorkspaceSurfaceForEntry,
  isActiveAutoBuild,
  planHandoffVisibleFor,
  previewBlockedPaneCopy,
  previewSessionReadyForGame,
  showNewUiBuildViewFor,
} from '../workspace/plan-build-handoff';
import { PreviewBlockedPane } from '../preview/PreviewBlockedPane';
import { usePlanReviewNavigation } from '../workspace/use-plan-review-navigation';
import { useBuildKickoffWatchdog } from '../workspace/use-build-kickoff-watchdog';
import { runDesignStartBuild } from '../workspace/design-start-build';
import type {
  CloneDeployAccessWarning,
  WorkspaceHeaderMode,
  WorkspaceMode,
} from '../workspace/workspace-view-types';
// Re-export so existing imports of these types from `../pages/Workspace` keep working.
export type { CloneDeployAccessWarning, WorkspaceHeaderMode, WorkspaceMode };

type AgentSendEffect = Extract<ChatEffect, { type: 'ipc/agent-send' }>;

/**
 * Default width (px) of the chat panel in the side-by-side Develop
 * layout — matched to the right side panel (`w-96` = 384px). The preview
 * pane flexes to fill the rest. Drag-adjustable + persisted per-game.
 */
const DEFAULT_LIVE_PREVIEW_CHAT_WIDTH = 384;

const FAST_BUILD_PLAN_BRIDGE_TIMEOUT_MS = 90_000;

/**
 * Selects the Workspace handler that should back the Now-summary resume CTA.
 *
 * Args:
 *   runState: The aggregate run state currently shown by the workspace.
 *
 * Returns:
 *   The resume action kind to invoke, or null when no primary resume action exists.
 *
 * Raises:
 *   Never.
 */
export function resumeBuildActionKindFromRunState(
  runState: WorkspaceRunState,
):
  | 'retry-interview'
  | 'start-building'
  | 'retry-auto-build'
  | 'resume-stalled-build'
  | 'rebuild-from-plan'
  | null {
  if (runState.kind !== 'needs-attention' || runState.primaryCta !== 'retry') return null;

  if (runState.debugSource.startsWith('kickoff:')) {
    return 'retry-interview';
  }

  if (runState.debugSource.startsWith('intake-authority:')) {
    return 'retry-interview';
  }

  if (runState.debugSource === 'interview-status:in-progress:stale') {
    return 'retry-interview';
  }

  if (isCompletionVerificationRetryRunState(runState)) {
    return 'rebuild-from-plan';
  }

  if (runState.debugSource.endsWith(':stale-build')) {
    return 'resume-stalled-build';
  }

  // Stale final-review states recover through the same build kickoff as watchdog stalls.
  if (
    runState.debugSource.includes('watchdog') ||
    runState.debugSource.endsWith(':stale-final-review')
  ) {
    return 'start-building';
  }

  // Auto-build failures retry the auto-pipeline setup instead of re-sending kickoff.
  return runState.debugSource.startsWith('auto-build:') ? 'retry-auto-build' : null;
}

/** Clears settled intake evidence without erasing a different game's warning. */
export function clearIntakeAuthorityMissingForGame(
  missingGameId: string | null,
  completedGameId: string | undefined,
): string | null {
  return completedGameId && missingGameId === completedGameId ? null : missingGameId;
}

/**
 * Checks whether a workspace mode is exempt from the asset-review
 * auto-redirect-to-chat effect.
 *
 * Args:
 *   mode: The workspace mode active when a build-time asset review is detected.
 *
 * Returns:
 *   True when the mode should be left alone; false when the effect should
 *   force workspaceMode back to 'chat'.
 *
 * Raises:
 *   Never.
 */
export function workspaceModeSurvivesAssetReviewRedirect(mode: WorkspaceMode): boolean {
  // crucible#2611 Task 4: the Art tab shows the SAME build-time review
  // (attached via art:open's already-open path) in a dedicated space instead
  // of inline-in-chat, so it must be exempted from the force-navigate-to-chat
  // below the same way 'chat'/'preview' already were.
  // crucible#2974: 'design' and 'protohub' have the identical gap — both are
  // workspaceMode values reached by an explicit header-pill click, and
  // neither was added to this exemption list when they became separate
  // modes. Without the exemption, clicking Design/protohub while a review is
  // detected for the game flips workspaceMode for one render and the effect
  // immediately snaps it back to 'chat' on the very next tick —
  // indistinguishable from the click doing nothing.
  return (
    mode === 'chat' ||
    mode === 'preview' ||
    mode === 'art' ||
    mode === 'design' ||
    mode === 'protohub'
  );
}

/**
 * Issue #643 (Codex review #1 BLOCK) — the previewToast auto-fade policy,
 * extracted for crucible#3819.
 *
 * Args:
 *   tone: The active preview toast's tone.
 *
 * Returns:
 *   `null` for an error toast — it's sticky; the user must dismiss it via
 *   the close button, since losing failure visibility before it's read
 *   would be worse than staying visible. Otherwise the auto-fade delay in
 *   milliseconds (info/success toasts are confirmation, not actionable).
 *
 * Raises:
 *   Never.
 */
export function previewToastAutoFadeDelayMs(tone: 'info' | 'success' | 'error'): number | null {
  return tone === 'error' ? null : 4000;
}

/**
 * The confirmation toast `handleRetryInitialInterview` shows after a manual
 * interview-recovery retry succeeds, extracted for crucible#3819.
 *
 * Args:
 *   result: The resolved `recoverInitialInterview('manual-retry')` outcome.
 *
 * Returns:
 *   Copy naming which recovery path fired: a fresh restart from saved
 *   answers, a replayed pending question, or (for `'active'` /
 *   `'not-needed'`) a generic reconnect message.
 *
 * Raises:
 *   Never.
 */
export function interviewRetryToastMessage(result: InterviewRecoverResult): {
  tone: 'info';
  message: string;
} {
  return {
    tone: 'info',
    message:
      result.kind === 'started'
        ? 'Interview restarted from your saved answers.'
        : result.kind === 'replayed'
          ? 'Interview question restored.'
          : 'Interview reconnected to its latest state.',
  };
}

/**
 * crucible#2611 Task 4 — whether the floating `BuildReviewOverlay` modal
 * should be suppressed, extracted for crucible#3819.
 *
 * Args:
 *   intakeGateEnabled: `flags.intake` — the collapsed intake gate
 *     (crucible#3313). The overlay is already suppressed under intake for
 *     reasons unrelated to this predicate's own history.
 *   mode: The active workspace mode.
 *
 * Returns:
 *   True when the floating modal must stay hidden. The Art tab mounts its
 *   OWN in-place `AssetReviewPanel` for the focused game; without this
 *   exemption, a build-time review for the same game could pop the
 *   floating modal on top of it — two conflicting review surfaces for the
 *   same review at once.
 *
 * Raises:
 *   Never.
 */
export function buildReviewOverlaySuppressed(
  intakeGateEnabled: boolean,
  mode: WorkspaceMode,
): boolean {
  return intakeGateEnabled || mode === 'art';
}

/**
 * The Design tab's embedded `PlanWorkspace` needs to know whether the plan
 * it is showing is still being drafted (vs. ready for review), extracted
 * for crucible#3819.
 *
 * Args:
 *   runStateKind: `workspaceRunState.kind`.
 *
 * Returns:
 *   True while the run state is actively drafting the plan.
 *
 * Raises:
 *   Never.
 */
export function designBuildPlanDraftPending(runStateKind: WorkspaceRunKind): boolean {
  return runStateKind === 'drafting-plan';
}

/**
 * The invoke channel that backs each capability-gated feature in the Cloud
 * runtime, extracted for crucible#3819. `resolveProjectCapability` reads
 * this via `canInvokeChannel` when no desktop-host `ProjectRuntimeSnapshot`
 * capability entry exists (i.e. `projectPlacementAuthority === 'cloud-runtime'`).
 *
 * This is the *intended* channel threaded through the authority-checking
 * `canInvokeChannel(clientCapabilities, …)` call, not a raw bridge invoke —
 * see the README's §2b note on why this isn't the crucible#3807 class of
 * pin (a raw invoke that bypasses project authority).
 */
export const BROWSER_CAPABILITY_CHANNEL = {
  agent: 'agent:send',
  preview: 'dev:start',
  publish: 'publish:run',
  history: 'agent:history',
  'game-state': 'game-state:get',
  chat: 'chat:load',
  'build-plan': 'build-game:get-interview-state',
  delete: 'game:delete',
  files: 'game:files',
  editor: 'game:open-in-editor',
  rename: 'game:set-display-name',
  'tile-edit': 'game:set-tile-image',
  design: 'design:openProject',
  art: 'art:open',
  'template-update': 'template:check-updates',
  'local-devices': 'vwr:setup-device',
  'native-simulator': 'protohub:open',
  'deploy-diagnostics': 'deploy:get-failure-context',
} as const satisfies Record<ProjectRuntimeCapability, string>;

/**
 * Whether the desktop-only `project-runtime:get` query should fire,
 * extracted for crucible#3819.
 *
 * Args:
 *   gameId: The route's game id, if any.
 *   projectPlacementAuthority: `clientCapabilities.projectPlacementAuthority`.
 *
 * Returns:
 *   True only for a `desktop-host`-authoritative renderer (Electron) with a
 *   game in scope. The snapshot this query returns is Electron-only; a
 *   browser (`cloud-runtime`) renderer must never invoke it and instead
 *   falls back to `resolveProjectCapability`'s `canInvokeChannel` branch.
 *
 * Raises:
 *   Never.
 */
export function projectRuntimeQueryEnabled(
  gameId: string | undefined,
  projectPlacementAuthority: ClientCapabilities['projectPlacementAuthority'],
): boolean {
  return !!gameId && projectPlacementAuthority === 'desktop-host';
}

/**
 * Resolves a single capability's availability, extracted for crucible#3819.
 *
 * Args:
 *   capability: The capability being checked.
 *   projectRuntime: The desktop-host `project-runtime:get` snapshot, if the
 *     query fired (see `projectRuntimeQueryEnabled`).
 *   clientCapabilities: The renderer's transport-level capabilities.
 *
 * Returns:
 *   The desktop-host snapshot's own verdict when present (Electron is
 *   authoritative on its own capabilities). Otherwise, for a `cloud-runtime`
 *   renderer, derives availability from whether the browser transport can
 *   invoke the capability's backing channel (`BROWSER_CAPABILITY_CHANNEL`).
 *   `undefined` when neither source can answer (e.g. desktop-host authority
 *   but the query hasn't resolved yet).
 *
 * Raises:
 *   Never.
 */
export function resolveProjectCapability(
  capability: ProjectRuntimeCapability,
  projectRuntime: ProjectRuntimeSnapshot | undefined,
  clientCapabilities: ClientCapabilities,
): { available: boolean; reason: string | null } | undefined {
  const desktopCapability = projectRuntime?.capabilities[capability];
  if (desktopCapability) return desktopCapability;
  if (clientCapabilities.projectPlacementAuthority !== 'cloud-runtime') return undefined;
  const available = canInvokeChannel(clientCapabilities, BROWSER_CAPABILITY_CHANNEL[capability]);
  return {
    available,
    reason: available ? null : 'This capability is unavailable in the Cloud runtime.',
  };
}

/**
 * The boolean form of `resolveProjectCapability`, extracted for
 * crucible#3819.
 *
 * Args:
 *   capability: The capability being checked.
 *   projectRuntime: See `resolveProjectCapability`.
 *   clientCapabilities: See `resolveProjectCapability`.
 *   placement: The project's placement ('local' | 'cloud').
 *
 * Returns:
 *   `resolveProjectCapability`'s verdict when it has one; otherwise `true`
 *   for a local project (no runtime-authority gate applies) and `false`
 *   for a cloud project with no answer yet — fails closed rather than
 *   flashing a capability on before the runtime confirms it.
 *
 * Raises:
 *   Never.
 */
export function resolveProjectCapabilityAvailable(
  capability: ProjectRuntimeCapability,
  projectRuntime: ProjectRuntimeSnapshot | undefined,
  clientCapabilities: ClientCapabilities,
  placement: 'local' | 'cloud',
): boolean {
  return (
    resolveProjectCapability(capability, projectRuntime, clientCapabilities)?.available ??
    placement !== 'cloud'
  );
}

/**
 * crucible#3497 review round 3 (MEDIUM) — whether the art:deploy-fallback-pr
 * toast should render as an error, extracted for crucible#3819.
 *
 * Args:
 *   toast: The last `art:deploy-fallback-pr` push payload, or `null` before
 *     one has arrived.
 *
 * Returns:
 *   True when a human needs to notice this toast: either the working tree
 *   is stranded off `main` (`strandedOnBranch` set — `commitArtRevision`'s
 *   branch guard will silently refuse every LATER Art revision until fixed
 *   by hand), or the auto-merge mutation itself failed (`!armed`).
 *   Stranding DOMINATES `armed` — a stranded-but-armed toast is still an
 *   error, because THIS PR merging fine doesn't fix the stuck checkout.
 *
 * Raises:
 *   Never.
 */
export function deriveProtectedMainFallbackIsError(
  toast: ProtectedMainFallbackInfo | null,
): boolean {
  return Boolean(toast && (toast.strandedOnBranch || !toast.armed));
}

/**
 * The art:deploy-fallback-pr toast's auto-fade delay, extracted for
 * crucible#3819 (same policy shape as `previewToastAutoFadeDelayMs`).
 *
 * Args:
 *   isError: `deriveProtectedMainFallbackIsError(toast)`'s verdict.
 *
 * Returns:
 *   `null` (sticky, dismiss-only) when the toast is an error — a stranded
 *   or unarmed toast needs a human to notice it. Otherwise 8000ms.
 *
 * Raises:
 *   Never.
 */
export function protectedMainFallbackAutoFadeDelayMs(isError: boolean): number | null {
  return isError ? null : 8000;
}

/** The rendered view of an `art:deploy-fallback-pr` toast, extracted for crucible#3819. */
export interface ProtectedMainFallbackToastView {
  tone: 'error' | 'info';
  /** Whether the toast shows a dismiss button — gated on the SAME derived
   *  value as `tone`, so an armed-but-stranded toast still gets one. */
  dismissible: boolean;
  message: string;
}

/**
 * Computes the art:deploy-fallback-pr toast's tone, dismissibility, and
 * message, extracted for crucible#3819. Stranding dominates the message
 * choice the same way it dominates `tone`/`dismissible` — a stranded
 * checkout is still-actionable news even when the PR itself armed fine.
 *
 * Args:
 *   toast: The `art:deploy-fallback-pr` push payload.
 *
 * Returns:
 *   The view model the JSX renders from directly.
 *
 * Raises:
 *   Never.
 */
export function protectedMainFallbackToastView(
  toast: ProtectedMainFallbackInfo,
): ProtectedMainFallbackToastView {
  const isError = deriveProtectedMainFallbackIsError(toast);
  const message = toast.strandedOnBranch
    ? `Art revision opened ${toast.owner}/${toast.repo}#${toast.number} — but the game folder is stuck on "${toast.strandedOnBranch}". Art saves are paused until it's back on main.`
    : toast.armed
      ? `Art revision opened ${toast.owner}/${toast.repo}#${toast.number} — merges automatically once checks pass.`
      : `Art revision opened ${toast.owner}/${toast.repo}#${toast.number}, but couldn't auto-merge — a human needs to merge it manually.`;
  return { tone: isError ? 'error' : 'info', dismissible: isError, message };
}

function phaseProgressInImplementation(progress: PhaseProgressUpdate | null | undefined): boolean {
  return (
    progress?.kind === 'derived' && progress.derived.current > 2 && progress.derived.current < 6
  );
}

function phaseProgressStartedAtMs(progress: PhaseProgressUpdate | null | undefined): number | null {
  if (progress?.kind !== 'derived') return null;
  const current = progress.derived.current;
  const transition = [...progress.derived.transitions]
    .reverse()
    .find((entry) => entry.phase === current);
  if (!transition?.at) return null;
  const parsed = Date.parse(transition.at);
  return Number.isFinite(parsed) ? parsed : null;
}

export function stalledBuildResumeAttemptKey(
  gameId: string | undefined,
  progress: PhaseProgressUpdate | null | undefined,
): string | null {
  if (!gameId || !phaseProgressInImplementation(progress) || progress?.kind !== 'derived') {
    return null;
  }
  return `${gameId}:${progress.derived.current}:${phaseProgressStartedAtMs(progress) ?? 'unknown'}`;
}

/**
 * Records that the current implementation phase already has a resume attempt.
 * A successful Start Building dispatch counts: emitting an additional generic
 * stale-build chat turn for the same phase would race the accepted kickoff.
 */
export function markStalledBuildResumeAttempted(
  attempted: Set<string>,
  gameId: string | undefined,
  progress: PhaseProgressUpdate | null | undefined,
): string | null {
  const attemptKey = stalledBuildResumeAttemptKey(gameId, progress);
  if (attemptKey) attempted.add(attemptKey);
  return attemptKey;
}

export function buildStalledBuildResumePrompt(
  progress: PhaseProgressUpdate | null | undefined,
  hasPlan: boolean,
): string {
  const opening =
    progress?.kind === 'derived'
      ? `Resume the stalled build from ${progress.derived.label || `phase ${progress.derived.current}`}${
          progress.derived.subPhase ? ` (${progress.derived.subPhase})` : ''
        }.`
      : 'Resume the stalled build.';

  // crucible#3052 — a build can start with NO plan at all (the wait-for-plan
  // guard's own best-effort fallback: the design agent's plan never landed
  // within budget, or it failed, so kickoff proceeded from the bare seed
  // anyway). If that build later stalls, there is no authoritative `plan`
  // field to point the resuming agent at — telling it one exists anyway would
  // be actively misleading. Have it infer scope from what's already on disk
  // instead of guessing at a plan that was never written.
  if (!hasPlan) {
    return [
      opening,
      'No design plan was authored for this build (it started from a best-effort pitch without one). Infer the intended scope from docs/build-progress.json (what has already been implemented) and the code already written in the repo, then continue from there.',
      'If the intended scope is genuinely unclear from the existing code and progress record, stop and explain what needs attention instead of guessing or restarting from scratch.',
    ].join(' ');
  }

  return [
    opening,
    'The game design plan is the source of truth for what to build. Its authoritative, always-current form is the `plan` field of build-game-interview.json in the game state directory under .foundry (alongside the locked design system, design-system.json). design/build-plan.md is a prose rendering of that plan that can lag behind it after a mid-build edit, so read the build-game-interview.json `plan` field and prefer it if the two ever disagree.',
    'docs/build-progress.json is NOT the plan: it only records which build steps are already complete; use it to preserve finished work, then continue implementing the remaining steps toward the plan.',
    'If you cannot safely continue, explain what needs attention instead of restarting from scratch.',
  ].join(' ');
}

export interface InitialKickoffRendererReadySignals {
  tokensResolved: boolean;
  hasAnthropicKey: boolean;
  flagsLoaded: boolean;
  interviewEngineV1: boolean;
  interviewRendererReady: boolean;
  /**
   * Issue #2596 — setup (background install) readiness. Since #2561 Part B
   * the create flow drops the user into the workspace as soon as the
   * interview seed is written (BEFORE clone/install/build finish). If the
   * `/build-game` auto-kickoff fires during that pre-engine setup window,
   * the turn claims `session.inFlight` in the adapter's pre-engine setup
   * window and any second send (or a re-fire) collides with the bouncer,
   * which cannot auto-retry a pre-engine turn and surfaces
   * `CRUCIBLE-FOUNDRY-TURN-IN-FLIGHT`; an unguarded throw in that
   * now-earlier window can also take the app down. Gating the kickoff on
   * "setup is no longer actively installing" keeps the turn OUT of the
   * adapter until it can safely run, so the collision can't happen. The
   * early workspace nav (the #2561 UX win) is unaffected — it navigates on
   * `create:setup-ready`; only the build-kickoff turn waits here.
   *
   * `false` while the install-state sentinel (#2584) reports `installing`;
   * `true` once it clears to `ok`/`failed`/absent (a genuine install
   * failure surfaces its own card; a missing sentinel means no create
   * install is in flight, e.g. opening an existing game). The sentinel's
   * own 10-minute stale-sweep is the backstop against a wedged
   * `installing` record, so this can never hold the kickoff forever.
   */
  setupReady: boolean;
}

export function shouldHoldInitialKickoffForRendererReady({
  tokensResolved,
  hasAnthropicKey,
  flagsLoaded,
  interviewEngineV1,
  interviewRendererReady,
  setupReady,
}: InitialKickoffRendererReadySignals): boolean {
  if (!tokensResolved) return true;
  if (!hasAnthropicKey) return false;
  if (!flagsLoaded) return true;
  // Issue #2596 — hold the kickoff while the background create-install is
  // still running. Checked BEFORE the engine-flag gate below because the
  // quick-interview path (interviewEngineV1 on, but the SKILL owns Phase
  // A) collides with the same setup window; the wait must apply
  // regardless of which interview surface owns Phase A.
  if (!setupReady) return true;
  if (!interviewEngineV1) return false;
  return !interviewRendererReady;
}

/**
 * Issue #2596 — derive the `setupReady` kickoff signal from the persisted
 * install-state sentinel (#2584).
 *
 * The kickoff should hold ONLY while a create-install is actively running
 * (`status: 'installing'`). Every other observation means the kickoff can
 * safely proceed:
 *   - `null` (no sentinel on disk) → no create-install in flight (e.g.
 *     opening an already-built game, or the record hasn't been written
 *     yet on a brand-new game — the worst case is the kickoff fires a beat
 *     early, exactly as it did pre-#2596 for non-create opens).
 *   - `'ok'` → the background install finished; safe to build.
 *   - `'failed'` → the install failed; let the turn proceed so the
 *     agent/deps-gate surfaces the failure card rather than hanging the
 *     kickoff forever behind a wait that will never clear.
 *
 * Pure + exported so the gate contract is unit-testable without mounting
 * the workspace.
 */
export function deriveKickoffSetupReady(installState: InstallState | null | undefined): boolean {
  return installState?.status !== 'installing';
}

/** Wall-clock budget for renderer-ready initial kickoff (Workspace timeout effect). */
export const INITIAL_KICKOFF_TIMEOUT_MS = 15_000;

/**
 * Issue #2596 — poll interval for the kickoff setup-ready probe. Only runs
 * while the kickoff is actively held; short enough that the build starts
 * promptly once the background install clears, cheap enough (one small IPC
 * read) that the bounded window is negligible.
 */
export const KICKOFF_SETUP_READY_POLL_MS = 400;

export interface UseKickoffSetupReadyArgs {
  gameId: string | undefined;
  /** True only while the auto-kickoff is in an active pre-dispatch status. */
  active: boolean;
  /** Called with the derived setup-readiness on every observation. */
  onReadyChange: (ready: boolean) => void;
  /**
   * Called once at the false→true transition (setup finished while the
   * kickoff was held). Lets the caller refresh the renderer-ready timeout
   * so a long install doesn't eat that budget. Optional.
   */
  onSetupCleared?: () => void;
  /** Poll interval override (tests). Defaults to {@link KICKOFF_SETUP_READY_POLL_MS}. */
  pollMs?: number;
}

/**
 * Issue #2596 — reactive setup-readiness for the `/build-game` auto-kickoff.
 *
 * While `active`, polls the #2584 install-state sentinel via `install-state:get`
 * and reports readiness through `onReadyChange` (`false` only while
 * `installing`, per {@link deriveKickoffSetupReady}). Runs ONLY while active:
 * a normal game open never enters an active kickoff status, so the poll is
 * dormant outside the create-handoff window. When it goes inactive it reports
 * `true` so a stale `false` from a previous kickoff can't wedge a later one.
 * A probe failure is treated as ready (never wedge the kickoff behind a wait
 * that can't clear; downstream deps-gate surfaces any real problem).
 *
 * Exported so the timing contract is unit-testable via `renderHook` without
 * mounting the full workspace.
 */
export function useKickoffSetupReady({
  gameId,
  active,
  onReadyChange,
  onSetupCleared,
  pollMs = KICKOFF_SETUP_READY_POLL_MS,
}: UseKickoffSetupReadyArgs): void {
  const client = useCrucibleClient();
  // Latest callbacks in refs so the poll effect only re-subscribes on the
  // real inputs (gameId / active / pollMs), not on every render's fresh
  // callback identity.
  const onReadyChangeRef = useRef(onReadyChange);
  onReadyChangeRef.current = onReadyChange;
  const onSetupClearedRef = useRef(onSetupCleared);
  onSetupClearedRef.current = onSetupCleared;
  // Tracks whether the last observation held the kickoff for setup, so the
  // false→true transition can fire `onSetupCleared` exactly once.
  const heldForSetupRef = useRef(false);

  useEffect(() => {
    if (!gameId || !active) {
      onReadyChangeRef.current(true);
      heldForSetupRef.current = false;
      return;
    }
    let cancelled = false;
    const probe = async (): Promise<void> => {
      let ready = true;
      try {
        const state = await client.invoke('install-state:get', { gameId });
        ready = deriveKickoffSetupReady(state);
      } catch {
        ready = true;
      }
      if (cancelled) return;
      onReadyChangeRef.current(ready);
      if (ready && heldForSetupRef.current) {
        heldForSetupRef.current = false;
        onSetupClearedRef.current?.();
      } else if (!ready) {
        heldForSetupRef.current = true;
      }
    };
    void probe();
    const timer = setInterval(() => void probe(), pollMs);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [gameId, active, pollMs]);
}

export interface UseDesignAgentStatusRecoveryArgs {
  gameId: string | undefined;
  /**
   * Called once per `gameId` when the durable marker records a terminal
   * design-agent failure. The argument is the failure's OWN timestamp
   * (parsed from the persisted record), not `Date.now()` — the failure may
   * have happened well before this probe ran (e.g. while the user was on
   * the Dashboard, or on a prior visit to this game).
   */
  onRecoveredFailure: (failedAt: number) => void;
}

/**
 * Fable-review fix (2026-07-16) — `designSystemFailedAt` (state declared in
 * `Workspace`, citing crucible#3183) previously hydrated from exactly ONE source: the live
 * `create:build-step` push. A push missed while the user was elsewhere
 * (Dashboard, another game), or a `gameId` switch (`Workspace` never
 * remounts — its per-game reset effect is the only thing that runs), left
 * the field unset forever: `deriveWorkspaceRunState`'s `needs-attention`
 * guard (run-state.ts) never fires and the "drafting-plan" branch spins
 * with no backstop — the exact bug class #3183 closed, just for the
 * "watching when it happened" case rather than the general one.
 *
 * `writeDesignAgentStatus` (crucible core `create.ts`) already persists a
 * durable `.foundry/persistent/design-agent-status.json` breadcrumb at
 * create time, independent of the live push. This hook probes that file
 * once per `gameId` (via the read-only `design-agent-status:get` channel,
 * which mirrors `install-state:get`'s shape) and reports a terminal
 * `'failed'` status back to the caller — the same "recover from a durable
 * marker instead of trusting only the live push" pattern as the northstar
 * steering-panel race fix (crucible#2182). A probe failure (including the
 * channel failing closed for a Cloud-placed game) is swallowed: this is a
 * best-effort recovery path, never a new way for the workspace to error.
 *
 * Exported (mirrors `useKickoffSetupReady`) so the probe/parse contract is
 * unit-testable via `renderHook` without mounting the full workspace.
 */
export function useDesignAgentStatusRecovery({
  gameId,
  onRecoveredFailure,
}: UseDesignAgentStatusRecoveryArgs): void {
  const client = useCrucibleClient();
  const onRecoveredFailureRef = useRef(onRecoveredFailure);
  onRecoveredFailureRef.current = onRecoveredFailure;

  useEffect(() => {
    if (!gameId) return;
    let cancelled = false;
    void (async () => {
      let record: { status?: string; at?: string } | null = null;
      try {
        record = await client.invoke('design-agent-status:get', { gameId });
      } catch {
        // Best-effort recovery probe (mirrors useKickoffSetupReady's probe
        // catch): a failure here — including the channel failing closed for
        // a Cloud-placed game — must never surface as an error. The live
        // push remains the primary signal; this is only a backstop.
        return;
      }
      if (cancelled || record?.status !== 'failed') return;
      const parsedAt = typeof record.at === 'string' ? Date.parse(record.at) : NaN;
      onRecoveredFailureRef.current(Number.isNaN(parsedAt) ? Date.now() : parsedAt);
    })();
    return () => {
      cancelled = true;
    };
  }, [gameId]);
}

const ACTIVE_INITIAL_KICKOFF_STATUSES = new Set<InitialKickoffStatus>([
  'waiting-for-renderer-ready',
  'dispatching',
]);

/** Whether kickoff is still in a pre-dispatch phase that shares one timeout window. */
export function isActiveInitialKickoffStatus(status: InitialKickoffStatus): boolean {
  return ACTIVE_INITIAL_KICKOFF_STATUSES.has(status);
}

/**
 * Stable key for the kickoff timeout effect: `startedAt` while active, else `null`.
 *
 * Status transitions among active phases create new `initialKickoff` object refs but
 * must not reset the 15s window — only a new `startedAt` (or leaving active) should.
 */
export function initialKickoffTimeoutStartedAt(kickoff: InitialKickoffState): number | null {
  if (!isActiveInitialKickoffStatus(kickoff.status)) return null;
  return kickoff.startedAt ?? null;
}

/** Remaining ms until kickoff should time out; clamps at zero for overdue `startedAt`. */
export function initialKickoffTimeoutDelayMs(
  startedAt: number,
  nowMs: number = Date.now(),
): number {
  return Math.max(0, INITIAL_KICKOFF_TIMEOUT_MS - (nowMs - startedAt));
}

// ---------------------------------------------------------------------------
// #191 quick-batch — pure decision helpers exported so the integration
// test can pin the gate behaviour without mounting the full Workspace
// (which would need react-query, react-router, IPC stubs, etc.).
//
// `showPlanModeFor` decides whether the centre column renders
// `<PlanModeView>` instead of `<ChatPanel>`. The gate is conservative:
// only quick-depth interviews with a matching subPhase value flip the
// surface — pre-#191 games (no `depth` field) AND full-depth games
// AND build-phase games all fall through to the normal chat panel.
//
// #826 Defect C fix: the gate also requires plan content to be present
// (`plan != null && plan.trim().length > 0`). Without this guard the
// plan-mode view mounted as soon as the SKILL set subPhase='plan' —
// which fires when the agent STARTS drafting, before any plan text
// exists. The result was an empty "Drafting your plan..." surface that
// also incorrectly showed Phase 2 ("Plan review") on the phase strip
// even though no plan existed yet. `'back-to-plan'` is exempt because
// the user returning to plan review implies a plan was already visible.
//
// `showBackToPlanFor` decides whether the WorkspaceHeader renders the
// Back-to-plan button. Symmetrically narrow: quick-depth plus the two
// sub-phases where the user has committed a plan or is already in
// Phase B (`committed` and `build`) paints it.
//
// Both helpers take the narrowed depth + subPhase pair (the
// caller pulls them off the top-level state file fields per schema
// PR #543) rather than the full `BuildGameInterviewState` so the
// tests can construct minimal inputs without satisfying every
// canonical field.
// ---------------------------------------------------------------------------

export function showPlanModeFor(args: {
  depth: import('@volley/crucible-ipc-types/build-game-types').KnownInterviewDepth | undefined;
  subPhase:
    | import('@volley/crucible-ipc-types/build-game-types').KnownInterviewSubPhase
    | undefined;
  /**
   * #826 Defect C — plan markdown body from the interview state.
   * When absent / empty, `'plan'` and `'refine'` sub-phases do NOT
   * activate the plan-mode view (the agent is still drafting). Pass
   * the value of `getEffectivePlan(interviewState)` or `interviewState?.plan`.
   *
   * `'back-to-plan'` is NOT gated on this: reaching that sub-phase
   * implies the plan was already shown and the user elected to
   * revise it, so we unconditionally surface the view there.
   */
  plan?: string | null;
}): boolean {
  if (args.depth !== 'quick') return false;
  const sp = args.subPhase;
  // Per the canonical schema (PR #543), `KnownInterviewSubPhase` has
  // six values: 'quick' (bulk-pitch turn), 'plan', 'refine',
  // 'committed' (just after Start Building, pre-Phase-B kickoff),
  // 'build', and 'back-to-plan'. The plan-mode full-screen view
  // only surfaces in the three sub-phases where the user is
  // actively interacting with the plan markdown.
  //
  // #826 Defect C: for 'plan' and 'refine', additionally require that
  // plan content exists. The SKILL sets subPhase='plan' the instant it
  // BEGINS writing — before any plan text lands in the state file.
  // Without this check the view activates on an empty plan, which
  // incorrectly signals Phase 2 ("Plan review") to the user while the
  // agent is still in Phase 1 (planning/drafting).
  const hasPlan = typeof args.plan === 'string' && args.plan.trim().length > 0;
  if (sp === 'plan' || sp === 'refine') return hasPlan;
  return sp === 'back-to-plan';
}

export function showBackToPlanFor(args: {
  depth: import('@volley/crucible-ipc-types/build-game-types').KnownInterviewDepth | undefined;
  subPhase:
    | import('@volley/crucible-ipc-types/build-game-types').KnownInterviewSubPhase
    | undefined;
}): boolean {
  if (args.depth !== 'quick') return false;
  return args.subPhase === 'committed' || args.subPhase === 'build';
}

export function pendingPhaseBDecisionFromState(
  state: BuildGameInterviewState | null | undefined,
): PhaseBDecision | null {
  if (state?.status === 'built' || state?.status === 'built-partial') {
    return null;
  }
  return (
    state?.phaseBDecisions?.find(
      (decision) =>
        decision.actionStatus === 'pending' &&
        (decision.answerOptionId === null || decision.answerOptionId === undefined),
    ) ?? null
  );
}

// BUILD_KICKOFF_STALL_MS (crucible#3106 bump to 45_000) moved to
// `workspace/use-build-kickoff-watchdog.ts` with the watchdog derivation (#3108).

export function shouldUseNorthStarCreatePreviewSurface(args: {
  newUi: boolean;
  /** The collapsed intake gate (crucible#3313 — formerly `flags.northStar`,
   *  which is why the surface family keeps the north-star name). */
  northStar: boolean;
  hasGameId: boolean;
  soulIntakeActive: boolean;
  soulInterviewFinalizing?: boolean;
  autoBuildProgressActive: boolean;
  createHandoffActive?: boolean;
  reviewComplete?: boolean;
}): boolean {
  return (
    args.newUi &&
    args.northStar &&
    args.hasGameId &&
    (args.soulIntakeActive ||
      args.soulInterviewFinalizing === true ||
      args.autoBuildProgressActive ||
      args.createHandoffActive === true ||
      args.reviewComplete === true)
  );
}

/**
 * The "who owns the preview pane" predicate.
 *
 * Deliberately narrower than `shouldUseNorthStarCreatePreviewSurface()` above:
 * it excludes auto-build-progress and create-handoff on purpose, so those
 * states now fall through to the ordinary chat + live-preview composition
 * (`previewSurfaceEl`) instead of a full-bleed takeover screen.
 *
 * The exclusion is structural, not a runtime check that could regress: this
 * function's signature has no `autoBuildProgressActive` / `createHandoffActive`
 * parameter at all. A fresh object literal passing either key at a call site
 * fails to typecheck (TypeScript's excess-property check); this only guards a
 * literal, not a variable spread into the call, so it raises the bar against
 * re-widening this predicate rather than making it impossible.
 *
 * Do not fold this back into `shouldUseNorthStarCreatePreviewSurface()`, and do
 * not add a build-progress/handoff argument here. See PR #4054
 * (`feat/moodboard-gate-preview-nav`, commit `242caa635`, self-reverted the
 * same day) — an added `args.buildTimeAssetReview &&` term collapsed the
 * combined boolean to `false` for ~100% of users and killed the interview form
 * along with the build-progress screen — and incident #2314
 * (`ChatPanel.tsx:1105-1110`) — a second call site independently read the same
 * overly-broad combined value to decide composer passivity for an entire
 * build. Both came from a caller reading (or a change widening) one
 * multi-purpose boolean for a purpose it was never scoped for; this function
 * exists so "who owns the preview pane" and "is a build/handoff in flight"
 * are separate values that cannot be conflated again.
 */
export function shouldUseNorthStarPreviewPaneSurface(args: {
  newUi: boolean;
  northStar: boolean;
  hasGameId: boolean;
  soulIntakeActive: boolean;
  soulInterviewFinalizing?: boolean;
  reviewComplete?: boolean;
}): boolean {
  return (
    args.newUi &&
    args.northStar &&
    args.hasGameId &&
    (args.soulIntakeActive || args.soulInterviewFinalizing === true || args.reviewComplete === true)
  );
}

export function shouldUseNorthStarInterviewSurface(args: {
  soulIntakeActive: boolean;
  soulInterviewFinalizing: boolean;
  runStateKind: WorkspaceRunKind;
}): boolean {
  const interviewRunActive =
    args.runStateKind === 'starting-interview' || args.runStateKind === 'interview-in-progress';
  if (args.soulInterviewFinalizing && !interviewRunActive) return false;
  return args.soulIntakeActive || interviewRunActive;
}

export function isNorthStarCreateHandoffActive(runStateKind: WorkspaceRunKind): boolean {
  return (
    runStateKind === 'starting-interview' ||
    runStateKind === 'interview-in-progress' ||
    runStateKind === 'drafting-plan' ||
    runStateKind === 'build-kickoff-pending'
  );
}

/**
 * Should the build-kickoff watchdog TRACK this game (and, after its timeout,
 * fire an auto-resume retry)? Only when the kickoff is committed AND neither
 * liveness signal says a build is already running:
 *   - `hasFreshRunningActivity` — the push `activity:state` snapshot, lit only
 *     for a stream THIS GUI drives;
 *   - `buildInFlightFromPoll` — the reattach poll's `agent:get-status`, the
 *     authoritative server-side refcount, reliable for a DETACHED build the GUI
 *     only observes.
 * The poll signal is load-bearing: without it the watchdog fired a spurious
 * "kickoff retry" at every plan approval, because the push feed is structurally
 * dark for a detached build even though the build is running fine.
 */
export function shouldTrackBuildKickoff(input: {
  committed: boolean;
  hasFreshRunningActivity: boolean;
  buildInFlightFromPoll: boolean;
}): boolean {
  return input.committed && !input.hasFreshRunningActivity && !input.buildInFlightFromPoll;
}

function hasPhaseProgressBeyondPlanReview(
  progress: PhaseProgressUpdate | null | undefined,
): boolean {
  return progress?.kind === 'derived' && progress.derived.current > 2;
}

function isTerminalInterviewStatus(status: BuildGameInterviewState['status'] | undefined): boolean {
  return status === 'built' || status === 'built-partial';
}

function buildKickoffAttemptKey(
  gameId: string,
  planRevision: number | undefined,
  planHash: number | undefined,
): string {
  return `${gameId}:${planRevision ?? 'no-revision'}:${planHash ?? 'no-hash'}`;
}

// ---------------------------------------------------------------------------
// #553 — plan-mode refetch safety net. The pure predicate +
// cadence constant live in `@volley/crucible-ipc-types/interview-poll-gate` so the
// regression suite can pin them without dragging in the full
// Workspace tree. The polling effect itself is encapsulated in
// `hooks/useInterviewStatePoll.ts` and invoked once below.
//
// Why broadened: pre-#553 the inline polling effect gated on
//   `depth === 'quick' && subPhase in {plan, refine, back-to-plan,
//    committed, build}`
// which short-circuited on the most common path — engine writes
// the fresh state file with a full-flavoured shape; the renderer's
// first read sees `depth` absent; `getEffectiveInterviewDepth`
// folds the absence to `'full'`; the gate then refuses to poll. The
// SKILL's eventual `depth: 'quick' / subPhase: 'plan'` write is
// then visible ONLY via the fast `interview:plan-updated` push.
// When the push misses (watcher-arm race / atomic-rename event drop
// / push-channel hiccup), the renderer is stuck on the wrong view
// with no Start Building button. See `useInterviewStatePoll` for
// the full failure-mode prose.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// #191 quick-batch — IPC error formatter (PR #544 follow-up).
//
// Bugbot + Macroscope flagged that `handleStartBuilding` and
// `handleBackToPlanConfirm` originally used `try / finally` without a
// `catch`, so an IPC rejection became an unhandled promise rejection and
// the user got NO feedback while `planModeBusy` reset to false (the
// button re-enabled with the underlying transition silently failed).
// They ALSO ignored the `{ ok: false, reason }` resolution shape — main
// can legitimately resolve with `feature-disabled` / `no-anthropic-key` /
// `transition-in-flight` (see the IPC contract in `ipc-channels.ts`),
// and any of those returned with no visible feedback.
//
// This helper centralises the mapping so the two call sites stay in
// sync (the same defensive copy is used for Start Building and Back to
// plan, with only the action verb swapped). It's a pure function so
// the unit test can pin every branch without mounting the component.
//
// The helper is symmetric for both directions:
//   - happy path → null (caller treats as success)
//   - structured `{ ok: false, reason }` → user-facing copy mapped
//     per reason
//   - thrown / rejected error → fallback copy with the error message
// ---------------------------------------------------------------------------

/**
 * Shape returned by interview/build kickoff IPCs that resolve structured failures.
 * Kept in sync with `IpcMap` in `shared/ipc-channels.ts` — if main starts
 * returning new `reason` strings, this union must grow.
 */
export type InterviewSentinelResult = {
  ok: boolean;
  reason?:
    | 'feature-disabled'
    | 'no-anthropic-key'
    // Codex round-3 BLOCKING #1 — cloud-VM v1 fail-closed (v9 §10.5).
    // `CRUCIBLE_CLOUD_VM_V1=1` is set but the proxy URL or budget-
    // token env var is missing; main refused dispatch rather than
    // silently degrading to direct-to-Anthropic routing. The user-
    // facing copy lives in `formatInterviewSentinelError` below.
    | 'no-cloud-proxy-config'
    | 'transition-in-flight'
    // Crucible #606 — main writes `subPhase` to
    // `.foundry/build-game-interview.json` before dispatching the
    // sentinel chat message. These three reasons surface failures of
    // THAT write. The user-facing copy lives in
    // `formatInterviewSentinelError` below.
    | 'state-missing'
    | 'state-corrupt'
    | 'write-failed'
    | 'invalid-approval'
    | 'plan-review-missing'
    | 'stale-plan'
    | 'approval-conflict'
    | 'approval-runtime-incompatible'
    // Auto-build retry can fail before dispatch when Design Mode is unavailable.
    | 'design-disabled'
    | 'not-armed'
    | 'conflict'
    // crucible#3571 — main refused the kickoff because this plan already
    // produced a finished build. TERMINAL: never auto-retry it (the retry IS
    // the loop this reason breaks).
    | 'already-built';
};

/**
 * Map an `interview:start-building` / `interview:back-to-plan` invocation
 * outcome to a human-readable error string, or `null` on the happy path.
 *
 * The `action` argument is the gerund label used in recovery copy
 * ("before starting the build", "try going back to plan again"). Generic
 * failure copy derives a grammatical verb phrase from it
 * ("Couldn't start building — …" vs "Couldn't go back to plan — …").
 *
 * Branches:
 *   - `{ ok: true }`                              → null (no error)
 *   - `{ ok: false, reason: 'no-anthropic-key' }` → "Add an Anthropic API key
 *                                                     in Settings before {action}."
 *   - `{ ok: false, reason: 'feature-disabled' }` → "The build-game interview
 *                                                     feature is disabled."
 *   - `{ ok: false, reason: 'design-disabled' }`  → auto-build retry unavailable
 *   - `{ ok: false, reason: 'not-armed' }`         → no persisted auto-build state
 *   - `{ ok: false, reason: 'conflict' }`          → state lock held by another write
 *   - `{ ok: false, reason: 'transition-in-flight' }` → silent (null) — this
 *                                                     reason fires on a rapid
 *                                                     double-click; main is
 *                                                     already processing the
 *                                                     first click, so a toast
 *                                                     would be misleading.
 *   - `{ ok: false, reason: <other> }`            → "Couldn't {action}: {reason}"
 *   - thrown / rejected (Error or non-Error)      → "Couldn't {action}: {message}"
 */
type InterviewSentinelAction =
  | 'starting the build'
  | 'going back to plan'
  | 'rebuilding from the plan';

function interviewSentinelFailureAction(action: InterviewSentinelAction): string {
  switch (action) {
    case 'starting the build':
      return 'start building';
    case 'going back to plan':
      return 'go back to plan';
    case 'rebuilding from the plan':
      return 'rebuild from the plan';
  }
}

function interviewSentinelRejectionMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'object' && error !== null && 'message' in error) {
    return String((error as { message: unknown }).message);
  }
  return String(error);
}

export function formatInterviewSentinelError(
  outcome:
    | { kind: 'result'; result: InterviewSentinelResult }
    | { kind: 'rejection'; error: unknown },
  action: InterviewSentinelAction,
): string | null {
  const failureAction = interviewSentinelFailureAction(action);
  if (outcome.kind === 'rejection') {
    const message = interviewSentinelRejectionMessage(outcome.error);
    return `Couldn't ${failureAction}: ${message}`;
  }
  const { result } = outcome;
  if (result.ok) return null;
  switch (result.reason) {
    case 'no-anthropic-key':
      return `Add an Anthropic API key in Settings before ${action}.`;
    case 'no-cloud-proxy-config':
      // Codex round-3 BLOCKING #1 — cloud-VM v1 fail-closed.
      // Operator-actionable: either set the missing env vars or
      // unset the feature flag. We don't surface the env-var names
      // verbatim in user-facing copy because the cohort that opts
      // in to cloud-VM v1 is engineering-only for now; the IPC log
      // carries the diagnostic detail via the warn-log at the
      // refusal site.
      return `Cloud-mode runtime is missing its proxy config — re-authenticate or restart Foundry without CRUCIBLE_CLOUD_VM_V1, then try ${action} again.`;
    case 'feature-disabled':
      return `The build-game interview feature is disabled.`;
    case 'design-disabled':
      return `Design-to-build retry is unavailable because Design Mode is disabled.`;
    case 'not-armed':
      return `Build setup is no longer armed for retry. Reopen Build or start /build-game again.`;
    case 'conflict':
      return `Build setup is busy saving state. Wait a moment, then try ${action} again.`;
    case 'already-built':
      // crucible#3571 — nothing to kick off: this plan already produced a
      // finished build. Silent null, deliberately. This is the AUTO-RESUME
      // path's usual landing spot (a stalled-kickoff watchdog firing against a
      // game that is actually finished), and a toast there would be alarming
      // noise about a non-problem. A user who genuinely wants to rebuild the
      // same plan has the "Rebuild from plan" affordance, which routes through
      // `interview:rebuild-from-plan` and is not subject to this refusal.
      return null;
    case 'transition-in-flight':
      // Rapid double-click — main is already processing the first
      // click. Surfacing a toast here would be misleading ("it didn't
      // work" when in fact it IS working). Silent null; the planModeBusy
      // reset in `finally` re-enables the button once the in-flight slot
      // releases (~1.5s per the main-side timer).
      return null;
    case 'state-missing':
      // Crucible #606 — main tried to write `subPhase` to
      // `.foundry/build-game-interview.json` and ENOENT'd. The user
      // workflow that gets here is rare (the auto-seed on Workspace
      // open should have created the file) but possible if the user
      // deletes the file from disk between Workspace open and the
      // click. Tell them to refresh.
      return `Interview state is missing — reopen the workspace to re-seed it, then try ${action} again.`;
    case 'state-corrupt':
      // Crucible #606 — JSON.parse threw on the on-disk file. The
      // SKILL's next preflight has a recovery pathway that renames the
      // corrupt file out; the user can either run /build-game from
      // Build or close + reopen the workspace.
      return `Interview state file is unreadable — run /build-game in Build to recover, then try ${action} again.`;
    case 'write-failed':
      // Crucible #606 — fs error during the atomic write (ENOSPC,
      // EROFS, EACCES on .foundry/, antivirus interference). Surface
      // generically; the underlying cause is logged by the IPC catch.
      return `Couldn't ${failureAction} — failed to save the interview state. Check disk space / permissions and try again.`;
    case 'stale-plan':
      return `The plan changed while you were reviewing it. Review the latest plan, then try ${action} again.`;
    case 'plan-review-missing':
      return `The Guided Interview plan is not ready yet. Reopen the workspace or retry preparing the plan.`;
    case 'approval-conflict':
      return `Another build approval already owns this plan. Reopen the workspace to follow that build.`;
    case 'invalid-approval':
      return `The build approval was incomplete. Refresh the plan review and try ${action} again.`;
    case 'approval-runtime-incompatible':
      return `This Cloud runtime cannot verify the Guided plan approval. Restart the Cloud project, then try ${action} again.`;
    case undefined:
      // Defensive: main returned `{ ok: false }` with no reason. Fall
      // through to the generic copy so the user still sees something.
      return `Couldn't ${failureAction}.`;
    default:
      // Future reason values main might add — surface the raw reason
      // so the user sees SOMETHING and the dev gets a clue from a
      // screenshot.
      return `Couldn't ${failureAction}: ${result.reason}`;
  }
}

export interface InterviewSentinelOutcomeInput {
  invoke: () => Promise<unknown>;
  expectedHandoffId: string;
  guidedReview: {
    handoffId: string;
    planRevision: number;
    planHash: number;
  };
  readDurableDispatch: () => Promise<{
    state: 'approved' | 'accepted' | 'failed';
    reviewedPlanRevision?: number;
    reviewedHandoffId?: string;
    reviewedPlanHash?: number;
  } | null>;
}

/**
 * Reconcile a lost guided-review IPC reply against the durable dispatch
 * record. This helper deliberately owns no caller UI state and never throws.
 */
export async function runInterviewSentinelOutcome(
  input: InterviewSentinelOutcomeInput,
): Promise<'started' | 'already-started' | 'failed'> {
  try {
    const result = await input.invoke();
    if (typeof result === 'object' && result !== null && 'ok' in result && result.ok === false) {
      return 'failed';
    }
    return 'started';
  } catch {
    try {
      const durableDispatch = await input.readDurableDispatch();
      const { guidedReview } = input;
      const matchedReviewedHandoff = input.expectedHandoffId === guidedReview.handoffId;
      if (
        matchedReviewedHandoff &&
        durableDispatch?.state === 'accepted' &&
        durableDispatch?.reviewedPlanRevision === guidedReview.planRevision &&
        durableDispatch.reviewedHandoffId === guidedReview.handoffId &&
        durableDispatch.reviewedPlanHash === guidedReview.planHash
      ) {
        return 'already-started';
      }
    } catch {
      // A failed durable read cannot prove the interrupted IPC completed.
    }
    return 'failed';
  }
}

function toolCallFromAskUserQuestionPush(push: AgentAskUserQuestionPush): ToolCallEntry {
  return {
    id: push.toolUseID,
    name: 'AskUserQuestion',
    input: push.input,
    status: 'running',
  };
}

export function shouldClearPinnedAskUserQuestion(
  toolUseID: string,
  chunk: AgentStreamChunk,
): boolean {
  if (chunk.type === 'tool_result') {
    return chunk.toolResult?.id === toolUseID;
  }
  return chunk.type === 'done' || chunk.type === 'interrupted' || chunk.type === 'error';
}

/**
 * Bridge-3 close-out — gate the failure-latch setter.
 *
 * The Workspace's `agent:stream` chunk handler calls
 * `setFurnaceLastFailure(view)` on every terminal `'error'` chunk
 * that satisfies this predicate.
 *
 * Decision matrix:
 *
 *   - `furnaceStatus === undefined` (status query still loading)
 *     → `true`. Pre-Bridge-3-close-out behaviour preserved: an
 *     in-cohort engineer whose first turn errors before
 *     `app:get-furnace-status` resolves still latches the
 *     failure so the indicator + banner can mount retroactively
 *     once status arrives. The downstream banner mount-gate
 *     (`furnaceBuildState.phase === 'failed' ? furnaceLastFailure : null`)
 *     + the resolve-to-out clearing effect together gate
 *     visibility. Codex round-1 MEDIUM (Agent N) anchored this
 *     intent + the close-out preserves it.
 *
 *   - Status resolved as in-cohort + flag-on → `true`. Steady-
 *     state Furnace failure latching.
 *
 *   - Status resolved as out-of-cohort OR flag-off → `false`.
 *     Bugbot Low #3 (close-out): a terminal `'error'` chunk
 *     that lands while DEFINITIVELY out-of-cohort must NEVER
 *     set the latch. The clearing effect
 *     `useEffect(() => { if (!isFurnaceRouted) setFurnaceLastFailure(null); }, [isFurnaceRouted])`
 *     only fires on a TRANSITION out of `isFurnaceRouted`. A
 *     latch set while out-of-cohort sticks until a transition
 *     out of routed (impossible — we're already out), so a
 *     subsequent cohort flip INTO `'in'` would resurface it as
 *     a stale Furnace failure banner. Gating the setter on the
 *     RESOLVED out-of-cohort case stops an out-of-cohort latch
 *     from ever existing, while still allowing the loading-
 *     window grace per the first row above.
 *
 * The `chunk.sessionId` truthy gate is intentionally NOT enforced
 * here — Bugbot Medium #2 (close-out): terminal `'error'` chunks
 * emitted by main-side pre-stream paths with an empty `sessionId`
 * reach the chat reducer and render in the chat bubble regardless.
 * The latch was the ONLY surface that stayed hidden, so the
 * indicator + banner went silent on those failures while the chat
 * bubble still showed a Furnace error. That's a three-surface
 * asymmetry; in-cohort engineers expect the indicator to flip when
 * a build fails, not just the chat bubble. The cross-workspace
 * concern that motivated the original sessionId gate (Codex
 * round-3 finding by Agent N) is mitigated by this function's
 * status-aware gate (out-of-cohort workspaces never latch) plus
 * the upstream cross-game regex guard `^gui-${gameId}-\\d+$`
 * (which drops sessionId-bearing chunks for other workspaces).
 * The remaining gap — sessionId-less chunks reaching the wrong
 * workspace — is tracked as a main-process emission-contract
 * follow-up.
 *
 * Cross-workspace contamination cost analysis (worst case): if
 * main emits a terminal error for workspace X to workspace Y's
 * stream subscription with an empty `sessionId`, both workspaces'
 * chat reducers already receive the chunk via the unconditional
 * `chatDispatch` downstream. The new latch behaviour matches the
 * chat bubble's: both workspaces see the error.
 *
 * Pure function. The downstream banner mount-gate keeps the
 * visibility honest, but gating the setter is cheaper than
 * relying on the visibility gate alone (a latch sitting in state
 * has no business existing if it'll never be shown).
 */
export function shouldLatchFurnaceFailure(args: {
  furnaceStatus: { cloudVmV1Enabled: boolean; cohort: 'in' | 'out' | 'unknown' } | undefined;
}): boolean {
  const { furnaceStatus } = args;
  // Loading window — preserve Bridge-3's eager-latch intent so
  // the indicator/banner can mount retroactively when status
  // resolves to in-cohort. The clearing effect cleans up if
  // status resolves to out-of-cohort instead.
  if (furnaceStatus === undefined) return true;
  // Status resolved — only latch when we know we're in-cohort.
  return furnaceStatus.cloudVmV1Enabled === true && furnaceStatus.cohort === 'in';
}

/**
 * Bridge-3 close-out — pick a scrub level for infrastructure-URL
 * stripping based on the live FurnaceStatus snapshot.
 *
 * Mirrors the decision matrix originally inlined in the
 * `agent:stream` chunk handler (added by Codex round-5) so the
 * same logic can be reused by `classifyKickoffFailure` (Codex
 * round-1 kickoff-detail path) and the `agent:send` catch path
 * (Codex round-4 user-bubble error dispatch). Threading the
 * level through every call site closes Bridge-3 close-out
 * Bugbot Medium #1: those two paths were hard-coded to
 * `'banned-only'`, so a pre-stream Furnace failure surfacing
 * through them while `furnaceStatus` was still loading could
 * leak an infrastructure URL into the chat bubble + kickoff-
 * failure detail line.
 *
 * Decision matrix (fail-closed):
 *   - `undefined` (status query loading)       → `'aggressive'`.
 *     We don't know whether this turn was Furnace-routed; assume
 *     it might be and strip every URL. A benign local URL gets
 *     redacted in this brief loading window — that's a small,
 *     edge-case regression compared to leaking an infrastructure
 *     URL when status hadn't resolved.
 *   - In-cohort + flag-on                      → `'aggressive'`.
 *     Definitely Furnace-routed; strip every URL.
 *   - Resolved + (flag-off OR cohort != 'in')  → `'banned-only'`.
 *     Definitely local. Preserve benign URLs (pre-Bridge-3
 *     identity for flag-OFF / out-of-cohort engineers — a local
 *     error referencing `http://localhost:3000` must stay
 *     visible verbatim). Banned infrastructure hostnames are
 *     still stripped on either path as defence-in-depth.
 *
 * The `'aggressive'` default is intentional: a caller that
 * forgets to pass a level still fails closed.
 *
 * Pure function — no side effects. Exported only so the
 * Workspace-internal tests can assert the matrix against the
 * same code path the renderer uses.
 */
export function pickFurnaceScrubLevel(
  status: { cloudVmV1Enabled: boolean; cohort: 'in' | 'out' | 'unknown' } | undefined,
  gameImported?: boolean | null,
): 'aggressive' | 'banned-only' {
  if (gameImported === true) return 'banned-only';
  if (status === undefined) return 'aggressive';
  if (status.cloudVmV1Enabled !== true || status.cohort !== 'in') return 'banned-only';
  if (gameImported === null) return 'aggressive';
  return 'aggressive';
}

function classifyKickoffFailure(
  error: unknown,
  scrubLevel: 'aggressive' | 'banned-only' = 'aggressive',
): {
  reason: InitialKickoffFailureReason;
  detail: string;
} {
  const serialised = unflattenIpcError(error);
  // Codex round-3 — the kickoff detail is rendered through the
  // run-state derivation into ActivityBanner. Scrub
  // infrastructure URLs from the user-facing message BEFORE
  // building the detail string so a pre-stream Furnace
  // provisioning failure can't leak a hostname through this
  // surface. The classifier still matches against the raw text
  // (substring scan for "cloud" / "proxy" / "anthropic" / etc.)
  // so the reason discriminator is unaffected.
  //
  // Bridge-3 close-out (Bugbot Medium #1) — `scrubLevel` is now
  // threaded from the caller. The default is `'aggressive'`
  // (fail-closed) so a missing argument can't downgrade the
  // scrub. Callers pass `pickFurnaceScrubLevel(furnaceStatus,
  // gameImported)` so the level mirrors the `agent:stream`
  // handler's loading-vs-known-state matrix.
  const detail =
    scrubInfrastructureUrls(serialised.message, scrubLevel) ||
    'The first agent message could not be sent.';
  const lower = `${serialised.code ?? ''} ${serialised.message ?? ''}`.toLowerCase();
  if (lower.includes('anthropic')) {
    return { reason: 'anthropic-token-missing', detail };
  }
  if (lower.includes('cloud') || lower.includes('proxy')) {
    return { reason: 'cloud-proxy-missing', detail };
  }
  if (lower.includes('in-flight') || lower.includes('already running')) {
    return { reason: 'turn-in-flight', detail };
  }
  if (lower.includes('interview state') || lower.includes('.foundry')) {
    return { reason: 'interview-state-missing', detail };
  }
  return { reason: 'agent-send-rejected', detail };
}

// ---------------------------------------------------------------------------
// Workspace (root)
// ---------------------------------------------------------------------------

// Stable module-level no-op. Used as the embedded preview's `onClose`
// and the pinned activity panel's `onClose` — neither surface is
// closable (the embedded preview guards its Escape / request-close
// pipeline on `!embedded`; the pinned panel ignores `open`/`onClose`).
// A module constant keeps the reference identity stable across renders
// (so PreviewOverlay's `[onClose]`-keyed effects never churn) without a
// per-render arrow — and satisfies the source-grep regression guard in
// PreviewOverlay.exit-affordances.test.tsx that bans inline `onClose`.
const NOOP = (): void => {};

export function isValidWorkspaceRouteGameId(gameId: string | undefined): gameId is string {
  // Mirrors the main-process `assertValidGameId` route shape for direct
  // renderer routes. In particular, reject macOS bundles like `Foundry.app`
  // before Workspace starts firing IPC for a non-game directory.
  return !!gameId && /^[a-z0-9][a-z0-9-]*[a-z0-9]$/.test(gameId);
}

export function renderDesignWorkspaceFallback({
  error,
  onReset,
}: {
  error: Error;
  onReset: () => void;
  gameId?: string;
}): React.JSX.Element {
  return (
    <div
      role="alert"
      data-testid="design-error-boundary-fallback"
      className="flex h-full items-center justify-center bg-crucible-bg px-6"
    >
      <div className="w-full max-w-xl rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 shadow-2xl">
        <div className="flex items-start gap-3">
          <AlertTriangle size={18} strokeWidth={1.8} className="mt-0.5 shrink-0 text-amber-300" />
          <div className="min-w-0 flex-1">
            <p className="text-xs uppercase tracking-wide text-amber-200/80">
              Design hit a render error
            </p>
            <p className="mt-1 text-sm text-white/80">
              Foundry caught a runtime error while opening Design and saved a diagnostic for
              support.
            </p>
            <details className="mt-3 text-xs text-white/45">
              <summary className="cursor-pointer hover:text-white/65">Error details</summary>
              <pre className="mt-1 max-h-28 overflow-auto whitespace-pre-wrap break-words text-[11px] text-white/55">
                {error.message}
              </pre>
            </details>
            <Button
              onClick={onReset}
              data-testid="design-error-boundary-reset"
              variant="secondary"
              size="sm"
              className="mt-4 hover:border-amber-500/40"
            >
              Try again
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Mirrors {@link renderDesignWorkspaceFallback} exactly (same design-boundary
 *  pattern, reworded for Art) — an error-boundary fallback for a RENDER
 *  ERROR inside the Art tab. The "no art yet" / "engine failed to start"
 *  states are ordinary data states handled inside `ArtWorkspace` itself,
 *  not errors, and never reach this boundary. */
export function renderArtWorkspaceFallback({
  error,
  onReset,
}: {
  error: Error;
  onReset: () => void;
  gameId?: string;
}): React.JSX.Element {
  return (
    <div
      role="alert"
      data-testid="art-error-boundary-fallback"
      className="flex h-full items-center justify-center bg-crucible-bg px-6"
    >
      <div className="w-full max-w-xl rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 shadow-2xl">
        <div className="flex items-start gap-3">
          <AlertTriangle size={18} strokeWidth={1.8} className="mt-0.5 shrink-0 text-amber-300" />
          <div className="min-w-0 flex-1">
            <p className="text-xs uppercase tracking-wide text-amber-200/80">
              Art hit a render error
            </p>
            <p className="mt-1 text-sm text-white/80">
              Foundry caught a runtime error while opening the Art tab and saved a diagnostic for
              support.
            </p>
            <details className="mt-3 text-xs text-white/45">
              <summary className="cursor-pointer hover:text-white/65">Error details</summary>
              <pre className="mt-1 max-h-28 overflow-auto whitespace-pre-wrap break-words text-[11px] text-white/55">
                {error.message}
              </pre>
            </details>
            <Button
              onClick={onReset}
              data-testid="art-error-boundary-reset"
              variant="secondary"
              size="sm"
              className="mt-4 hover:border-amber-500/40"
            >
              Try again
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * crucible#2990 — error-boundary fallback for the ENTIRE workspace centre
 * column (every mode arm: Design / Preview / ProtoHub / Art / the
 * chat+plan CentreColumnWithBanner). Before this boundary existed, a
 * render throw anywhere in the centre column propagated to the React root
 * and unmounted the whole Workspace — or, behind a higher boundary,
 * rendered NOTHING. A silent blank centre column is never acceptable: the
 * fallback is a visible error card carrying the thrown message, with a
 * Try-again reset. `min-w-0 flex-1` keeps the flex-row layout (files
 * panel / right rail) intact while the card fills the centre slot.
 */
export function renderCentreColumnFallback({
  error,
  onReset,
}: {
  error: Error;
  onReset: () => void;
  gameId?: string;
}): React.JSX.Element {
  return (
    <div
      role="alert"
      data-testid="workspace-centre-error-boundary-fallback"
      className="flex min-w-0 flex-1 items-center justify-center bg-crucible-bg px-6"
    >
      <div className="w-full max-w-xl rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 shadow-2xl">
        <div className="flex items-start gap-3">
          <AlertTriangle size={18} strokeWidth={1.8} className="mt-0.5 shrink-0 text-amber-300" />
          <div className="min-w-0 flex-1">
            <p className="text-xs uppercase tracking-wide text-amber-200/80">
              The workspace hit a render error
            </p>
            <p className="mt-1 text-sm text-white/80">
              Foundry caught a runtime error while rendering this game&apos;s workspace and saved a
              diagnostic for support. Your game and its build history are safe.
            </p>
            <details className="mt-3 text-xs text-white/45">
              <summary className="cursor-pointer hover:text-white/65">Error details</summary>
              <pre className="mt-1 max-h-28 overflow-auto whitespace-pre-wrap break-words text-[11px] text-white/55">
                {error.message}
              </pre>
            </details>
            <Button
              onClick={onReset}
              data-testid="workspace-centre-error-boundary-reset"
              variant="secondary"
              size="sm"
              className="mt-4 hover:border-amber-500/40"
            >
              Try again
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function shouldQueryWorkspaceGameLiteList(
  flags: Pick<FeatureFlagsState, 'loaded' | 'lazyDashboardLoad'>,
): boolean {
  return flags.loaded && flags.lazyDashboardLoad;
}

/**
 * crucible#3041/#3516 — pure gate for the split-create Design lane's "Build
 * my game" affordance. Sean's ratified three-path design: the Build lane
 * auto-finishes phase 2 on interview completion; the Design lane STOPS at
 * that point and waits for this explicit trigger. True for the WHOLE
 * deferred window — from the moment the between-phases interview finishes
 * (`deferredDesignInterviewDone`) until `game:create:finish` resolves
 * (`buildStatus` flips to `'done'`) — and deliberately NOT gated on
 * `workspaceMode`/which tab is active: NO STRANDING (the veto of the narrow
 * fix this shipped instead of) means the trigger must be reachable no
 * matter which tab the creator is looking at.
 */
export function shouldShowDeferredDesignBuildAffordance(args: {
  deferredDesignHandoff: boolean;
  deferredDesignInterviewDone: boolean;
  buildStatus: 'idle' | 'pending' | 'done';
}): boolean {
  return (
    args.deferredDesignHandoff && args.deferredDesignInterviewDone && args.buildStatus !== 'done'
  );
}

/**
 * crucible#3041/#3516 — fires the explicit split-create finish trigger
 * (`game:create:finish`) for a deferred Design create. Returns a
 * discriminated result instead of mutating component state directly, so
 * this is unit-testable against a mock client and the caller decides how to
 * surface a failure (Workspace routes it through the page-scope toast;
 * `game:create:finish` is explicitly kept reachable as the retry surface by
 * the main process — see its `ipc-registrars/game.ts` comment — so a
 * failure here must leave the affordance re-clickable, not stranded).
 */
export async function triggerDeferredDesignFinish(
  client: {
    invoke: (channel: 'game:create:finish', options: { name: string }) => Promise<unknown>;
  },
  gameId: string,
): Promise<{ ok: true } | { ok: false; message: string }> {
  try {
    await client.invoke('game:create:finish', { name: gameId });
    return { ok: true };
  } catch (err) {
    // `unflattenIpcError` returns a `SerialisedError` (a plain object whose
    // `.message` is always populated) — not an `Error` instance — so read
    // `.message` directly rather than guarding with `instanceof Error`.
    return { ok: false, message: unflattenIpcError(err).message };
  }
}

/**
 * crucible#3041/#3516 — the legacy finish-setup banner for a deferred Design
 * create. Extracted as a standalone presentational component (mirrors
 * `WorkspaceChatSurface`) so it is mountable and clickable in isolation —
 * mounting the full ~8k-line Workspace is expensive/brittle (the rationale
 * `Workspace.mode-jsx-additive-only.test.ts` and its siblings already
 * document for this test directory).
 */
export function DeferredDesignBuildBanner({
  pending,
  onBuildClick,
}: {
  pending: boolean;
  onBuildClick: () => void;
}): React.JSX.Element {
  return (
    <div
      data-testid="deferred-design-build-banner"
      className="flex items-center justify-between gap-4 border-b border-emerald-500/20 bg-emerald-500/[0.07] px-5 py-2 text-xs text-emerald-100/80"
    >
      <span>
        <strong className="font-medium text-emerald-100">One setup step remains.</strong> Finish
        preparing this Design workspace, then keep editing or send it to build.
      </span>
      <Button
        size="sm"
        data-testid="deferred-design-build-button"
        disabled={pending}
        onClick={onBuildClick}
      >
        {pending ? 'Finishing setup…' : 'Finish Design setup'}
      </Button>
    </div>
  );
}

export function Workspace({
  initialRightPanelOpen,
}: { initialRightPanelOpen?: boolean } = {}): React.JSX.Element | null {
  const { placement = 'local', gameId } = useParams<{
    placement: 'local' | 'cloud';
    gameId: string;
  }>();
  const projectRef = { placement, gameId: gameId ?? '' } as const;
  const navigate = useNavigate();
  const flags = useFeatureFlags();
  const client = useCrucibleClient();
  const clientCapabilities = useClientCapabilities();
  const localReadiness = useLocalOperationalReadiness(
    placement === 'local' && clientCapabilities.hasLocalFilesystem,
  );
  const publishBacked = canPublishRun(clientCapabilities);
  // crucible#2325: the native ProtoHub simulator is reachable only
  // from a preload-backed Electron renderer. In the cookie-mode cloud
  // SPA (and pure-web), `protohub:*` has no transport to Electron main
  // and would fall through to the pod ("Method not found:
  // protohub.open@1"). Gate every ProtoHub entry on this so the
  // post-deploy auto-open never mounts the surface where it can't work.
  const nativeSimulatorAvailable = canDisplayOnHub(clientCapabilities);
  // crucible#2326: when the native simulator is unreachable in-process (the
  // cookie-mode cloud SPA), the cloud desktop shell injects a host bridge so
  // we can still open the native ProtoHub focused on the game (staging origin).
  // null in local Electron (native path works directly) + pure-web (no host).
  const hostBridge = useHostBridge();
  // Live-preview scaffold-first (A2.3b): the inferred-genre outcome carried
  // from NewGame via route state, for the preview genre chip. Present only for
  // the create→preview session (a fresh nav / refresh clears it); the chip is
  // additionally gated on `flags.scaffoldFirstActive`. Route state is `unknown`
  // — VALIDATE the shape before trusting it (a crafted nav / refactor could
  // hand us a malformed object); a bad payload collapses to `null` → no chip.
  const location = useLocation();
  const routeState = location.state;
  const rawScaffoldFirst = (routeState as { scaffoldFirst?: unknown } | null)?.scaffoldFirst;
  const scaffoldFirstInference: ScaffoldFirstInference | null = isScaffoldFirstInference(
    rawScaffoldFirst,
  )
    ? rawScaffoldFirst
    : null;
  const createInitialPrompt = readCreateInitialPromptFromRouteState(routeState);
  const createRuntimePending = readCreateRuntimePendingFromRouteState(routeState);
  const createHandoffMode = readCreateHandoffModeFromRouteState(routeState);
  const deferredDesignHandoff = readDeferredDesignHandoffFromRouteState(routeState);

  // Left panel
  const [filesCollapsed, setFilesCollapsed] = useState(true);
  const [leftWidth, setLeftWidth] = useState(260);

  // Phase 3 visibility — read `showCostInChat` once per Workspace
  // mount. The flag flips rarely (engineer toggling on for spend
  // debugging), so a stable cached read is fine; useIpcQuery keeps
  // it consistent with the App-level theme query.
  const { data: workspaceConfig } = useIpcQuery('config:get', undefined);
  const showCostInChat = !!workspaceConfig?.showCostInChat;

  const [initialKickoff, setInitialKickoff] = useState<InitialKickoffState>({
    status: 'idle',
    startedAt: null,
  });
  const [interviewRendererReady, setInterviewRendererReady] = useState(false);
  const [interviewReadyHandshakeAttempt, setInterviewReadyHandshakeAttempt] = useState(0);
  // Issue #2596 — setup (background install) readiness for the auto-kickoff
  // gate. Defaults `true` so a normal game open (no create-install in
  // flight) never waits; the poll effect below flips it `false` only while
  // the #2584 install-state sentinel reports `installing`, so the
  // `/build-game` kickoff holds until clone/install/build finish. See
  // `deriveKickoffSetupReady` + `shouldHoldInitialKickoffForRendererReady`.
  const [setupReady, setSetupReady] = useState(true);
  const pendingInitialAgentSendRef = useRef<AgentSendEffect | null>(null);
  const pendingInteractionHydrationKeyRef = useRef<string | null>(null);
  const automaticInterviewRecoveryKeyRef = useRef<string | null>(null);
  const interviewRecoveryInFlightRef = useRef<Promise<InterviewRecoverResult> | null>(null);
  const appliedCreateHandoffPromptKeyRef = useRef<string | null>(null);

  // Workspace layout mode — read by the chat brain (pulse-vs-pop on
  // add-selection) and by the preview brain / render below. Declared before
  // the chat hook so `useChatModule` can take the live value as an input.
  const [workspaceMode, setWorkspaceMode] = useState<WorkspaceMode>('chat');

  /**
   * Page-scope preview/action toast. Error toasts are sticky (dismiss via the
   * close button); info/success auto-fade. Lives at page chrome scope so it
   * surfaces with or without the preview overlay open, and is threaded into
   * `useChatModule` so a selection-attach failure can raise it. (Formerly a
   * #1 BLOCK: a 4s failure toast was too easy to miss over the overlay's
   * centred error card.)
   */
  const [previewToast, setPreviewToast] = useState<{
    tone: 'info' | 'success' | 'error';
    message: string;
  } | null>(null);
  useEffect(() => {
    if (!previewToast) return;
    // See previewToastAutoFadeDelayMs's docblock (crucible#643 BLOCK fix)
    // for why error toasts are sticky and info/success ones fade.
    const delay = previewToastAutoFadeDelayMs(previewToast.tone);
    if (delay === null) return;
    const timer = setTimeout(() => setPreviewToast(null), delay);
    return () => clearTimeout(timer);
  }, [previewToast]);

  /**
   * crucible#2715 — Art tab's ruleset-protected-main branch+PR fallback
   * (art-commit.ts's `pushViaDeployBranchPR`) opened a PR. Sticky (tone
   * `error`, dismiss button) when auto-merge couldn't be armed — the user
   * needs to notice and manually merge, or the revision never reaches the
   * TV. Auto-fades when armed — informational; GitHub handles the rest.
   *
   * Not gameId-scoped (the `art:deploy-fallback-pr` payload carries no
   * gameId — see that channel's own docblock in ipc-channels.ts) — a rare
   * event, and today there is only ever one active Workspace/game per
   * window, the same simplification `interview:auto-seeded`'s own
   * multi-window caveat already accepts elsewhere in this file.
   */
  const [protectedMainFallbackToast, setProtectedMainFallbackToast] =
    useState<ProtectedMainFallbackInfo | null>(null);
  /**
   * crucible#3497 review round 3 (MEDIUM) — stranding DOMINATES the tone
   * decision, regardless of `armed`. `strandedOnBranch` means the game
   * folder is stuck off `main` (see that field's own docblock on
   * `ProtectedMainFallbackInfo` in art-commit.ts) and `commitArtRevision`'s
   * branch guard will silently refuse every LATER Art revision until a human
   * fixes the checkout by hand — that is still-actionable news even though
   * THIS PR's own auto-merge armed fine. Rendering it as the auto-fading
   * "merges automatically" info toast (the `armed` flag alone would pick
   * that) would hide a real, ongoing failure behind a toast that vanishes in
   * 8s.
   */
  const protectedMainFallbackIsError = deriveProtectedMainFallbackIsError(
    protectedMainFallbackToast,
  );
  const protectedMainFallbackView = protectedMainFallbackToast
    ? protectedMainFallbackToastView(protectedMainFallbackToast)
    : null;
  useEffect(() => {
    const unsub = client.subscribe('art:deploy-fallback-pr', (payload) => {
      setProtectedMainFallbackToast(payload);
    });
    return unsub;
  }, [client]);
  useEffect(() => {
    if (!protectedMainFallbackToast) return;
    // See protectedMainFallbackAutoFadeDelayMs's docblock (crucible#3497
    // review round 3) for why a stranded or unarmed toast stays sticky.
    const delay = protectedMainFallbackAutoFadeDelayMs(protectedMainFallbackIsError);
    if (delay === null) return;
    const timer = setTimeout(() => setProtectedMainFallbackToast(null), delay);
    return () => clearTimeout(timer);
  }, [protectedMainFallbackToast, protectedMainFallbackIsError]);

  // Chat brain — composes `useChatSession` (reducer + hydration + queue-drain
  // + reattach poll, plan-001 Step 5.3) and owns the chat-scoped
  // attachment/selection state + the resize-then-attach handler. Extracted to
  // `../chat/useChatModule` (issue #2507).
  const {
    chat,
    chatDispatch,
    setInterpreter,
    getChatState,
    hydratedForGameIdRef,
    pendingCheckpointPushesRef,
    hydrationLongRunning,
    buildInFlightFromPoll,
    setBuildInFlightFromPoll,
    chatPersistError,
    setChatPersistError,
    handleRetryHydration,
    attachmentError,
    setAttachmentError,
    selectionAddedHint,
    attachmentPop,
    handleAddSelectionToChat,
  } = useChatModule({
    gameId,
    createInitialPrompt,
    setInitialKickoff,
    appliedCreateHandoffPromptKeyRef,
    setPreviewToast,
    workspaceMode,
  });
  const {
    messages,
    chatInput,
    isAiTyping,
    queuedMessages,
    pendingAttachments,
    lastTurnSpend,
    pendingDependencyRequests,
    pendingTemplateFileOverrideRequests,
    agents,
    mainAgent,
    hydrationStatus,
    hydrationError,
  } = chat;
  const featureFlags = useFeatureFlags();
  // Cloud-VM supportability TDD §B-1 — maintainer-mode gate for the
  // left-side VM Debug pane. Read here so the parent can thread the
  // boolean through to the conditional render below; A-1's lesson
  // learned (pulling `useFeatureFlags` into `<VmStatusPill>` broke
  // 13 SSR tests across 4 files) applies one-to-one — gate at the
  // call site, not inside the pane. See `useMaintainerMode` for the
  // hook contract.
  //
  // Codex round-1 HIGH fix: the gate MUST include the cloud-VM flag.
  // A user who launched Foundry with `CRUCIBLE_MAINTAINER_MODE=1`
  // but without the cloud-VM flag would otherwise lose `<FilesPanel>`
  // for an inert pane (the message-stream capture is never
  // constructed when the flag is OFF, so no messages ever land).
  // Pane mounts ONLY when BOTH gates are ON, matching the producer-
  // side construction guard in `main/index.ts`.
  const maintainerMode = useMaintainerMode();
  const showVmDebugPane =
    maintainerMode.loaded &&
    maintainerMode.maintainer &&
    featureFlags.loaded &&
    featureFlags.cloudVmV1;
  // Game status query. This lives before Furnace route derivation so imported
  // locality can close the renderer-side cloud gate during `game:status`
  // loading and for imported/user-owned local projects.
  const { data: gameStatus, isError: gameStatusErrored } = useIpcQuery('game:status', projectRef, {
    enabled: !!gameId,
  });
  const { data: projectRuntime } = useIpcQuery(
    'project-runtime:get',
    { gameId: gameId ?? '' },
    {
      enabled: projectRuntimeQueryEnabled(gameId, clientCapabilities.projectPlacementAuthority),
    },
  );
  // See resolveProjectCapability / resolveProjectCapabilityAvailable
  // (crucible#3819) for the actual gate. These stay as thin closures over
  // projectRuntime/clientCapabilities so every downstream call site below
  // keeps its existing `projectCapability('x')` / `projectCapabilityAvailable('x')` shape.
  const projectCapability = (
    capability: keyof NonNullable<typeof projectRuntime>['capabilities'],
  ) => resolveProjectCapability(capability, projectRuntime, clientCapabilities);
  const projectCapabilityAvailable = (
    capability: keyof NonNullable<typeof projectRuntime>['capabilities'],
  ): boolean =>
    resolveProjectCapabilityAvailable(capability, projectRuntime, clientCapabilities, placement);
  const localFilesystemEffectsAvailable =
    placement === 'local' && clientCapabilities.hasLocalFilesystem;
  const gameImportedForFurnace = gameStatus === undefined ? null : gameStatus.imported === true;

  // Bridge-3 — derive the Furnace build state. Inputs:
  //   - FurnaceStatus query (cohort + flag snapshot).
  //   - chat reducer's `isAiTyping` (turn in flight).
  //   - VM-connection state (latest push).
  //   - `furnaceLastFailure` — sticky latch reset on the next turn.
  // The component reads `state.routedToFurnace` to short-circuit on
  // flag-OFF / out-of-cohort, so renderer behaviour is identical
  // to pre-Bridge-3 main in those cases.
  const { data: furnaceStatus } = useIpcQuery('app:get-furnace-status', undefined);
  const vmConnectionState = useVmConnectionState();
  const [furnaceLastFailure, setFurnaceLastFailure] = useState<FurnaceErrorView | null>(null);
  // Mirror `furnaceStatus` into a ref so the `agent:stream`
  // subscription's chunk handler can read the current snapshot
  // without re-subscribing the channel on every status refresh.
  // Used to pick the scrub level (aggressive on Furnace-routed
  // turns, banned-only on local-build) so a local `http://localhost`
  // error is rendered verbatim per pre-Bridge-3 main behaviour
  // (Codex round-3 flag-OFF regression).
  const furnaceStatusRef = useRef(furnaceStatus);
  const gameImportedForFurnaceRef = useRef(gameImportedForFurnace);
  useEffect(() => {
    furnaceStatusRef.current = furnaceStatus;
  }, [furnaceStatus]);
  useEffect(() => {
    gameImportedForFurnaceRef.current = gameImportedForFurnace;
  }, [gameImportedForFurnace]);
  // Reset the failure latch whenever a new turn dispatches (isAiTyping
  // flips true) so the indicator clears as soon as the engineer
  // retries.
  useEffect(() => {
    if (isAiTyping) setFurnaceLastFailure(null);
  }, [isAiTyping]);
  // Reset on game switch too — a stale failure from game A must
  // not bleed into game B's chrome.
  useEffect(() => {
    setFurnaceLastFailure(null);
  }, [gameId]);
  // Belt-and-braces: when the routing gate closes mid-session
  // (cohort flip, flag flip, IPC drops back to null), clear any
  // latched failure so the banner doesn't outlive the route. The
  // banner mount-gate below ALSO checks `routedToFurnace`, so this
  // is defence-in-depth — but it keeps the latch state honest.
  //
  // Bridge-3 close-out (Bugbot Low #3): track `furnaceStatus`
  // itself rather than the derived `isFurnaceRouted` boolean.
  // Reason: when status transitions from `undefined` (loading)
  // to a resolved out-of-cohort value, `isFurnaceRouted` stays
  // `false` across both rows. A dep on `isFurnaceRouted` doesn't
  // re-fire, so any latch set during the loading-window grace
  // (per `shouldLatchFurnaceFailure`'s undefined → true row)
  // would persist through the loading → out transition and
  // resurface on a subsequent cohort flip into `'in'`. Tracking
  // `furnaceStatus` instead causes the effect to fire on every
  // status query update, where we re-derive routing freshness +
  // clear stale latches.
  const isFurnaceRouted =
    gameImportedForFurnace === false &&
    furnaceStatus !== undefined &&
    furnaceStatus.cloudVmV1Enabled === true &&
    furnaceStatus.cohort === 'in';
  useEffect(() => {
    if (!isFurnaceRouted) setFurnaceLastFailure(null);
  }, [furnaceStatus, isFurnaceRouted]);
  const furnaceBuildState: FurnaceBuildState = computeFurnaceBuildState({
    furnaceStatus: furnaceStatus ?? null,
    gameImported: gameImportedForFurnace,
    isAiTyping,
    vmConnectionState,
    lastFurnaceFailure: furnaceLastFailure !== null,
  });

  useEffect(() => {
    setInitialKickoff({ status: 'idle', startedAt: null });
    setInterviewRendererReady(false);
    setInterviewReadyHandshakeAttempt(0);
    pendingInitialAgentSendRef.current = null;
    appliedCreateHandoffPromptKeyRef.current = null;
    // Live-preview split is per-game UI state — never carry game A's
    // open split into game B's workspace, but DO restore the split the way
    // the user/auto-open latch last left game B (a building game we left
    // and came back to keeps its open split rather than being force-closed
    // while the auto-open latch refuses to reopen it).
    // Develop is the default workspace view: the side-by-side split opens
    // by default for a game (unless this game was explicitly left closed).
    setLivePreviewSplitOpen(
      gameId ? (livePreviewSplitOpenByGameRef.current.get(gameId) ?? true) : false,
    );
  }, [gameId]);

  // New-UI activity rail is pinned and ON by default — it reserves its
  // own column (no longer a slide-in overlay that covers the chat), so
  // "open" just means the rail is visible. The header toggle collapses
  // it. Defaults open so users see progress + live sub-agents without
  // hunting for a button. Storybook can still seed it closed via the prop.
  const [rightPanelOpen, setRightPanelOpen] = useState(initialRightPanelOpen ?? true);
  const [artifactSoftClosed, setArtifactSoftClosed] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  // `workspaceMode` is declared earlier (above the chat brain) so
  // `useChatModule` can read the live value. See the chat-brain call.
  const [protoHubSource, setProtoHubSource] = useState<ProtoHubSource>('manual');
  // Per-view Progress sidebar memory. The Preview tab maximises the embedded
  // game, so the rail must default hidden there and never steal preview width
  // on its own — but the user can still open it manually within Preview. The
  // non-preview views (chat / plan / design) share ONE remembered open state:
  // entering Preview snapshots it, leaving Preview restores it, so whatever the
  // user last left the rail as in chat reappears when they return — consistent
  // across non-preview tabs. (Toggling inside Preview never touches the
  // snapshot, so it can't leak back into chat.)
  const nonPreviewRightPanelOpenRef = useRef(initialRightPanelOpen ?? true);
  const prevWorkspaceModeForRailRef = useRef<WorkspaceMode>(workspaceMode);
  useEffect(() => {
    const prevMode = prevWorkspaceModeForRailRef.current;
    if (prevMode === workspaceMode) return;
    const wasImmersive = prevMode === 'preview' || prevMode === 'protohub';
    const isImmersive = workspaceMode === 'preview' || workspaceMode === 'protohub';
    if (isImmersive && !wasImmersive) {
      // Entering an immersive surface: remember the non-preview state, then hide the rail.
      nonPreviewRightPanelOpenRef.current = rightPanelOpen;
      setRightPanelOpen(false);
    } else if (!isImmersive && wasImmersive) {
      // Leaving an immersive surface: restore the remembered non-preview state.
      setRightPanelOpen(nonPreviewRightPanelOpenRef.current);
    }
    prevWorkspaceModeForRailRef.current = workspaceMode;
  }, [workspaceMode, rightPanelOpen]);
  const [designBuildPlanFocusNonce, setDesignBuildPlanFocusNonce] = useState(0);
  // #1408/#1420 — live-preview split. DISTINCT from the manual Preview
  // tab (`workspaceMode === 'preview'`): the split docks the embedded
  // preview ABOVE the chat in chat mode, so the agent stream, composer,
  // and interview cards stay mounted while the game assembles. Toggled
  // by the header Live pill and by the live-preview auto-open effect.
  const [livePreviewSplitOpen, setLivePreviewSplitOpen] = useState(true);
  const initialisedModeForGameRef = useRef<string | null>(null);
  // Per-game split-open memory. The component persists across game
  // switches, so the split state is per-game UI: leaving a building game
  // for another and returning must restore the split exactly as the user
  // (or the auto-open latch) last left it — closing it unconditionally on
  // every gameId change while the auto-open latch still records the game
  // as opened stranded the user with a permanently-closed split. Keyed by
  // gameId; absent → never opened for that game (default closed).
  const livePreviewSplitOpenByGameRef = useRef<Map<string, boolean>>(new Map());
  const setLivePreviewSplitOpenForGame = useCallback(
    (open: boolean) => {
      if (gameId) livePreviewSplitOpenByGameRef.current.set(gameId, open);
      setLivePreviewSplitOpen(open);
    },
    [gameId],
  );
  // Side-by-side chat-panel width (px) — pinned to the right-panel width
  // by default, drag-adjustable, persisted per-game (dragging the divider
  // in game A must not bleed into game B's layout).
  const [livePreviewChatWidth, setLivePreviewChatWidth] = useState(DEFAULT_LIVE_PREVIEW_CHAT_WIDTH);
  // Chat-panel tabs: Chat (default) | State (the right-side panel) |
  // Diagnostic (preview dev-server health).
  const [chatTab, setChatTab] = useState<'chat' | 'state' | 'diagnostic'>('chat');
  const livePreviewChatWidthByGameRef = useRef<Map<string, number>>(new Map());
  const setLivePreviewChatWidthForGame = useCallback(
    (width: number) => {
      if (gameId) livePreviewChatWidthByGameRef.current.set(gameId, width);
      setLivePreviewChatWidth(width);
    },
    [gameId],
  );
  useEffect(() => {
    setLivePreviewChatWidth(
      (gameId && livePreviewChatWidthByGameRef.current.get(gameId)) ||
        DEFAULT_LIVE_PREVIEW_CHAT_WIDTH,
    );
  }, [gameId]);

  // Preview "brain" moved to `usePreviewModule` (design doc §4 PR A). The
  // hook owns the `usePreviewSession()` session, the construction bridge,
  // the install-state query, the lifecycle handlers, the derived state, and
  // the auto-open / auto-start / cloud-session-reset / diagnostics-refetch
  // effects. It is called below (`const preview = usePreviewModule({…})`)
  // once its Workspace-owned inputs (run-state, phase gate, mode setters)
  // are in scope; every former inline `previewSession.*` / derived read is
  // now `preview.*`.

  const kickoffTimeoutStartedAt = initialKickoffTimeoutStartedAt(initialKickoff);

  useEffect(() => {
    if (kickoffTimeoutStartedAt === null) return;
    // Issue #2596 — do NOT arm the 15s renderer-ready timeout while the
    // background create-install is still running. A cold pnpm install can
    // legitimately exceed the renderer-ready budget, and timing the
    // kickoff out mid-install would surface `renderer-not-ready-timeout`
    // for what is a normal (and bounded) setup wait. The install-state
    // sentinel's own 10-minute stale sweep (#459) is the backstop; once
    // setup clears (`setupReady` flips true) this effect re-runs and arms
    // the timeout against the (still-original) `startedAt`.
    if (!setupReady) return;
    const startedAt = kickoffTimeoutStartedAt;
    const timeout = setTimeout(() => {
      setInitialKickoff((current) => {
        if (!isActiveInitialKickoffStatus(current.status)) {
          return current;
        }
        return {
          status: 'timed-out',
          reason: 'renderer-not-ready-timeout',
          startedAt,
        };
      });
    }, initialKickoffTimeoutDelayMs(startedAt));
    return () => clearTimeout(timeout);
  }, [kickoffTimeoutStartedAt, setupReady]);

  // Save chat history when messages change. We keep one piece of state
  // for the most recent persist failure rather than appending to a list:
  // a save typically fires on every keystroke, and rapid-fire toasts
  // would obscure each other. The user gets a single visible signal with
  // recovery copy, and dismissing it lets the next failure surface.
  //
  // Wire-shape projection: the renderer-side `ChatMessage` extends
  // shared with `toolCalls?` and `error?` (see chat/types.ts). Those
  // are local UI state — tool-call statuses come from agent:stream
  // push, errors come from `unflattenIpcError(...)`. Persisting them
  // would (a) bloat chat-history.json with structured tool-call
  // inputs that may carry secrets only redacted on the TEXT path,
  // and (b) round-trip stale `running` statuses on the next load
  // before the SDK re-emits them. Project to the shared shape at
  // the IPC boundary.
  useEffect(() => {
    if (!gameId || messages.length === 0) return;
    // Crucible #602 / #736 (C4 Issue B): widen the projection to preserve
    // the `checkpoint` payload on `role: 'inline-checkpoint'` rows per
    // `docs/c4-inline-checkpoints-design.md` §6.2. The spread is
    // CONDITIONAL — user/assistant rows leave `checkpoint` undefined and
    // we deliberately omit the field from their wire shape rather than
    // emit `"checkpoint": null`, which would pollute every non-checkpoint
    // row on disk. Tool-call statuses and `error` payloads remain
    // ephemeral per the existing convention (see `renderer/chat/types.ts`).
    const wireMessages = messages.map(
      ({ id, role, content, timestamp, checkpoint, source, streamSeq, streamStatus, turnId }) => ({
        id,
        role,
        content,
        timestamp,
        ...(source !== undefined ? { source } : {}),
        ...(streamSeq !== undefined ? { streamSeq } : {}),
        ...(streamStatus !== undefined ? { streamStatus } : {}),
        ...(turnId !== undefined ? { turnId } : {}),
        ...(checkpoint !== undefined ? { checkpoint } : {}),
      }),
    );
    client
      .invoke('chat:save', { gameId, messages: wireMessages })
      .then(() => {
        setChatPersistError((prev) =>
          // Only clear errors that came from save (CHAT-WRITE / CHAT-MKDIR);
          // a CHAT-CORRUPT error from a failed load shouldn't be auto-
          // cleared just because save still works against the new (empty)
          // file.
          prev?.code?.startsWith('CRUCIBLE-FOUNDRY-CHAT-WRITE') ||
          prev?.code?.startsWith('CRUCIBLE-FOUNDRY-CHAT-MKDIR')
            ? null
            : prev,
        );
      })
      .catch((err: unknown) => {
        setChatPersistError(unflattenIpcError(err));
      });
    // Deps: gameId + messages only. We deliberately omit
    // `chatPersistError` even though the closure reads it (via the
    // setState callback form on line 114) — including it would
    // re-fire the save on every error toggle, hammering disk on
    // failure paths.
  }, [gameId, messages]);

  // Deploy state (Preview removed in alpha.50 — see plan doc
  // `docs/plan-big-work-and-agents-ui.md`. Coming back as a header-
  // toggle mode in a follow-up restructure.) `editorError` and
  // `showTemplateModal` are unrelated to preview but live in the
  // same general "page chrome" group.
  const [deployState, setDeployState] = useState<DeployState>('idle');
  const [deployMessage, setDeployMessage] = useState('Preparing deployment...');
  // #3132 — the Deploy confirm step is an in-app modal, NOT a native
  // `window.confirm()`. The native dialog was the issue's actual
  // "silent no-op": it is not a DOM node, and CDP/Playwright
  // auto-dismisses native dialogs, so automated flows silently
  // DECLINED the deploy at the confirm guard with no DOM trace and no
  // state change. A DOM modal is visible, testable, and consistent
  // across the desktop shell and the cloud SPA.
  const [deployConfirmOpen, setDeployConfirmOpen] = useState(false);
  // #2349 — dismiss flag for the pre-flight / non-CI deploy-error
  // banner. A deploy FAILURE flips `deployState='error'` + sets
  // `deployMessage`, but `deployMessage` only ever rendered inside the
  // LoadingOverlay (which hides the moment state leaves 'deploying'),
  // and the `<DeployFailureCard>` only arms once CI was triggered. So a
  // PRE-CI failure (e.g. CRUCIBLE-202 uncommitted-changes, a missing
  // remote, a quality-gate refusal) surfaced NOTHING. This flag drives a
  // dismissable error banner for exactly that gap; it's reset on the next
  // Deploy click so a fresh failure always re-surfaces.
  const [deployErrorDismissed, setDeployErrorDismissed] = useState(false);
  // Issue #415 Layer 3 (Codex round 2 fix): when a local pre-
  // flight quality-gate check fails (lint / typecheck / shared
  // build), main pushes the failed check name on
  // `publish:progress.failedCheck`. We surface it via the SAME
  // "Failed step" chip the existing #408/#413 deploy-failure UX
  // uses for CI failures — the chip's `failedStepName` source
  // becomes this local state when no remote
  // `deployFailureContext` exists, mirroring the spec's "use the
  // SAME UX as the existing deploy-failure flow" requirement.
  const [localFailedCheck, setLocalFailedCheck] = useState<string | null>(null);
  // #2080 — the CrucibleError code of the current deploy failure
  // (e.g. 'CRUCIBLE-206'), decoded from the publish:run rejection.
  // Drives the pre-flight banner's plain-language headline
  // (deployFailureCopy); the raw `deployMessage` stays available in
  // the banner's "Technical details" expander. Null when the failure
  // carried no Crucible code (a non-Crucible throw).
  const [deployErrorCode, setDeployErrorCode] = useState<string | null>(null);
  // #3490 — a readiness REFUSAL (not-built, wrong-branch, up-to-date)
  // carries no CrucibleError code, so its plain reason can't come from
  // `deployFailureCopy(deployErrorCode)`. This holds the refusal's own
  // plain copy (from BLOCK_COPY via refusedDeployStateUpdates); when
  // set, the banner headlines it directly instead of the generic
  // code-keyed fallback. Cleared the moment a real deploy proceeds.
  const [deployRefusalCopy, setDeployRefusalCopy] = useState<{
    headline: string;
    nextStep?: string;
  } | null>(null);
  const [editorError, setEditorError] = useState<string | null>(null);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  // Issue #597 — non-blocking toast surfaced when main auto-seeds the
  // interview state file on Workspace open (typical case: the user
  // cloned a game from GitHub and `.foundry/` was correctly gitignored
  // so the seed never made the trip). Auto-fades after 8s — slightly
  // longer than `editorError`'s 5s because the message is informational
  // and the user is likely about to type `/build-game` for the first
  // time, so giving them a beat to read it pays off.
  const [autoSeededBanner, setAutoSeededBanner] = useState<string | null>(null);
  const [pinnedAskUserQuestion, setPinnedAskUserQuestion] = useState<ToolCallEntry | null>(null);
  // #3060 — last failed composer gate-answer message; null when none. Set in
  // handleSendMessage when the answer invoke rejects / returns not-ok, rendered
  // as a composer banner. Cleared when the gate closes (below) so a resolved
  // gate never leaves a stale banner.
  const [composerAnswerError, setComposerAnswerError] = useState<string | null>(null);
  useEffect(() => {
    if (!pinnedAskUserQuestion) setComposerAnswerError(null);
  }, [pinnedAskUserQuestion]);
  // #3059 — the effect interpreter closure (below) is installed WITHOUT gameId
  // in its deps so it does not churn on navigation, so it cannot capture the
  // live gameId. This ref tracks the current active game for the async
  // deps:respond re-enqueue guard: a late rejection must be dropped rather than
  // re-surface a prior game's modal on the game the user switched to.
  const gameIdRef = useRef(gameId);
  useEffect(() => {
    gameIdRef.current = gameId;
  }, [gameId]);

  // Issue #277: when the deploy fails we flip
  // `deployFailureCardEnabled=true`, which kicks off the
  // `deploy:get-failure-context` query inside `useDeployFailure`. The
  // card mounts as soon as the query resolves with `status: 'failure'`
  // and unmounts when the user clicks Dismiss (which clears this
  // flag) or after a successful retry. We do NOT auto-poll — every
  // mount that flipped this flag burns a GitHub API call, and the
  // deploy lifecycle is action-driven (user clicks Deploy, deploy
  // resolves or rejects, card stays until dismissed).
  const [deployFailureCardEnabled, setDeployFailureCardEnabled] = useState(false);
  const { data: deployFailureContext, refetch: refetchDeployFailure } = useDeployFailure(
    gameId,
    deployFailureCardEnabled,
  );

  // Issue #637 — ambient deploy-status snapshot pushed by the main-side
  // watcher (started on this game's `game:status` query). Undefined
  // until the first push; flag-OFF (`CRUCIBLE_DEPLOY_WATCH` != '1') the
  // watcher never starts so this stays undefined and the card renders
  // nothing.
  const deployStatus = useDeployStatus(gameId, { enabled: localFilesystemEffectsAvailable });

  // crucible#3495 — on-demand "already up to date on the TV" signal for
  // the Save-to-TV readiness gate. Same gate as `useDeployStatus` above:
  // the check shells out to `git fetch` against the local checkout, so
  // it's meaningless (and wasted work) wherever local filesystem/git
  // effects aren't available. `upToDate` stays `undefined` (fail-open,
  // never blocks) until the first successful check.
  const { upToDate: deployUpToDate } = useDeployUpToDate(gameId, {
    enabled: localFilesystemEffectsAvailable,
  });
  // Moved up from its original position (was declared much later in
  // this component) so the round-2 Bugbot invalidation effect right
  // below can use it — a pure context read, safe to hoist.
  const queryClient = useQueryClient();
  // Bugbot round 2 on #3495 — "Stale up-to-date blocks deploy": a
  // cached `upToDate: true` with NO invalidation trigger would leave
  // the Deploy button wrongly disabled for up to 30s (or until the
  // next window focus) after a NEW same-session commit — exactly the
  // false-block class this feature's fail-open contract exists to
  // rule out. `gameStatus.localGit.lastCommitDate` changes the moment
  // a new commit lands (from the chat/build agent, or externally);
  // force the next read fresh the instant that happens rather than
  // trusting the stale cache. Skips the very first render (the ref's
  // initial value already matches) so mounting doesn't fire a
  // spurious invalidation.
  // #3495 Bugbot round 3 — invalidate on a DIRTY-TREE change too, not
  // only a new commit. The core signal treats a dirty working tree as
  // "not up to date" (publish:run auto-commits it before push), so
  // edits that bump `uncommittedChanges` WITHOUT a new commit add
  // deployable work — a cached `upToDate: true` must be re-checked the
  // instant the dirty count shifts, or Deploy stays wrongly disabled
  // for up to staleTime after real work appears. (The `isStale`
  // fail-open gate already bounds that window; this closes the
  // same-session-edit gap inside it.)
  const lastCommitDateRef = useRef(gameStatus?.localGit?.lastCommitDate);
  const uncommittedChangesRef = useRef(gameStatus?.localGit?.uncommittedChanges);
  useEffect(() => {
    const nextCommit = gameStatus?.localGit?.lastCommitDate;
    const nextDirty = gameStatus?.localGit?.uncommittedChanges;
    // #3495 Bugbot — only a change from a PREVIOUSLY-KNOWN value counts.
    // `game:status` hydrates async, so localGit fields start `undefined`
    // and land their first real values after mount; treating that
    // initial `undefined → value` hydration as a "change" would fire a
    // spurious invalidation on every first status resolve (a needless
    // git fetch that briefly flips a confirmed `upToDate: true` to
    // `undefined`). Requiring the ref to already hold a defined baseline
    // makes the first hydration set that baseline silently.
    const commitChanged =
      nextCommit !== undefined &&
      lastCommitDateRef.current !== undefined &&
      nextCommit !== lastCommitDateRef.current;
    const dirtyChanged =
      nextDirty !== undefined &&
      uncommittedChangesRef.current !== undefined &&
      nextDirty !== uncommittedChangesRef.current;
    // Always advance the refs to the latest observed values (including
    // the first hydration), so a later real change is measured against
    // the current baseline rather than `undefined`.
    lastCommitDateRef.current = nextCommit;
    uncommittedChangesRef.current = nextDirty;
    if (commitChanged || dirtyChanged) {
      invalidateDeployUpToDate(queryClient, client, gameId);
    }
  }, [
    gameStatus?.localGit?.lastCommitDate,
    gameStatus?.localGit?.uncommittedChanges,
    gameId,
    queryClient,
    client,
  ]);
  // crucible#3501 / hw#2080 — feed the deploy-failure banner's "Report
  // this problem" URL assembly (below, after the early-return guard).
  // MUST live here, alongside the other deploy hooks, not near that
  // assembly: hooks must run unconditionally on every render, and
  // `isValidWorkspaceRouteGameId` short-circuits with `return null`
  // further down this component (Bugbot round 1 on #3501).
  const appFrame = useAppFrame();
  const { data: deployReportFoundryVersion } = useIpcQuery('app:get-version', undefined, {
    enabled: Boolean(gameStatus?.githubRepository),
    staleTime: Infinity,
  });

  const clonedRepoFullName = gameStatus?.clonedFrom;
  // Lifecycle badge (Draft/Building/Prototype/Live/Error) for the project panel.
  const gameStatusLite = useGameStatusLite(projectRef, { enabled: !!gameId });
  const { data: clonedRepoAccess } = useIpcQuery(
    'github:repo-access',
    { repoFullName: clonedRepoFullName ?? '' },
    { enabled: !!clonedRepoFullName, staleTime: 5 * 60_000 },
  );

  // Live-preview scaffold-first: the inferred-genre outcome for the preview
  // chip, from three sources in priority order, all validated
  // `ScaffoldFirstInference`s:
  //  1. DESKTOP durable (A2.3c) — `game:status` reads the persisted crucible.json
  //     (not served over the cloud RPC, so undefined on crucible-web);
  //  2. route-state flash (A2.3b) — set right after create, before queries resolve;
  //  3. WEB durable — the per-game localStorage cache (survives refresh / re-nav
  //     on the SPA where (1) isn't available). Memoised on gameId — a sync read.
  const persistedGenreInference = useMemo(() => readPersistedScaffoldFirst(gameId), [gameId]);
  // Freshness rule (see pickDurableGenreInference): `game:status` (desktop,
  // reads crucible.json) is authoritative; the localStorage cache is consulted
  // ONLY when that query ERRORED — i.e. the cloud RPC doesn't serve it
  // (crucible-web). Keying on the error state (not "data is undefined") means a
  // stale cache can't flicker during the desktop load window.
  const previewGenreInference: ScaffoldFirstInference | null = pickDurableGenreInference({
    statusErrored: gameStatusErrored,
    fromStatus: gameStatus?.scaffoldFirst ?? null,
    fromRouteState: scaffoldFirstInference,
    fromCache: persistedGenreInference,
  });

  // Facepile — contributors for the current game. We read the already-cached
  // `game:list:lite` query (populated by Dashboard) so no extra network round-
  // trip is needed; the data is stale-while-revalidate and typically already
  // in the QueryClient by the time the user navigates here.
  const { data: gameLiteList } = useIpcQuery('game:list:lite', undefined, {
    enabled: shouldQueryWorkspaceGameLiteList(flags),
  });
  const gameContributors = gameLiteList?.find((g) => g.gameId === gameId)?.contributors;

  // Crucible #416 Chunk C — phase strip subscription. The hook owns
  // the `phase-progress:subscribe` IPC + the `phase-progress:update`
  // listener for the active gameId; cleanup unsubscribes the watcher
  // in main on game switch and on unmount.
  const gameStateAvailable = projectCapabilityAvailable('game-state');
  const phaseProgress = usePhaseProgress(gameId, {
    enabled: gameStateAvailable,
    project: projectRef,
  });
  const foundryGameState = useFoundryGameStateSnapshot(gameId, {
    enabled: gameStateAvailable,
    project: projectRef,
  });
  const previewPhaseGate = useMemo<PreviewPhaseGate | undefined>(() => {
    if (foundryGameState?.build.terminal && foundryGameState.build.status === 'completed') {
      return undefined;
    }
    if (foundryGameState?.phase.current) {
      return {
        currentPhase: foundryGameState.phase.current,
        phaseLabel: foundryGameState.phase.label ?? `Phase ${foundryGameState.phase.current}`,
      };
    }
    return phaseProgress?.kind === 'derived'
      ? {
          currentPhase: phaseProgress.derived.current,
          phaseLabel: phaseProgress.derived.label,
        }
      : undefined;
  }, [
    foundryGameState?.build.status,
    foundryGameState?.build.terminal,
    foundryGameState?.phase.current,
    foundryGameState?.phase.label,
    phaseProgress,
  ]);
  // `previewPhaseGate === undefined` has multiple meanings: the authoritative
  // build-progress projection is still hydrating, the game is intentionally
  // ungated (legacy / no progress file), or that projection could not be read.
  // Auto-start is safe only after the same source used by main's dev:start
  // preflight returns a positive start decision. Parse/read errors stay manual
  // so navigation cannot manufacture an error card without user action.
  const previewPhaseAutoStartReady = isPreviewPhaseReadyForAutoStart(
    gameStateAvailable,
    phaseProgress,
  );
  const previewPhaseAutoStartEvidence = useMemo(
    () => previewPhaseAutoStartEvidenceKey(gameStateAvailable, phaseProgress),
    [gameStateAvailable, phaseProgress],
  );
  const openPreviewSurface = useCallback(() => {
    setWorkspaceMode('preview');
  }, []);

  const openProtoHubSurface = useCallback(
    (source: ProtoHubSource) => {
      // No native simulator (cookie-mode cloud SPA / pure-web): do not
      // switch into 'protohub' mode — mounting the surface would invoke
      // host-only `protohub:*` over the wire and bannered
      // "Method not found: protohub.open@1" (crucible#2325). The manual
      // "Display on Hub" button is already hidden in these shells
      // (shellChrome !== 'electron'); this guards the post-deploy
      // auto-open, the one remaining caller that reaches here in cloud.
      if (!nativeSimulatorAvailable) return;
      setProtoHubSource(source);
      setWorkspaceMode('protohub');
    },
    [nativeSimulatorAvailable],
  );

  const handleDisplayOnHub = useCallback(() => {
    openProtoHubSurface('manual');
  }, [openProtoHubSurface]);

  const recordPhaseEvent = usePhaseEventsStore((s) => s.recordEvent);
  // Crucible #507: drives the PhaseStrip's smart empty-state when
  // markers haven't been written yet (e.g. pre-alpha.71 games, #506).
  // Crucible #519: also drives the BuildPartialBanner — surfaces a
  // sticky warning above the chat when status === 'built-partial'.
  const interviewStatusQuery = useInterviewStatus(gameId);
  const interviewStatus = interviewStatusQuery.data?.status;
  const interviewStalenessTick = useElapsedTick(interviewStatus === 'in-progress', 30_000);

  // Issue #791 — workspace-scoped activity snapshot for the top-bar
  // agent-activity pill. Same `gameId`-keyed hook the banner +
  // agents panel already subscribe to; the cross-game discard rules
  // live inside the hook so re-subscribing here is cheap and the
  // pill stays consistent with the other transparency surfaces. The
  // `gameId ?? ''` coalesce mirrors the `useIpcQuery('game:status'...)`
  // pattern below — the pre-resolve window leaves the hook keyed on
  // the empty string and the subscription's cross-game guard drops
  // every push until the real `gameId` lands a render later.
  const activitySnapshot = useActivitySnapshot(gameId ?? '');

  // Interview state + phase progress feed the phase strip, chat status
  // row, and plan panel below. The Deploy-to-TV button does not gate on
  // build COMPLETENESS (deploy failures surface in-app — see
  // `deploy-action-state.ts`), but #3132 added a fail-open readiness
  // gate for KNOWN never-built / mid-build interview statuses (see the
  // `deployBuildReadiness` derivation near the deployActionState memo).
  const interviewStateQuery = useInterviewState(gameId);
  const refetchInterviewState = interviewStateQuery.refetch;

  // #191 quick-batch — surface the depth + subPhase + plan body off
  // the interview state. Per schema PR #543, the three fields live
  // at the TOP LEVEL of `BuildGameInterviewState` (siblings of
  // `status`, `phaseBDecisions`, etc.) — NOT nested under
  // `interview`. Use the canonical helpers
  // (`getEffectiveInterviewDepth` etc.) so the legacy-default rule
  // and the unknown-future-value defensive narrowing live in ONE
  // place. Pre-#191 games fold to `depth = 'full'` (no plan-mode
  // UI, no back-to-plan button); a SKILL-side new sub-phase value
  // we haven't learned yet folds to `subPhase = undefined` and
  // similarly suppresses plan-mode chrome.
  const [fastAnswersSubmittedAt, setFastAnswersSubmittedAt] = useState<number | null>(null);
  const [fastPlanMissingAt, setFastPlanMissingAt] = useState<number | null>(null);
  const fastAnswersSubmittedAtRef = useRef<number | null>(null);
  const fastPlanReadyRef = useRef(false);
  const interviewState = interviewStateQuery.data;
  const autoBuildState = interviewStatusQuery.data?.autoBuild ?? interviewState?.autoBuild ?? null;
  const legacyDeployAutoBuildStates = [
    interviewStatusQuery.data?.autoBuild,
    interviewState?.autoBuild,
  ] as const;
  // A best-effort-cleared `commit-starting` record is trusted for Deploy only
  // inside a bounded liveness window. Keep that wall-clock decision advancing
  // while the handoff remains fresh, then stop ticking once it can no longer
  // affect readiness. The phase-only stale-build clock is allowed to stay
  // frozen here because Phase B has not started yet.
  useElapsedTick(
    legacyDeployAutoBuildStates.some(
      (state) =>
        state?.status === 'commit-starting' &&
        isFreshLegacyDeployHandoff(state.updatedAt, Date.now()),
    ),
    30_000,
  );
  const interviewDepth = interviewState ? getEffectiveInterviewDepth(interviewState) : 'full';
  const interviewSubPhase = interviewState
    ? getEffectiveInterviewSubPhase(interviewState)
    : undefined;
  const interviewPlan = interviewState ? getEffectivePlan(interviewState) : '';
  useEffect(() => {
    fastAnswersSubmittedAtRef.current = fastAnswersSubmittedAt;
  }, [fastAnswersSubmittedAt]);
  useEffect(() => {
    const planReady =
      interviewDepth !== 'quick' ||
      interviewSubPhase !== 'quick' ||
      interviewPlan.trim().length > 0;
    fastPlanReadyRef.current = planReady;
    if (planReady) {
      setFastAnswersSubmittedAt(null);
      setFastPlanMissingAt(null);
    }
  }, [interviewDepth, interviewPlan, interviewSubPhase]);
  useEffect(() => {
    if (fastAnswersSubmittedAt === null) return undefined;
    const elapsed = Date.now() - fastAnswersSubmittedAt;
    const remaining = Math.max(0, FAST_BUILD_PLAN_BRIDGE_TIMEOUT_MS - elapsed);
    const timer = window.setTimeout(() => {
      if (!fastPlanReadyRef.current) setFastPlanMissingAt(Date.now());
      setFastAnswersSubmittedAt(null);
    }, remaining);
    return () => window.clearTimeout(timer);
  }, [fastAnswersSubmittedAt]);
  const pendingPhaseBDecision = useMemo(
    () =>
      resolvePendingPhaseBDecision({
        foundrySnapshot: foundryGameState,
        decision: pendingPhaseBDecisionFromState(interviewState),
        activitySnapshot,
        phaseProgress,
      }),
    [activitySnapshot, foundryGameState, interviewState, phaseProgress],
  );
  // Crucible #823 — elapsed timer anchor for the plan-drafting empty
  // state. Use the orchestrator (`__main__`) agent's `startedAt` from
  // the live activity snapshot as the anchor epoch. This is the moment
  // the orchestrator's most-recent subsystem/action changed — a close
  // proxy for "when the composing pass started". Falls back to null
  // when the snapshot is absent (no active build) or when the plan is
  // already rendered (the timer is only shown while plan is empty).
  const draftingStartedAt: number | null =
    activitySnapshot?.byAgentId['__main__']?.startedAt ?? null;
  // Codex round-1 HIGH — thread the orchestrator's terminal `stoppedAt`
  // through to `<PlanModeView>` so the elapsed timer freezes when the
  // agent reaches a terminal state. Without this, the timer keeps
  // ticking through the activity-row linger window even after the
  // orchestrator has stopped. Null means the agent is still running.
  const draftingStoppedAt: number | null =
    activitySnapshot?.byAgentId['__main__']?.stoppedAt ?? null;
  const showPlanModeView = showPlanModeFor({
    depth: interviewDepth,
    subPhase: interviewSubPhase,
    // #826 Defect C: pass plan content so the gate can distinguish
    // "agent is drafting" (plan null/empty → stay on chat panel)
    // from "plan ready for review" (plan present → show PlanModeView).
    plan: interviewPlan,
  });
  // Modal state for the back-to-plan confirmation. Lives at Workspace
  // scope so the header button + modal share one source of truth.
  const [backToPlanModalOpen, setBackToPlanModalOpen] = useState(false);
  // True while either Start Building or Back to plan is in flight via
  // IPC. Disables the buttons so a second click can't double-dispatch.
  const [planModeBusy, setPlanModeBusy] = useState(false);
  const guidedApprovalRef = useRef<{ planKey: string; approvalId: string } | null>(null);
  // PR #544 follow-up (Bugbot + Macroscope MEDIUM): user-facing error
  // copy for the Start Building / Back to plan IPC. Originally the
  // two handlers used `try / finally` with no catch, so an IPC
  // rejection silently re-enabled the buttons; this state plus the
  // toast rendered at page chrome surfaces the failure so the user
  // knows their click had no effect. Renders at page-chrome scope so
  // BOTH plan-mode (full-screen `<PlanModeView>`) AND build-mode
  // (header back-to-plan button) clicks reuse the same surface. Auto-
  // clears after 6s — slightly longer than `editorError`'s 5s
  // because plan-mode failures often need the user to switch to
  // Settings, which takes longer than reading a chat-editor error.
  const [planModeError, setPlanModeError] = useState<string | null>(null);
  const planModeErrorTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const showPlanModeError = useCallback((message: string): void => {
    setPlanModeError(message);
    if (planModeErrorTimerRef.current) clearTimeout(planModeErrorTimerRef.current);
    planModeErrorTimerRef.current = setTimeout(() => setPlanModeError(null), 6_000);
  }, []);
  useEffect(() => {
    return () => {
      if (planModeErrorTimerRef.current) clearTimeout(planModeErrorTimerRef.current);
    };
  }, []);
  // True when the paginated interview overlay is currently waiting for
  // an answer. Keep this above the run-state selector so the header can
  // say "Waiting on you" even while the orchestrator row is still live.
  const [interviewSlotActive, setInterviewSlotActive] = useState(false);
  // #2058 — in-chat asset-review card. When `interviewIntegrated` is ON and
  // the build task fires `asset-review:ready`, we surface the review inline in
  // the chat stream (via ChatTvSteeringNudge → TvSteeringSurface) instead of the
  // BuildReviewOverlay modal. Null → no card; non-null → the nudge is mounted in
  // the message stream.
  const [assetReviewCardGameId, setAssetReviewCardGameId] = useState<string | null>(null);
  const [northStarReviewCompleteGameId, setNorthStarReviewCompleteGameId] = useState<string | null>(
    null,
  );
  const [northStarReviewSummary, setNorthStarReviewSummary] =
    useState<NorthStarReviewSummary | null>(null);
  const [northStarBuildGateReleasedGameId, setNorthStarBuildGateReleasedGameId] = useState<
    string | null
  >(null);
  // #2058 — true while an adaptive soul-intake run is in flight for this game, so the
  // interview is hosted INLINE in the Workspace chat (the newUi=1 NewGame→/workspace flow).
  const [soulIntakeActive, setSoulIntakeActive] = useState(false);
  const [soulInterviewFinalizing, setSoulInterviewFinalizing] = useState(false);
  const [intakeAuthorityMissingGameId, setIntakeAuthorityMissingGameId] = useState<string | null>(
    null,
  );
  const intakeAuthorityMissing = intakeAuthorityMissingGameId === gameId;
  // crucible#3041/#3516 — the split-create DESIGN lane defers phase 2 until an
  // explicit "Build this" trigger (Sean's ratified three-path design: only the
  // Build lane auto-finishes). Set true the moment the between-phases interview
  // finishes for a deferred Design create (both the live-completion handler and
  // the mount-time `intake:turn` probe below can be the one that observes it —
  // whichever fires first flips this). Gates the "Build my game" affordance;
  // NO STRANDING means this must stay true (and the affordance mounted) for the
  // whole deferred window, not just a one-shot event.
  const [deferredDesignInterviewDone, setDeferredDesignInterviewDone] = useState(false);
  // Tracks the `game:create:finish` invoke lifecycle for the "Build my game"
  // button: 'idle' (not yet clicked, or a previous click errored — retryable),
  // 'pending' (in flight — guards the double-click), 'done' (resolved — the
  // deferred window is over, hide the affordance).
  const [deferredDesignBuildStatus, setDeferredDesignBuildStatus] = useState<
    'idle' | 'pending' | 'done'
  >('idle');
  // crucible#3183 — feed the terminal design-system failure into
  // deriveWorkspaceRunState (via WorkspaceRunSignals.designSystemFailedAt)
  // instead of just a card's copy. Before this, the plan-phase run-state
  // branches had no way to learn the design agent already gave up, so
  // "drafting-plan" ran forever with no backstop when no plan ever landed.
  const [designSystemFailedAt, setDesignSystemFailedAt] = useState<number | null>(null);
  useEffect(() => {
    return client.subscribe('create:build-step', (event) => {
      if (event.gameId !== gameId) return;
      if (event.step !== 'design-system' || event.status !== 'failed') return;
      setDesignSystemFailedAt(Date.now());
    });
  }, [gameId]);
  // crucible#3041/#3516 — a terminal soul-intake `create:build-step` pushed by
  // main is the SINGLE authority that settles a deferred Design create's
  // between-phases interview (flips `deferredDesignInterviewDone` → mounts the
  // "Build my game" affordance). Both terminal statuses land here:
  //  - `failed` — the interview threw or never ran (Bugbot MEDIUM finding 2:
  //    `IntegratedSoulInterview`'s own `onDone` fires ONLY on `turn.done`, so a
  //    failure otherwise left the interview on its dead-end error card and the
  //    affordance never mounted — a hard stranding).
  //  - `done` — the interview succeeded AND main finished `writeIntakeResult`
  //    (Bugbot HIGH "Finish races intake persistence"). The interview panel's
  //    local `onDone` fires the instant the interview resolves, BEFORE that
  //    persist; mounting the affordance on it let a fast "Build my game" click
  //    run phase 2 while `intake-result.json` was still absent and silently
  //    drop the enriched interview. Main pushes `done` only AFTER persisting,
  //    so gating the mount on this signal (not the panel onDone) means the
  //    finish can never outrun the write. `handleSoulInterviewDone` below no
  //    longer flips the state for this reason.
  // Scoped to `deferredDesignHandoff` — a Build-lane interview needs none of
  // this (the Build lane auto-finishes into the build regardless).
  useEffect(() => {
    if (!deferredDesignHandoff) return;
    return client.subscribe('create:build-step', (event) => {
      if (event.gameId !== gameId) return;
      if (event.step !== 'soul-intake' || (event.status !== 'done' && event.status !== 'failed'))
        return;
      setSoulIntakeActive(false);
      setSoulInterviewFinalizing(false);
      setWorkspaceMode('design');
      setDeferredDesignInterviewDone(true);
    });
  }, [deferredDesignHandoff, gameId]);
  // crucible#3041/#3516 (Bugbot HIGH "No-interview settle can miss subscribe")
  // — the DURABLE mount-time backstop, and the single mount-time flip authority
  // for a deferred Design create. The live `create:build-step` subscribe above
  // is the fast path, but its terminal push is ONE-SHOT and can land before
  // this Workspace subscribes; the in-memory `intake:turn` probe cannot recover
  // the no-interview didn't-run path (no session ⇒ it throws). Main writes
  // `.foundry/persistent/design-handoff-status.json` on EVERY deferred-Design
  // terminal outcome (interviewed / no-interview / failed), so reading it on
  // mount / gameId change flips the affordance uniformly, regardless of whether
  // an interview session ever existed or whether the live push was observed.
  // Best-effort: a read failure just leaves the live subscribe as the settle.
  useEffect(() => {
    if (!deferredDesignHandoff || !gameId) return;
    let cancelled = false;
    void (async () => {
      try {
        const status = await client.invoke('create:deferred-design-status:get', { gameId });
        if (!cancelled && status) {
          setSoulIntakeActive(false);
          setSoulInterviewFinalizing(false);
          setWorkspaceMode('design');
          setDeferredDesignInterviewDone(true);
        }
      } catch {
        /* best-effort backstop — the live create:build-step subscribe is the fast path */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [deferredDesignHandoff, gameId]);
  const handleSoulInterviewDone = useCallback(() => {
    // IntegratedSoulInterview calls onDone only after the durable terminal
    // handoff has reached its destination (for Guided builds, after
    // plan-review status is ready). The snapshot callback owns the interim
    // "Preparing your build plan" state; clear it here so the prepared plan
    // surface can take over without requiring a reload.
    setSoulInterviewFinalizing(false);
    setSoulIntakeActive(false);
    setIntakeAuthorityMissingGameId((current) =>
      clearIntakeAuthorityMissingForGame(current, gameId),
    );
    if (createHandoffMode === 'design') {
      setWorkspaceMode('design');
      // NOTE: the "Build my game" affordance is deliberately NOT mounted here.
      // This handler fires on the interview panel's LOCAL completion, which
      // races main's `writeIntakeResult` persist; mounting the affordance on it
      // let a fast finish run phase 2 before `intake-result.json` existed
      // (Bugbot HIGH "Finish races intake persistence"). Main pushes a terminal
      // soul-intake `create:build-step` AFTER the persist, and the subscribe
      // above is the single authority that flips `deferredDesignInterviewDone`.
    } else {
      // Plan preparation completed after the query's last snapshot. Refresh
      // the durable plan identity immediately so plan-review navigation does
      // not fall through to chat until a reload or the next polling tick.
      void refetchInterviewState();
    }
  }, [createHandoffMode, gameId, refetchInterviewState]);
  const handleSoulInterviewUnavailable = useCallback(() => {
    if (!gameId) return;
    setSoulInterviewFinalizing(false);
    setSoulIntakeActive(false);
    setIntakeAuthorityMissingGameId(gameId);
  }, [gameId]);
  const handleSoulInterviewSnapshot = useCallback((snapshot: IntegratedSoulInterviewSnapshot) => {
    if (snapshot.finalizing) {
      setSoulInterviewFinalizing(true);
      return;
    }
    if (snapshot.current || snapshot.thinking) {
      setSoulInterviewFinalizing(false);
    }
  }, []);
  useEffect(() => {
    setNorthStarReviewCompleteGameId(null);
    setNorthStarReviewSummary(null);
    setNorthStarBuildGateReleasedGameId(null);
    setDesignSystemFailedAt(null);
    // crucible#3516 — a fresh game (in-place switch) starts with no
    // deferred-build state of its own; re-derived from route state / the
    // intake probe below as this effect and the mount probe re-run.
    setDeferredDesignInterviewDone(false);
    setDeferredDesignBuildStatus('idle');
  }, [gameId]);
  // crucible#3041/#3516 — the Design-tab "Build this" affordance. The
  // gate + the IPC call are pure, exported, unit-tested functions (see
  // `shouldShowDeferredDesignBuildAffordance` / `triggerDeferredDesignFinish`
  // above the component) — this wiring just threads component state through
  // them and is guarded against double-click via deferredDesignBuildStatus.
  const handleBuildThisDeferredDesign = useCallback(async (): Promise<void> => {
    if (!gameId || deferredDesignBuildStatus === 'pending') return;
    setDeferredDesignBuildStatus('pending');
    const result = await triggerDeferredDesignFinish(client, gameId);
    if (result.ok) {
      setDeferredDesignBuildStatus('done');
    } else {
      // Retryable — leaves buildStatus at 'idle' so the affordance stays
      // clickable instead of stranding the creator on a failed attempt.
      setDeferredDesignBuildStatus('idle');
      setPreviewToast({
        tone: 'error',
        message: `Couldn't start building your game: ${result.message}`,
      });
    }
  }, [gameId, deferredDesignBuildStatus, client]);
  const showBuildThisDeferredDesign = shouldShowDeferredDesignBuildAffordance({
    deferredDesignHandoff,
    deferredDesignInterviewDone,
    buildStatus: deferredDesignBuildStatus,
  });
  // Fable-review fix (2026-07-16) — the live `create:build-step` subscribe
  // above is the ONLY thing that ever set the two fields it just reset. A
  // push missed while the user was elsewhere, or this exact gameId switch,
  // left them null forever with no backstop (see `useDesignAgentStatusRecovery`'s
  // doc comment). Probe the durable `.foundry/persistent/design-agent-status.json`
  // marker on mount / gameId change and hydrate from it when the live push
  // never landed. Runs AFTER the reset above resolves (this probe is async;
  // the reset is synchronous within the same commit), and only fills in a
  // still-null field so it never clobbers a live push that arrived first.
  useDesignAgentStatusRecovery({
    gameId,
    onRecoveredFailure: (failedAt) => {
      setDesignSystemFailedAt((prev) => prev ?? failedAt);
    },
  });
  const buildKickoffFirstSeenRef = useRef<Map<string, number>>(new Map());
  const buildKickoffResumeAttemptedRef = useRef<Set<string>>(new Set());
  const stalledBuildResumeAttemptedRef = useRef<Set<string>>(new Set());
  const previousBuildKickoffGameIdRef = useRef<string | null>(null);
  const [buildKickoffClockNow, setBuildKickoffClockNow] = useState(() => Date.now());
  useEffect(() => {
    const previousGameId = previousBuildKickoffGameIdRef.current;
    if (previousGameId && previousGameId !== gameId) {
      const prefix = `${previousGameId}:`;
      for (const key of buildKickoffFirstSeenRef.current.keys()) {
        if (key.startsWith(prefix)) buildKickoffFirstSeenRef.current.delete(key);
      }
      for (const key of buildKickoffResumeAttemptedRef.current.keys()) {
        if (key.startsWith(prefix)) buildKickoffResumeAttemptedRef.current.delete(key);
      }
      for (const key of stalledBuildResumeAttemptedRef.current.keys()) {
        if (key.startsWith(prefix)) stalledBuildResumeAttemptedRef.current.delete(key);
      }
      // `<Workspace>` is NOT remounted per gameId (the route has no
      // `key={gameId}` — only child components do), so this poll-fed state must
      // be reset on a game switch like the kickoff refs above; otherwise a
      // stale `true` from the previous game could briefly suppress a legitimate
      // lost-kickoff retry for the new one until its first poll tick.
      setBuildInFlightFromPoll(false);
    }
    previousBuildKickoffGameIdRef.current = gameId ?? null;
  }, [gameId]);
  const buildKickoffKey =
    gameId && interviewDepth === 'quick'
      ? buildKickoffAttemptKey(gameId, interviewState?.planRevision, interviewState?.planHash)
      : null;
  const buildKickoffPhaseStarted = hasPhaseProgressBeyondPlanReview(phaseProgress);
  const currentPlanWasHandedToBuild = savedPlanMatchesBuilt({
    planRevision: interviewState?.planRevision,
    planHash: interviewState?.planHash,
    lastBuiltPlanRevision: interviewState?.lastBuiltPlanRevision,
    lastBuiltPlanHash: interviewState?.lastBuiltPlanHash,
  });
  const buildKickoffCommitted =
    !!gameId &&
    interviewDepth === 'quick' &&
    (interviewSubPhase === 'committed' || currentPlanWasHandedToBuild) &&
    !buildKickoffPhaseStarted &&
    !isTerminalInterviewStatus(interviewState?.status);
  // Stand down the kickoff watchdog when EITHER liveness signal says a build is
  // running (see shouldTrackBuildKickoff): the push `activitySnapshot` (lit for a
  // GUI-driven stream) OR the poll's `agent:get-status` (reliable for a detached,
  // server-driven build the GUI only observes).
  const buildKickoffShouldTrack = shouldTrackBuildKickoff({
    committed: buildKickoffCommitted,
    hasFreshRunningActivity: hasFreshRunningActivitySnapshot(activitySnapshot),
    buildInFlightFromPoll,
  });
  useEffect(() => {
    if (!gameId) return;
    if (!buildKickoffCommitted) {
      const prefix = `${gameId}:`;
      for (const key of buildKickoffFirstSeenRef.current.keys()) {
        if (key.startsWith(prefix)) buildKickoffFirstSeenRef.current.delete(key);
      }
      for (const key of buildKickoffResumeAttemptedRef.current.keys()) {
        if (key.startsWith(prefix)) buildKickoffResumeAttemptedRef.current.delete(key);
      }
      return;
    }
    if (!buildKickoffShouldTrack || !buildKickoffKey) return;
    if (!buildKickoffFirstSeenRef.current.has(buildKickoffKey)) {
      const now = Date.now();
      buildKickoffFirstSeenRef.current.set(buildKickoffKey, now);
      setBuildKickoffClockNow(now);
    }
  }, [buildKickoffCommitted, buildKickoffKey, buildKickoffShouldTrack, gameId]);
  useEffect(() => {
    if (!buildKickoffCommitted) return undefined;
    const timer = window.setInterval(() => {
      setBuildKickoffClockNow(Date.now());
    }, 1_000);
    return () => window.clearInterval(timer);
  }, [buildKickoffCommitted]);
  // #3108 — referentially STABLE until an observable field actually changes.
  // The old inline useMemo here depended on the raw 1s `buildKickoffClockNow`
  // tick, so it rebuilt a NEW object every second at unchanged status; that
  // object feeds the `workspaceRunState` useMemo → `usePreviewModule`, churning
  // the preview-derivation path once per second for the whole kickoff-tracking
  // window. The hook derives the same fields from the same inputs (identical
  // escalation timing — see use-build-kickoff-watchdog.ts) but keys the object
  // identity on status/committedAt/stalledAt. The ref reads below happen during
  // render, exactly as the old memo body's did.
  const buildKickoffWatchdog = useBuildKickoffWatchdog({
    tracking: buildKickoffShouldTrack && buildKickoffKey !== null,
    committedAt: buildKickoffKey
      ? buildKickoffFirstSeenRef.current.get(buildKickoffKey)
      : undefined,
    resumeAttempted: buildKickoffKey
      ? buildKickoffResumeAttemptedRef.current.has(buildKickoffKey)
      : false,
    clockNow: buildKickoffClockNow,
  });
  // #3033/#3034 — the SINGLE build-watch liveness source. One client-relative
  // (skew-immune) verdict on one clock, consumed by the chat status row, the
  // right-panel now-summary, AND the run-state escalations — no per-consumer
  // clock to forget to advance. `nowMs` is the same escalation clock (arms
  // phase-source-agnostically across the FULL 3-6 range, round-3/round-5),
  // reused for run-state's 15-minute stale-BUILD wall-clock check.
  const {
    liveness: buildLiveness,
    provenLive: buildProvenLive,
    nowMs: staleBuildClockNow,
    snapshotPhaseStartedAt: buildSnapshotPhaseStartedAt,
  } = useActivityLiveness({
    gameId,
    interviewLastActivityAt: interviewStatusQuery.data?.lastActivityAt,
    phaseProgress,
    snapshotPhaseCurrent: foundryGameState?.phase?.current,
    // #3033 round-3 — scope proof-of-life to the build RUN so a same-game retry
    // does not inherit the prior run's proof. buildRunId is stable across phases
    // and changes on a new run; it is present exactly when lastActivityAt is
    // (same PR-A+ cloud runtime).
    buildEpochKey: foundryGameState?.build.buildRunId ?? null,
  });
  // Slot cards render in the main chat (not the ArtifactPane) so the sidebar
  // stays visible during the one-at-a-time interview.
  const artifactPaneOpen = flags.newUi && interviewStatus === 'complete' && !artifactSoftClosed;
  const workspaceRunState = useMemo(
    () =>
      deriveWorkspaceRunState({
        activitySnapshot,
        interviewState,
        interviewStatus,
        interviewProgressAt: interviewStatusQuery.data?.progressAt ?? null,
        interviewNowMs: Date.now(),
        phaseProgress,
        // #3033/#3034 — POSITIVE proof-of-life from the SINGLE client-relative
        // source suppresses the stale-build / stale-final-review escalations.
        // Round-2: only a client-OBSERVED advance counts (not a warm-up `fresh`),
        // so a build mounted-into with an already-old stamp still escalates on
        // the backstops' own criteria. stalled/unknown/warm-up keep them armed.
        provenLive: buildProvenLive,
        foundrySnapshot: foundryGameState,
        isAiTyping,
        chatPersistError,
        planModeError,
        initialKickoff,
        isInterviewWaitingForUser: interviewSlotActive,
        intakeAuthorityMissing,
        buildKickoffWatchdog,
        fastAnswersSubmittedAt,
        fastPlanMissingAt,
        autoBuildState,
        nowMs: staleBuildClockNow,
        // #3035 — client-relative snapshot phase-start anchor for the
        // snapshot-only implementation-phase stale-build detector.
        snapshotPhaseStartedAt: buildSnapshotPhaseStartedAt,
        // crucible#3183 — see WorkspaceRunSignals.designSystemFailedAt's doc
        // comment: the design agent's own terminal 'failed' status, so the
        // plan-phase branches stop waiting for a plan that isn't coming.
        designSystemFailedAt,
      }),
    [
      activitySnapshot,
      interviewState,
      interviewStatus,
      interviewStatusQuery.data?.progressAt,
      interviewStalenessTick,
      phaseProgress,
      buildProvenLive,
      foundryGameState,
      isAiTyping,
      chatPersistError,
      planModeError,
      initialKickoff,
      interviewSlotActive,
      intakeAuthorityMissing,
      buildKickoffWatchdog,
      fastAnswersSubmittedAt,
      fastPlanMissingAt,
      autoBuildState,
      staleBuildClockNow,
      buildSnapshotPhaseStartedAt,
      designSystemFailedAt,
    ],
  );
  const resumeBuildActionKind = resumeBuildActionKindFromRunState(workspaceRunState);
  const buildCompletionUnverified = isCompletionVerificationRetryRunState(workspaceRunState);

  // The live-preview "brain" — owns the session, lifecycle, derived state,
  // and the auto-start / cloud-reset / diagnostics-refetch effects (design
  // doc §4 PR A; see `preview/usePreviewModule.ts`). The preview surface
  // itself opens only manually, via the header "Develop" / "Preview" tabs.
  // Placed here so all its Workspace-owned inputs (run-state, phase gate, interview
  // state, the mode / split setters) are in scope. Every former inline
  // `previewSession.*` / `previewDevLoopState` / `displayPreviewUrl` … read
  // downstream is now `preview.*`.
  const preview = usePreviewModule({
    gameId,
    installStateAvailable: localFilesystemEffectsAvailable,
    flags: {
      loaded: flags.loaded,
      livePreview: flags.livePreview,
      newUi: flags.newUi,
      inAppPairing: flags.inAppPairing,
    },
    interviewState,
    foundryGameState,
    workspaceRunState,
    workspaceConfig,
    isAiTyping: chat.isAiTyping,
    previewPhaseGate,
    previewPhaseAutoStartReady,
    previewPhaseAutoStartEvidence,
    setWorkspaceMode,
    openPreviewSurface,
    setLivePreviewSplitOpenForGame,
    setPreviewToast,
  });
  const previewDisabledReason = preview.derived.disabledReason;
  const previewAvailableTitle = preview.derived.availableTitle;
  // #4053 — the same force-disabled / disabled-reason resolution the
  // WorkspaceHeader call site below already computes inline, hoisted so
  // `handlePreviewTabClick` (AC2) and the Preview tab's blocked-pane
  // (AC3) read the identical values instead of re-deriving them and
  // risking drift between the header prop and the pane logic.
  const previewForceDisabledResolved =
    preview.derived.forceDisabled ||
    !projectCapabilityAvailable('preview') ||
    (placement === 'local' && !localReadiness.previewAvailable);
  const previewDisabledReasonResolved =
    placement === 'local' && !localReadiness.previewAvailable
      ? 'Node.js and pnpm must be ready before starting a Local preview.'
      : (projectCapability('preview')?.reason ?? previewDisabledReason);

  // #4053 [AC2] — the Preview tab's click handler. Branches on the SAME
  // `disabled` computation as the tab's own visual state (derived via
  // `derivePreviewLaunchPillState`) BEFORE deciding what to do:
  //   - blocked (no game / phase-gated / force-disabled / dev-server
  //     still starting) -> only switch to the preview workspace mode so
  //     the blocked-pane content (AC3) can explain why. Do NOT call
  //     `preview.handlers.launch()` here — per plan §B.1e, `PreviewModule`
  //     deliberately SUPPRESSES the phase-gate error (built for a
  //     race-condition case, not a deliberate blocked click), so calling
  //     `launch()` while blocked would land the user on a silently blank
  //     pane instead of the blocked-pane message.
  //   - ready -> call `preview.handlers.launch()`, exactly what the
  //     legacy icon-only button's `onPreview` does today.
  const handlePreviewTabClick = useCallback(() => {
    const pill = derivePreviewLaunchPillState({
      gameId,
      previewLoading: preview.derived.launchButtonLoading,
      previewForceDisabled: previewForceDisabledResolved,
      previewDisabledReason: previewDisabledReasonResolved,
      previewAvailableTitle,
      previewPhaseGate,
    });
    if (pill.disabled) {
      setWorkspaceMode('preview');
      return;
    }
    preview.handlers.launch();
  }, [
    gameId,
    preview.derived.launchButtonLoading,
    preview.handlers.launch,
    previewForceDisabledResolved,
    previewDisabledReasonResolved,
    previewAvailableTitle,
    previewPhaseGate,
    setWorkspaceMode,
  ]);

  // #4053 [AC3] — which blocked sub-state (if any) the Preview pane should
  // show instead of `activePreviewSurfaceEl`. See `derivePreviewPaneBlockedState`'s
  // doc — deliberately excludes `previewLoading` so a launch already in
  // flight still shows the real `PreviewModule` surface, not a static
  // blocked message.
  const previewPaneBlockedState = derivePreviewPaneBlockedState({
    gameId,
    previewPhaseGate,
    previewForceDisabled: previewForceDisabledResolved,
  });
  const previewPaneCopy = previewPaneBlockedState
    ? previewBlockedPaneCopy(previewPaneBlockedState, {
        previewPhaseGate,
        previewDisabledReason: previewDisabledReasonResolved,
      })
    : null;

  // Three-zone build view (new UI). While a build is actually running
  // we swap the standard chat-centred layout for: embedded live preview
  // in the main area, a pinned sub-agent activity panel on the right,
  // and the chat (with input) docked along the bottom. Gated on
  // `flags.newUi` and active Phase B work per the run-state machine.
  // A committed plan is only a kickoff handoff; until Phase B actually
  // starts, keep the chat/plan layout instead of opening an empty or
  // stale preview.
  const buildView = flags.newUi && showNewUiBuildViewFor(workspaceRunState.kind);
  // Auto-start of the live preview lives in `usePreviewModule` (design doc
  // §4 PR A) — the brain owns the attempt-bounded auto-start map. The
  // preview surface itself only opens manually, via the header "Develop" /
  // "Preview" tabs; there is no auto-open effect. See `preview/usePreviewModule.ts`.
  const previewModeVisible = flags.newUi && workspaceMode === 'preview';
  const protoHubModeVisible = workspaceMode === 'protohub';
  // crucible#2007 (D-77) — consumer tier hides Design + Art (prosumer-only
  // surfaces); default 'prosumer' when unset, so this is a no-op today.
  const isProsumerTier = workspaceConfig?.tier !== 'consumer';
  // Art tab (crucible#2611 Task 4) — gated the same shape as Craft
  // (flag + newUi + a loaded game), but a real WorkspaceMode (a tab you
  // navigate to and away from) rather than a toggled viewer overlay.
  const artModeVisible =
    flags.loaded &&
    flags.artTab &&
    flags.newUi &&
    !!gameId &&
    projectCapabilityAvailable('art') &&
    isProsumerTier &&
    workspaceMode === 'art';
  const designWorkspaceEnabled =
    flags.newUi &&
    flags.designTab &&
    clientCapabilities.hasLocalFilesystem &&
    projectCapabilityAvailable('design') &&
    !!gameId &&
    isProsumerTier &&
    workspaceConfig?.designTabEnabled !== false;
  const planModeVisible = flags.newUi
    ? !designWorkspaceEnabled && workspaceMode === 'plan'
    : showPlanModeView;
  // Design is a new-UI workspace surface gated by the process-level
  // CRUCIBLE_DESIGN_TAB hard-disable plus the user-facing GUI opt-out. Do not
  // read `gameStatus.designMode` here; that field remains only the legacy
  // Advanced/skip-interview marker.
  const designContentVisible = designWorkspaceEnabled && workspaceMode === 'design';
  // crucible#2990 — chat is the centre column's FALL-THROUGH surface, not a
  // mode-equality check. When `workspaceMode` names a surface whose gate is
  // off (e.g. 'art' without the Art flag), the old `workspaceMode === 'chat'`
  // gate rendered a silent blank centre column: no tabs, no composer, no
  // error. Chat now renders whenever no dedicated surface claims the mode
  // and plan mode isn't showing.
  const chatModeVisible = centreChatSurfaceVisibleFor({
    newUi: flags.newUi,
    showPlanModeView,
    planModeVisible,
    designContentVisible,
    previewModeVisible,
    protoHubModeVisible,
    artModeVisible,
  });
  // crucible#3617 — generated Guided Interview plans use the same editable
  // Design-tab approval boundary as every other plan-driven build.
  const planHandoffVisible = planHandoffVisibleFor({ showPlanModeView });
  // Live-preview split renders only in chat mode (the Preview tab keeps
  // its full-bleed layout — the two surfaces are deliberately separate).
  const livePreviewSplitActive =
    flags.newUi && flags.livePreview && livePreviewSplitOpen && chatModeVisible;

  // Develop (side-by-side) hides the right side panel BY DEFAULT — the
  // preview owns that space — but it stays reopenable via the header
  // panel toggle. Snapshot the pre-Develop rail state on entry and restore
  // it on exit; while Develop is active, don't fight a manual reopen.
  const preDevelopRightPanelOpenRef = useRef<boolean | null>(null);
  useEffect(() => {
    if (livePreviewSplitActive) {
      if (preDevelopRightPanelOpenRef.current === null) {
        preDevelopRightPanelOpenRef.current = rightPanelOpen;
        if (rightPanelOpen) setRightPanelOpen(false);
      }
    } else if (preDevelopRightPanelOpenRef.current !== null) {
      setRightPanelOpen(preDevelopRightPanelOpenRef.current);
      preDevelopRightPanelOpenRef.current = null;
    }
  }, [livePreviewSplitActive, rightPanelOpen]);

  useEffect(() => {
    if (!flags.newUi || !gameId) return;
    if (initialisedModeForGameRef.current === gameId) return;
    initialisedModeForGameRef.current = gameId;
    // crucible#2990 — two guards against initialising a game into a surface
    // with no chat: (1) `previewSessionReadyForGame` ignores a ready session
    // that belongs to the PREVIOUS game (the Workspace persists across
    // in-place game switches, so the raw `status === 'ready'` check bled
    // cross-game); (2) with live-preview ON, a genuinely-ready session lands
    // on chat with the split docked open instead of the full-bleed Preview
    // tab, whose header has no Chat/Build pill to escape with.
    // Codex R5: gate the ENTRY plan-routing on the SAME eligibility the forward
    // gate uses, so entering during an active auto-build (which may have seeded
    // the plan doc) lands on chat/build, not stranded on the plan.
    const surface = initialWorkspaceSurfaceForEntry({
      planHandoffVisible,
      runStateKind: workspaceRunState.kind,
      autoBuildActive: isActiveAutoBuild(autoBuildState),
      buildView,
      previewReady: previewSessionReadyForGame(preview.state, gameId),
      planReadyMode: designWorkspaceEnabled ? 'design' : 'plan',
      // Adaptive Design begins in chat so the interview stays visible. The
      // done/skip handler performs the one-way handoff to Design.
      createHandoffMode: resolveCreateHandoffModeForWorkspaceEntry({
        designWorkspaceEnabled,
        intakeEnabled: flags.intake,
        createHandoffMode,
        deferredDesignHandoff,
      }),
      livePreviewEnabled: flags.livePreview,
    });
    setWorkspaceMode(surface.mode);
    if (surface.openLivePreviewSplit) setLivePreviewSplitOpenForGame(true);
  }, [
    autoBuildState,
    buildView,
    createHandoffMode,
    deferredDesignHandoff,
    designWorkspaceEnabled,
    flags.intake,
    flags.livePreview,
    flags.newUi,
    gameId,
    planHandoffVisible,
    preview.state,
    setLivePreviewSplitOpenForGame,
    workspaceRunState.kind,
  ]);

  // #3038 plan-review navigation (forward gate + return leg). The wiring — the
  // two effects, the two refs, their dependency arrays, and the order they run
  // in — lives in usePlanReviewNavigation so it is a single named unit the
  // integration test exercises directly; the pure decisions live in
  // plan-build-handoff.ts.
  usePlanReviewNavigation({
    gameId,
    newUiEnabled: flags.newUi,
    designWorkspaceEnabled,
    planHandoffVisible,
    runStateKind: workspaceRunState.kind,
    // Codex R3 / Bugbot: only an IN-FLIGHT auto-build vetoes navigation; a
    // terminal one (done/cancelled/failed) that returned to plan-review must not.
    autoBuildArmed: isActiveAutoBuild(autoBuildState),
    workspaceMode,
    setWorkspaceMode,
    bumpBuildPlanFocusNonce: () => setDesignBuildPlanFocusNonce((n) => n + 1),
  });

  useEffect(() => {
    if (workspaceMode !== 'design' || designWorkspaceEnabled) return;
    setWorkspaceMode(planHandoffVisible ? 'plan' : 'chat');
  }, [designWorkspaceEnabled, planHandoffVisible, workspaceMode]);

  useEffect(() => {
    if (!flags.newUi || !designWorkspaceEnabled || workspaceMode !== 'plan') return;
    if (planHandoffVisible) {
      setDesignBuildPlanFocusNonce((n) => n + 1);
      setWorkspaceMode('design');
    } else {
      setWorkspaceMode('chat');
    }
  }, [designWorkspaceEnabled, flags.newUi, planHandoffVisible, workspaceMode]);

  // Codex round-4 HIGH-3: capture refetch via a ref so the
  // plan-updated subscription + 3s poll effects don't tear down on
  // every render. The React Query hook returns a fresh object on
  // every render (new identity even when the underlying state is
  // unchanged), so naively listing `interviewStateQuery` in an
  // effect's deps means the subscription rebinds and the interval
  // restarts on every chunk/state change. With heavy chat-stream
  // traffic the interval can keep getting cleared before its 3s
  // tick — missing the committed → build transition entirely. The
  // ref pattern stabilises the call site without losing freshness.
  const refetchInterviewStateRef = useRef(interviewStateQuery.refetch);
  refetchInterviewStateRef.current = interviewStateQuery.refetch;

  // Subscribe to `interview:plan-updated` pushes so the renderer
  // re-reads the state file the moment the SKILL writes a new plan
  // revision. The push is the fast path — main spins up an fs.watch
  // on the state file lazily on first read, so subsequent SKILL
  // writes get autonomous notifications.
  useEffect(() => {
    if (!gameId) return;
    const unsub = client.subscribe('interview:plan-updated', (payload) => {
      if (payload.gameId !== gameId) return;
      refetchInterviewStateRef.current();
    });
    return unsub;
  }, [gameId]);

  // #553 belt-and-braces poller — see `hooks/useInterviewStatePoll`
  // for the full failure-mode + design prose. Mirrors the existing
  // `useInterviewStatus` refetch-interval policy onto the state
  // query, gated by `shouldPollInterviewState` (status-driven, not
  // depth-driven) so the SKILL's first quick-batch plan write is
  // observed even when the renderer's first read landed on a
  // pre-quick state file.
  //
  // Feed the gate off the STATE query's own status field (not the
  // separate `useInterviewStatus` summary). Two reasons:
  //   (1) Same query we're polling — no extra dependency / cross-query
  //       coupling that could rebind the effect on summary-only refetches.
  //   (2) Wider type — the state file persists the full
  //       `PersistedBuildGameInterviewStatus` (forward-compat for any
  //       future SKILL terminal), whereas the summary narrows to the
  //       closed `BuildGameInterviewStatus` literal. The poll predicate
  //       wants to keep polling on UNKNOWN statuses too, so reading the
  //       wide field is the safer plumbing.
  useInterviewStatePoll({
    gameId,
    status: interviewStateQuery.data?.status,
    refetch: interviewStateQuery.refetch,
  });

  const startOverInterview = useStartOverInterview();

  const sendStartBuildingKickoff = useCallback(
    async (source: 'user' | 'auto-resume', seededPlan?: DesignBuildPlanSeed): Promise<boolean> => {
      if (!gameId || planModeBusy) return false;
      const localBuildReason = localBuildUnavailableReason(placement, localReadiness);
      if (localBuildReason) {
        showPlanModeError(localBuildReason);
        return false;
      }
      setPlanModeBusy(true);
      // PR #544 follow-up: clear any stale error before the new attempt
      // so a successful retry doesn't leave the previous failure's toast
      // hanging until its 6s timer expires.
      setPlanModeError(null);
      try {
        const guidedReview = interviewStateQuery.data?.guidedPlanReview;
        let invokeResult: InterviewSentinelResult | null = null;
        let invokeError: unknown;
        let guidedApproval:
          | {
              approvalId: string;
              expectedHandoffId: string;
              expectedPlanRevision: number;
              expectedPlanHash: number;
            }
          | undefined;
        if (guidedReview) {
          const planKey = `${guidedReview.handoffId}:${guidedReview.planRevision}:${guidedReview.planHash}`;
          const durableDispatch = interviewStateQuery.data?.buildDispatch;
          if (guidedApprovalRef.current?.planKey !== planKey) {
            guidedApprovalRef.current = {
              planKey,
              approvalId:
                durableDispatch?.reviewedPlanRevision === guidedReview.planRevision &&
                durableDispatch.reviewedHandoffId === guidedReview.handoffId &&
                durableDispatch.reviewedPlanHash === guidedReview.planHash
                  ? durableDispatch.approvalId
                  : globalThis.crypto.randomUUID(),
            };
          }
          guidedApproval = {
            approvalId: guidedApprovalRef.current.approvalId,
            expectedHandoffId: guidedReview.handoffId,
            expectedPlanRevision: guidedReview.planRevision,
            expectedPlanHash: guidedReview.planHash,
          };
        }
        const invoke = async (): Promise<void> => {
          try {
            invokeResult = await client.invoke('interview:start-building', {
              gameId,
              ...(seededPlan?.planRevision !== undefined
                ? { planRevision: seededPlan.planRevision }
                : interviewStateQuery.data?.planRevision !== undefined
                  ? { planRevision: interviewStateQuery.data.planRevision }
                  : {}),
              ...guidedApproval,
            });
          } catch (error) {
            invokeError = error;
            throw error;
          }
        };
        const outcome = guidedReview
          ? await runInterviewSentinelOutcome({
              invoke,
              expectedHandoffId: guidedApproval!.expectedHandoffId,
              guidedReview,
              readDurableDispatch: async () =>
                (await client.invoke('build-game:get-interview-state', { gameId }))
                  ?.buildDispatch ?? null,
            })
          : (await invoke(), 'started' as const);
        const result = invokeResult as InterviewSentinelResult | null;
        if (outcome === 'failed' || (outcome !== 'already-started' && !result?.ok)) {
          if (result?.reason === 'stale-plan') {
            refetchInterviewStateRef.current();
          }
          const errorMessage = result
            ? formatInterviewSentinelError({ kind: 'result', result }, 'starting the build')
            : formatInterviewSentinelError(
                { kind: 'rejection', error: unflattenIpcError(invokeError) },
                'starting the build',
              );
          if (errorMessage) showPlanModeError(errorMessage);
          return false;
        }
        // The accepted kickoff is already the recovery attempt for the
        // currently stale implementation phase. Claim its phase key while
        // planModeBusy is still true so the generic auto-resume effect cannot
        // enqueue a second chat turn before agent liveness reaches the renderer.
        markStalledBuildResumeAttempted(
          stalledBuildResumeAttemptedRef.current,
          gameId,
          phaseProgress,
        );
        const now = Date.now();
        chatDispatch({
          type: 'chat/local-event',
          id: `build-started-${source}-${gameId}-${now}`,
          content:
            source === 'auto-resume'
              ? 'Build kickoff retry sent. Returning to Build.'
              : 'Build kickoff sent. Returning to Build.',
          timestamp: now,
        });
        setChatTab('chat');
        setWorkspaceMode('chat');
        void queryClient.invalidateQueries({
          queryKey: projectQueryKey(client, ['build-game:interview-status', gameId]),
        });
        void queryClient.invalidateQueries({
          queryKey: projectQueryKey(client, ['build-game:get-interview-state', gameId]),
        });
        // Refetch immediately so the renderer picks up the SKILL's
        // subPhase transition without waiting on the heartbeat.
        refetchInterviewStateRef.current();
        return true;
      } catch (err) {
        // Catch IPC rejections (main throws / preload bridge errors / etc.).
        // Without this, the rejection became an unhandled promise rejection
        // and the user had no idea the click did nothing.
        //
        // Bugbot MEDIUM (#544): the preload bridge flattens errors via
        // `flattenIpcError` on the main side; the raw `err` here carries
        // the wire-flattened shape. `unflattenIpcError` restores the
        // original Error semantics (CrucibleError reason chain, cause,
        // structured payload) so the user-facing message is accurate
        // rather than the raw flattened shape's `.message`.
        const unflattened = unflattenIpcError(err);
        const errorMessage = formatInterviewSentinelError(
          { kind: 'rejection', error: unflattened },
          'starting the build',
        );
        if (errorMessage) showPlanModeError(errorMessage);
        return false;
      } finally {
        setPlanModeBusy(false);
      }
    },
    [
      chatDispatch,
      client,
      gameId,
      interviewStateQuery.data?.planRevision,
      interviewStateQuery.data?.guidedPlanReview,
      interviewStateQuery.data?.buildDispatch,
      localReadiness.agentAvailable,
      localReadiness.toolchainAvailable,
      placement,
      phaseProgress,
      planModeBusy,
      queryClient,
      showPlanModeError,
    ],
  );

  const handleStartBuilding = useCallback(() => {
    void sendStartBuildingKickoff('user');
  }, [sendStartBuildingKickoff]);

  const handleResumeBuildKickoff = useCallback(() => {
    void sendStartBuildingKickoff('auto-resume');
  }, [sendStartBuildingKickoff]);

  const refreshInterviewRecoveryState = useCallback((): void => {
    if (!gameId) return;
    void queryClient.invalidateQueries({
      queryKey: projectQueryKey(client, ['build-game:interview-status', gameId]),
    });
    void queryClient.invalidateQueries({
      queryKey: projectQueryKey(client, ['build-game:get-interview-state', gameId]),
    });
  }, [client, gameId, queryClient]);

  const recoverInitialInterview = useCallback(
    (
      trigger: 'workspace-open' | 'manual-retry',
      signal?: AbortSignal,
    ): Promise<InterviewRecoverResult> => {
      if (!gameId) return Promise.reject(new Error('Project is unavailable'));
      const currentRecovery = interviewRecoveryInFlightRef.current;
      if (currentRecovery) return currentRecovery;

      const recovery = recoverInterviewWithBackoff({
        signal,
        recover: () => client.invoke('interview:recover', { gameId, trigger }),
        shouldRetry: (error) => !isCloudRuntimeUpdateRequired(error),
      }).finally(() => {
        if (interviewRecoveryInFlightRef.current === recovery) {
          interviewRecoveryInFlightRef.current = null;
        }
      });
      interviewRecoveryInFlightRef.current = recovery;
      return recovery;
    },
    [client, gameId],
  );

  const handleRetryInitialInterview = useCallback(() => {
    if (!gameId) {
      setPreviewToast({
        tone: 'error',
        message: 'This project is not available, so the interview cannot be retried.',
      });
      return;
    }
    setChatTab('chat');
    setWorkspaceMode('chat');
    const retryGameId = gameId;
    void recoverInitialInterview('manual-retry')
      .then((result) => {
        if (gameIdRef.current !== retryGameId) return;
        // This recovers the legacy interview agent, not adaptive intake. Keep
        // negative adaptive authority latched until intake itself proves that
        // a session, checkpoint, or durable plan exists.
        refreshInterviewRecoveryState();
        setPreviewToast(interviewRetryToastMessage(result));
      })
      .catch((error: unknown) => {
        if (gameIdRef.current !== retryGameId) return;
        setPreviewToast({
          tone: 'error',
          message: formatInterviewRecoveryError(error, 'Interview still needs attention'),
        });
      });
  }, [gameId, recoverInitialInterview, refreshInterviewRecoveryState]);

  const handleRetryAutoBuild = useCallback(() => {
    if (!gameId) return;
    void client
      .invoke('build:autoPipeline:retry', { gameId })
      .then((result) => {
        const errorMessage = formatInterviewSentinelError(
          { kind: 'result', result },
          'rebuilding from the plan',
        );
        if (!result.ok) {
          if (errorMessage) showPlanModeError(errorMessage);
          return;
        }
        void queryClient.invalidateQueries({
          queryKey: projectQueryKey(client, ['build-game:interview-status', gameId]),
        });
        void queryClient.invalidateQueries({
          queryKey: projectQueryKey(client, ['build-game:get-interview-state', gameId]),
        });
      })
      .catch((err: unknown) => {
        const unflattened = unflattenIpcError(err);
        const errorMessage = formatInterviewSentinelError(
          { kind: 'rejection', error: unflattened },
          'rebuilding from the plan',
        );
        if (errorMessage) showPlanModeError(errorMessage);
      });
  }, [gameId, queryClient, showPlanModeError]);

  const handleCancelAutoBuild = useCallback(() => {
    if (!gameId) return;
    void client
      .invoke('build:autoPipeline:cancel', { gameId })
      .then((result) => {
        if (!result.ok) return;
        const now = Date.now();
        chatDispatch({
          type: 'chat/local-event',
          id: `auto-build-cancelled-${gameId}-${now}`,
          content: 'Build setup cancelled.',
          timestamp: now,
        });
        void queryClient.invalidateQueries({
          queryKey: projectQueryKey(client, ['build-game:interview-status', gameId]),
        });
        void queryClient.invalidateQueries({
          queryKey: projectQueryKey(client, ['build-game:get-interview-state', gameId]),
        });
      })
      .catch((err: unknown) => {
        const unflattened = unflattenIpcError(err);
        showPlanModeError(`Could not cancel build setup: ${unflattened.message}`);
      });
  }, [chatDispatch, gameId, queryClient, showPlanModeError]);

  const sendStalledBuildResume = useCallback(() => {
    setChatTab('chat');
    setWorkspaceMode('chat');
    const hasPlan = typeof interviewPlan === 'string' && interviewPlan.trim().length > 0;
    chatDispatch({
      type: 'user/send-text',
      text: buildStalledBuildResumePrompt(phaseProgress, hasPlan),
    });
  }, [chatDispatch, interviewPlan, phaseProgress]);

  useEffect(() => {
    if (resumeBuildActionKind !== 'resume-stalled-build') return;
    if (isAiTyping || planModeBusy) return;
    if (chatInput.trim().length > 0 || pendingAttachments.length > 0) return;
    const attemptKey = stalledBuildResumeAttemptKey(gameId, phaseProgress);
    if (!attemptKey || stalledBuildResumeAttemptedRef.current.has(attemptKey)) return;

    markStalledBuildResumeAttempted(stalledBuildResumeAttemptedRef.current, gameId, phaseProgress);
    sendStalledBuildResume();
  }, [
    chatInput,
    gameId,
    isAiTyping,
    pendingAttachments.length,
    phaseProgress,
    planModeBusy,
    resumeBuildActionKind,
    sendStalledBuildResume,
  ]);

  useEffect(() => {
    if (!buildKickoffKey) return;
    if (buildKickoffWatchdog?.status !== 'stalled') return;
    if (isAiTyping || planModeBusy) return;
    if (buildKickoffResumeAttemptedRef.current.has(buildKickoffKey)) return;
    buildKickoffResumeAttemptedRef.current.add(buildKickoffKey);
    setBuildKickoffClockNow(Date.now());
    void sendStartBuildingKickoff('auto-resume');
  }, [
    buildKickoffKey,
    buildKickoffWatchdog?.status,
    isAiTyping,
    planModeBusy,
    sendStartBuildingKickoff,
  ]);

  const handleSavePlan = useCallback(
    async (plan: string, expectedPlan?: PlanSaveWitness) => {
      if (!gameId) throw new Error('No game is open.');
      setPlanModeBusy(true);
      setPlanModeError(null);
      try {
        const result = await client.invoke('interview:save-plan', {
          gameId,
          plan,
          source: 'manual',
          ...(expectedPlan ?? {}),
        });
        if (!result.ok) {
          if (result.reason === 'conflict') {
            refetchInterviewStateRef.current();
            throw new Error(
              'The plan changed while you were editing it. Your draft is still here. Copy it if needed, then cancel editing to load the latest plan before applying your changes again.',
            );
          }
          throw new Error(`Couldn't save the plan (${result.reason ?? 'unknown error'}).`);
        }
        refetchInterviewStateRef.current();
      } catch (err) {
        const unflattened = unflattenIpcError(err);
        showPlanModeError(unflattened.message || 'Could not save the plan.');
        throw unflattened;
      } finally {
        setPlanModeBusy(false);
      }
    },
    [gameId, showPlanModeError],
  );

  const handleRebuildFromPlan = useCallback(async () => {
    if (!gameId || planModeBusy) return;
    setPlanModeBusy(true);
    setPlanModeError(null);
    try {
      const result = await client.invoke('interview:rebuild-from-plan', {
        gameId,
        ...(interviewStateQuery.data?.planRevision !== undefined
          ? { planRevision: interviewStateQuery.data.planRevision }
          : {}),
      });
      const errorMessage = formatInterviewSentinelError(
        { kind: 'result', result },
        'rebuilding from the plan',
      );
      if (!result.ok) {
        if (errorMessage) showPlanModeError(errorMessage);
        return;
      }
      const now = Date.now();
      chatDispatch({
        type: 'chat/local-event',
        id: `rebuild-started-${gameId}-${now}`,
        content: 'Rebuild kickoff sent. Returning to Build.',
        timestamp: now,
      });
      setWorkspaceMode('chat');
      refetchInterviewStateRef.current();
    } catch (err) {
      const unflattened = unflattenIpcError(err);
      const errorMessage = formatInterviewSentinelError(
        { kind: 'rejection', error: unflattened },
        'rebuilding from the plan',
      );
      if (errorMessage) showPlanModeError(errorMessage);
    } finally {
      setPlanModeBusy(false);
    }
  }, [
    chatDispatch,
    gameId,
    interviewStateQuery.data?.planRevision,
    planModeBusy,
    showPlanModeError,
  ]);

  // PR #3237 sweep sibling of the deploy double-publish finding:
  // synchronous in-flight latch for the async back-to-plan confirm
  // window. `planModeBusy` only disables the header button when React
  // commits, so between a confirm activation and that commit a second
  // activation (double-click / Enter repeat on the CTA — also latched
  // component-side in BackToPlanConfirmModal — or a header click
  // re-opening + re-confirming the modal) would double-invoke
  // `interview:back-to-plan`. Set before any await, released in
  // finally once the invoke settles.
  const backToPlanInFlightRef = useRef(false);
  const handleBackToPlanRequest = useCallback(() => {
    if (backToPlanInFlightRef.current) return;
    setBackToPlanModalOpen(true);
  }, []);

  const handleBackToPlanConfirm = useCallback(async () => {
    if (!gameId) return;
    // Synchronous re-entrancy latch — see backToPlanInFlightRef above.
    if (backToPlanInFlightRef.current) return;
    backToPlanInFlightRef.current = true;
    setBackToPlanModalOpen(false);
    setPlanModeBusy(true);
    setPlanModeError(null);
    try {
      const result = await client.invoke('interview:back-to-plan', { gameId });
      const errorMessage = formatInterviewSentinelError(
        { kind: 'result', result },
        'going back to plan',
      );
      if (!result.ok) {
        if (errorMessage) showPlanModeError(errorMessage);
        return;
      }
      if (designWorkspaceEnabled) {
        setDesignBuildPlanFocusNonce((n) => n + 1);
        setWorkspaceMode('design');
      } else if (flags.newUi) {
        setWorkspaceMode('plan');
      }
      refetchInterviewStateRef.current();
    } catch (err) {
      // See handleStartBuilding for the unflattenIpcError rationale.
      const unflattened = unflattenIpcError(err);
      const errorMessage = formatInterviewSentinelError(
        { kind: 'rejection', error: unflattened },
        'going back to plan',
      );
      if (errorMessage) showPlanModeError(errorMessage);
    } finally {
      setPlanModeBusy(false);
      // Release the synchronous latch once the invoke settles — by now
      // planModeBusy has committed for the in-flight window, and a NEW
      // pause from here is legitimate.
      backToPlanInFlightRef.current = false;
    }
  }, [client, designWorkspaceEnabled, flags.newUi, gameId, showPlanModeError]);

  const handleBackToPlanCancel = useCallback(() => {
    setBackToPlanModalOpen(false);
  }, []);

  const handlePlanRefinement = useCallback(
    (text: string) => {
      // Bugbot MEDIUM (#544 round-2): the UI-level disable on the
      // refinement input can be bypassed by a rapid double-submit
      // before isAiTyping flips. Guard at dispatch with a fresh
      // getChatState() read; the UI disable handles the cosmetic
      // side, this catches the race.
      if (getChatState().isAiTyping) {
        showPlanModeError(
          'Plan refinement is already in flight — wait for the current turn to finish.',
        );
        return;
      }
      // Refinements travel through the normal chat path — the SKILL
      // observes `subPhase === 'plan'` and treats the user message as
      // a plan-refinement turn (rewriting the top-level `plan` markdown
      // per the canonical schema in #543).
      chatDispatch({ type: 'user/type', text });
      chatDispatch({ type: 'user/send' });
    },
    [chatDispatch, getChatState, showPlanModeError],
  );

  // Design → build UPDATES go to Build (not Plan): switch to the Build tab and
  // auto-send the distilled edit list to the agent so it applies the changes
  // to the already-built game. Fired by the build-handoff dialog when the user
  // accepts an update (full first builds still route to Plan via onSentToBuild).
  const handleSendDesignUpdate = useCallback(
    (message: string) => {
      if (!message.trim()) return;
      setWorkspaceMode('chat');
      chatDispatch({ type: 'user/type', text: message });
      chatDispatch({ type: 'user/send' });
    },
    [chatDispatch],
  );

  const handlePlanStartOver = useCallback(() => {
    if (!gameId) return;
    // Bugbot MEDIUM (#544): void-discarded promise swallows IPC
    // rejections. Surface them through the same toast affordance as
    // start-building / back-to-plan so the user knows the click did
    // nothing.
    startOverInterview.startOver(gameId).catch((err: unknown) => {
      const unflattened = unflattenIpcError(err);
      const message = unflattened instanceof Error ? unflattened.message : String(unflattened);
      showPlanModeError(`Couldn't start over: ${message}`);
    });
  }, [gameId, startOverInterview, showPlanModeError]);

  // Credential readiness — drives whether the AI Chat panel renders. Chat
  // is backed by the Anthropic SDK; without a key the placeholder card
  // takes its place pointing the user at Settings (audit §4). We
  // distinguish "still loading" from "loaded with no key" so users
  // who do have a key never briefly see the "needs setup" copy
  // (Codex review: tokens?.anthropic === true is false for undefined,
  // which causes a placeholder flicker before the IPC resolves).
  //
  // Treat "errored" as resolved — Macroscope flagged that on the
  // keychain-unavailable path the query rejects and `tokens` stays
  // undefined forever; without this the spinner would render indefinitely.
  // Local workspaces then rely on verified readiness, while Cloud workspaces
  // land on the placeholder because Settings owns the keychain error detail.
  const {
    data: tokens,
    isLoading: tokensLoading,
    error: tokensError,
  } = useIpcQuery('tokens:get', undefined);
  const hasAnthropicKey =
    placement === 'local' ? localReadiness.agentAvailable : tokens?.anthropic === true;
  const tokensResolved =
    !tokensLoading &&
    (tokens !== undefined || tokensError !== null) &&
    (placement === 'cloud' || localReadiness.state !== 'checking');

  const gameName = gameStatus?.displayName ?? gameId ?? 'Unknown Game';
  const handleDesignStartBuild = useCallback(
    (seededPlan?: DesignBuildPlanSeed): Promise<void> =>
      runDesignStartBuild({ sendStartBuildingKickoff, seededPlan }),
    [sendStartBuildingKickoff],
  );
  const designBuildPlan = useMemo(
    () => ({
      gameName,
      focusNonce: designBuildPlanFocusNonce,
      plan: interviewPlan,
      answeredCount: interviewStatusQuery.data?.answeredCount,
      totalCount: interviewStatusQuery.data?.totalCount,
      planRevision: interviewState?.planRevision,
      planHash: interviewState?.planHash,
      planUpdatedAt: interviewState?.planUpdatedAt,
      planSource: interviewState?.planSource,
      guidedPlanReview: interviewStateQuery.data?.guidedPlanReview !== undefined,
      lastBuiltPlanRevision: interviewState?.lastBuiltPlanRevision,
      lastBuiltPlanHash: interviewState?.lastBuiltPlanHash,
      buildCompletionUnverified,
      buildRequestPending: workspaceRunState.planRevisionLocked,
      busy: isAiTyping || planModeBusy,
      draftPlanPending: designBuildPlanDraftPending(workspaceRunState.kind),
      onSavePlan: handleSavePlan,
      onStartBuilding: handleDesignStartBuild,
      onRebuildFromPlan: handleRebuildFromPlan,
      onBackToChat: () => setWorkspaceMode('chat'),
      onSubmitRefinement: handlePlanRefinement,
      onStartOver: handlePlanStartOver,
    }),
    [
      designBuildPlanFocusNonce,
      gameName,
      handleDesignStartBuild,
      handlePlanRefinement,
      handlePlanStartOver,
      handleRebuildFromPlan,
      handleSavePlan,
      interviewPlan,
      interviewState?.lastBuiltPlanHash,
      interviewState?.lastBuiltPlanRevision,
      interviewState?.planHash,
      interviewState?.planRevision,
      interviewState?.planSource,
      interviewStateQuery.data?.guidedPlanReview,
      interviewState?.planUpdatedAt,
      interviewStatusQuery.data?.answeredCount,
      interviewStatusQuery.data?.totalCount,
      isAiTyping,
      buildCompletionUnverified,
      planModeBusy,
      workspaceRunState.kind,
      workspaceRunState.planRevisionLocked,
    ],
  );
  const visibleDeployFailureContext =
    deployFailureCardEnabled && deployFailureContext?.status === 'failure'
      ? deployFailureContext
      : undefined;
  // #3132 — build-readiness verdict for the Deploy button. The RAW
  // persisted status is authoritative whenever present — INCLUDING an
  // unrecognised future SKILL terminal, which the readiness switch
  // fails OPEN on. This DELIBERATELY deviates from run-state's
  // `interviewSnapshot` resolution (raw-if-known, else summary):
  // the summary poll FOLDS unknown statuses to 'in-progress', so
  // falling back to it while a raw status exists would launder that
  // fold into "never built" and permanently disable Deploy on a
  // future-terminal game — the #2981 class (Bugbot round 3). See
  // resolveDeployReadinessStatus for the full contract.
  // Both clocks schedule the relevant 30s re-render; sample wall time here so
  // Phase-B progress and a pre-Phase-B commit handoff age on every such render.
  const deployNowMs = Date.now();
  const deployBuildReadiness = deriveDeployBuildReadiness(
    resolveDeployReadinessStatus(interviewState?.status, interviewStatus),
    hasActiveLegacyDeployRebuild({
      runStateKind: workspaceRunState.kind,
      autoBuildStates: legacyDeployAutoBuildStates,
      directBuildLiveness: hasDirectLegacyDeployBuildLiveness({
        hasFreshPhaseBProgress: hasFreshPhaseBProgressRecord(phaseProgress, deployNowMs),
        initialKickoffActive: isActiveInitialKickoffStatus(initialKickoff.status),
        canonicalBuildActive:
          foundryGameState?.build.status === 'running' && foundryGameState.build.terminal === false,
      }),
      nowMs: deployNowMs,
    }),
  );
  // #3494 — the checked-out git branch feeds the readiness classifier's
  // branch-mismatch precondition (committed work not on the deploy
  // branch). Undefined when there's no local git / status yet, which
  // the classifier treats as fail-open (never blocks).
  const deployCurrentBranch = gameStatus?.localGit?.branch;
  const deployActionState = useMemo(
    () =>
      placement === 'local' && !localReadiness.publishAvailable
        ? {
            kind: 'blocked' as const,
            label: 'Deploy unavailable',
            tone: 'neutral' as const,
            badgeClassName: 'bg-white/10 text-white/50',
            deployButtonLabel: 'Deploy to TV',
            deployButtonTitle:
              'GitHub, Git, Node.js, and pnpm must be ready before publishing a Local project.',
            deployButtonDisabled: true,
            deployButtonGated: true,
            failureSecondaryCtas: [],
            disabledReason: 'Complete Local setup in Settings before publishing this project.',
            debugSource: 'deploy:local-setup-unavailable',
          }
        : publishBacked
          ? deriveDeployActionState({
              deployState,
              deployFailureContext: visibleDeployFailureContext,
              localFailedCheck,
              ciTriggeredThisDeploy: deployFailureCardEnabled,
              hasAnthropicKey,
              buildReadiness: deployBuildReadiness,
              currentBranch: deployCurrentBranch,
              upToDate: deployUpToDate,
            })
          : {
              kind: 'blocked' as const,
              label: 'Deploy unavailable',
              tone: 'neutral' as const,
              badgeClassName: 'bg-white/10 text-white/50',
              deployButtonLabel: 'Deploy to TV',
              deployButtonTitle: 'Deploy to TV is unavailable in this runtime.',
              deployButtonDisabled: true,
              deployButtonGated: true,
              failureSecondaryCtas: [],
              disabledReason: 'Deploy to TV is unavailable in this runtime.',
              debugSource: 'deploy:capability-unavailable',
            },
    [
      deployBuildReadiness,
      deployCurrentBranch,
      deployUpToDate,
      deployFailureCardEnabled,
      deployState,
      hasAnthropicKey,
      localFailedCheck,
      publishBacked,
      placement,
      localReadiness.publishAvailable,
      visibleDeployFailureContext,
    ],
  );
  const cloneDeployAccessWarning = useMemo<CloneDeployAccessWarning | undefined>(() => {
    if (!clonedRepoFullName) return undefined;
    const access = clonedRepoAccess ?? {
      state: 'unknown' as const,
      repoUrl: `https://github.com/${clonedRepoFullName}`,
      reason: 'Checking source repository access.',
    };
    if (access.state === 'writable') return undefined;
    return {
      repoFullName: clonedRepoFullName,
      ...access,
    };
  }, [clonedRepoAccess, clonedRepoFullName]);

  // Crucible #602 PR #C4 (Issue E) — hydration cursor for the

  // Crucible #602 PR #C4 (Issue E) — inline-checkpoint push subscriber.
  // Adjacent to the `agent:stream` and `interview:*` subscriptions —
  // same `client.subscribe/off` shape. The emitter side
  // (`main/inline-checkpoint-emitter.ts`) writes to disk FIRST and
  // pushes ONLY on a successful append (design §2.1 + §6.3); a stray
  // push for the wrong gameId is also defended against by the reducer's
  // cross-game guard in `chat/state.ts` "chat/checkpoint-received".
  //
  // The flag-gate is at registration time per design §12 Issue E and
  // mirrors `useFeatureFlags`'s contract — the subscription is NOT
  // registered until `loaded && agentTransparency`. A future flag-off
  // window therefore receives ZERO checkpoint pushes (no subscriber
  // exists), which is the FROZEN-wire-compatible way to disable the
  // surface.
  //
  // Pre-hydration buffering — Codex round-1 HIGH #1. If `chat:load`
  // hasn't resolved yet for the current `gameId`, the push is queued
  // into `pendingCheckpointPushesRef` and drained when the load's
  // `.then` dispatches `chat/loaded`. The buffer survives across
  // the brief Frame-1 → Frame-2 window described in design §8.
  useEffect(() => {
    if (!gameId) return;
    if (!featureFlags.loaded || !featureFlags.agentTransparency) return;
    const dispatchOrBuffer = (push: { gameId: string; checkpoint: InlineCheckpoint }): void => {
      if (hydratedForGameIdRef.current === gameId) {
        chatDispatch({
          type: 'chat/checkpoint-received',
          gameId: push.gameId,
          checkpoint: push.checkpoint,
        });
        return;
      }
      // Not hydrated yet — buffer for replay after `chat/loaded`.
      pendingCheckpointPushesRef.current.push(push);
    };
    const unsub = client.subscribe('activity:checkpoint', (push) => {
      // Cross-game guard. The reducer also enforces this on the
      // dispatch path (action drop) but bailing here saves a render
      // tick + a `useChatReducer` `dispatch` round-trip when the same
      // renderer can observe pushes for any registry-active game.
      if (push.gameId !== gameId) return;
      // Defence-in-depth on the wire shape — a malformed push with
      // `checkpoint: undefined` would crash the reducer's
      // `state.messages.some((m) => m.id === action.checkpoint.id)`.
      // The emitter never produces this shape but ipc-bridge bugs and
      // forwarded test fixtures have a habit of slipping bad payloads
      // through; silently dropping is the right move (the disk write
      // is the durable surface anyway).
      if (!push.checkpoint || typeof push.checkpoint.id !== 'string') return;
      dispatchOrBuffer({ gameId: push.gameId, checkpoint: push.checkpoint });
    });
    return unsub;
  }, [gameId, chatDispatch, featureFlags.loaded, featureFlags.agentTransparency]);

  // Issue #2596 — kickoff setup-ready probe (see `useKickoffSetupReady`).
  // While the auto-kickoff is held, poll the #2584 install-state sentinel so
  // the `/build-game` turn waits until the background create-install
  // finishes, then fires exactly once. At the setup→ready transition,
  // refresh the renderer-ready timeout window so a long cold install does
  // not eat the fresh 15s renderer-ready budget.
  const initialKickoffActive = isActiveInitialKickoffStatus(initialKickoff.status);
  const handleSetupCleared = useCallback(() => {
    setInitialKickoff((current) =>
      isActiveInitialKickoffStatus(current.status)
        ? { ...current, startedAt: Date.now() }
        : current,
    );
  }, []);
  useKickoffSetupReady({
    gameId,
    active: initialKickoffActive && localFilesystemEffectsAvailable,
    onReadyChange: setSetupReady,
    onSetupCleared: handleSetupCleared,
  });

  const shouldHoldInitialKickoff = useCallback(
    () =>
      shouldHoldInitialKickoffForRendererReady({
        tokensResolved,
        hasAnthropicKey,
        flagsLoaded: featureFlags.loaded,
        interviewEngineV1: featureFlags.interviewEngineV1,
        interviewRendererReady,
        setupReady,
      }),
    [
      featureFlags.loaded,
      featureFlags.interviewEngineV1,
      hasAnthropicKey,
      interviewRendererReady,
      setupReady,
      tokensResolved,
    ],
  );

  const invokeAgentSendEffect = useCallback(
    (effect: AgentSendEffect, isInitialKickoff: boolean): void => {
      if (isInitialKickoff) {
        setInitialKickoff((current) => ({
          status: 'dispatching',
          startedAt: current.startedAt ?? Date.now(),
        }));
      }
      client
        .invoke('agent:send', {
          gameId: effect.gameId,
          message: effect.message,
          ...(effect.userMessageId ? { userMessageId: effect.userMessageId } : {}),
          assistantMessageId: effect.assistantMessageId ?? effect.assistantMsgId,
          ...(effect.userTimestamp !== undefined ? { userTimestamp: effect.userTimestamp } : {}),
          ...(effect.assistantTimestamp !== undefined
            ? { assistantTimestamp: effect.assistantTimestamp }
            : {}),
          ...(effect.turnId ? { turnId: effect.turnId } : {}),
          ...(effect.initialAutofire ? { initialAutofire: true as const } : {}),
          ...(effect.attachments && effect.attachments.length > 0
            ? { attachments: [...effect.attachments] }
            : {}),
        })
        .then((result) => {
          if (!isInitialKickoff) return;
          if (result.kind === 'not-needed') {
            chatDispatch({
              type: 'ipc/autofire-not-needed',
              assistantMsgId: effect.assistantMsgId,
            });
            setInitialKickoff({ status: 'idle', startedAt: null });
            return;
          }
          setInitialKickoff((current) => {
            if (
              current.status !== 'waiting-for-renderer-ready' &&
              current.status !== 'dispatching'
            ) {
              return current;
            }
            return {
              status: 'dispatched',
              startedAt: current.startedAt ?? null,
            };
          });
        })
        .catch((err: unknown) => {
          // Bridge-3 close-out (Bugbot Medium #1) — pick the scrub
          // level from the LIVE FurnaceStatus snapshot via the ref
          // (so the closure stays stable across status refreshes).
          // The previous hard-coded `'banned-only'` could leak an
          // infrastructure URL through both the kickoff-failure
          // detail and the user-bubble error dispatch when status
          // was still loading at the moment of failure (the
          // `agent:stream` chunk handler already picks
          // `'aggressive'` in the same loading window, so the
          // mismatch was the bug).
          const scrubLevel = pickFurnaceScrubLevel(
            furnaceStatusRef.current,
            gameImportedForFurnaceRef.current,
          );
          if (isInitialKickoff) {
            const failure = classifyKickoffFailure(err, scrubLevel);
            setInitialKickoff((current) => ({
              status: 'failed',
              reason: failure.reason,
              detail: failure.detail,
              startedAt: current.startedAt ?? null,
            }));
          }
          // Codex round-3 — scrub infrastructure URLs from the
          // decoded SerialisedError before dispatch to the chat
          // reducer. The reducer stores the error verbatim on
          // the assistant bubble, where `CrucibleErrorState`
          // renders `.message` AND `.recovery` AND nested
          // `.processOutput` / `.cause` fields. The recursive
          // scrubber visits every user-facing string field so
          // a pre-stream Furnace failure can't leak a hostname
          // through any of them.
          //
          // Bridge-3 close-out (Bugbot Medium #1) — `scrubLevel`
          // now mirrors the `agent:stream` chunk handler's
          // decision (aggressive when status is loading or
          // in-cohort, banned-only when definitively local). The
          // pre-Bridge-3 identity for flag-OFF / out-of-cohort
          // engineers is preserved — `pickFurnaceScrubLevel`
          // returns `'banned-only'` once status has resolved as
          // local, so a local error referencing
          // `http://localhost:3000` stays visible verbatim.
          chatDispatch({
            type: 'ipc/send-failed',
            assistantMsgId: effect.assistantMsgId,
            error: scrubSerialisedError(unflattenIpcError(err), scrubLevel),
          });
          // Bridge-3 close-out (Bugbot Medium, round-2) — also
          // fire the Furnace failure-latch on pre-stream
          // `agent:send` rejections. The chat reducer was
          // already rendering these in the chat bubble (via the
          // dispatch above) but the indicator + banner stayed
          // silent because the latch only fired from the
          // `agent:stream` chunk handler. That left the same
          // three-surface asymmetry Med #2 closed for streaming
          // errors. Pre-stream rejections (e.g. cohort consult
          // refusal, no-cloud-proxy-config) are the highest-
          // signal Furnace failure class — they MUST flip the
          // indicator + banner. Classify the RAW error so the
          // classifier matches against the original substrings
          // (consistent with the stream handler — the classified
          // copy is built from constants, not the raw text, so
          // no infrastructure URL leaks through). The latch is
          // gated through `shouldLatchFurnaceFailure` so
          // out-of-cohort engineers + flag-OFF engineers don't
          // see a Furnace surface for a local rejection.
          if (
            shouldLatchFurnaceFailure({
              furnaceStatus: furnaceStatusRef.current,
            })
          ) {
            const raw = err instanceof Error ? err.message : typeof err === 'string' ? err : '';
            const view = classifyFurnaceError(raw);
            setFurnaceLastFailure(view);
          }
        });
    },
    [chatDispatch],
  );

  useEffect(() => {
    const pending = pendingInitialAgentSendRef.current;
    if (!pending) return;
    if (initialKickoff.status === 'timed-out' || initialKickoff.status === 'failed') {
      pendingInitialAgentSendRef.current = null;
      return;
    }
    if (shouldHoldInitialKickoff()) return;
    pendingInitialAgentSendRef.current = null;
    invokeAgentSendEffect(pending, true);
  }, [initialKickoff.status, shouldHoldInitialKickoff, invokeAgentSendEffect]);

  // Subscribe to agent:stream push events from main process. Every
  // chunk routes through the reducer's `chunk/received` action — the
  // case-by-case handling (append text, upsert tool calls, drain
  // queue on terminal) lives in `chat/state.ts`.
  useEffect(() => {
    const unsub = client.subscribe('agent:stream', (chunk: AgentStreamChunk) => {
      // Multi-game stream-discriminator invariant (Codex round 3 §4 +
      // round 4 §5 of PR #149's plan-doc review). Every chunk carries
      // a sessionId of the form `gui-<gameId>-<timestamp>` where
      // timestamp is a positive integer (Date.now()). The
      // `agent:stream` channel is global so a chunk for game A can
      // technically reach a Workspace mounted on game B.
      //
      // Naive predicates fail in two directions:
      //   - `.includes('-<gameId>-')` false-accepts `gui-foo-bar-…`
      //     on a `foo` Workspace because the substring "-foo-" matches.
      //   - `.startsWith('gui-<gameId>-')` ALSO false-accepts
      //     `gui-foo-bar-…` on a `foo` Workspace because "gui-foo-bar"
      //     starts with "gui-foo-".
      //
      // The structurally correct check is "the bit between `gui-` and
      // the trailing `-<digits>` is exactly `<gameId>`". Match against
      // an anchored regex: gameIds are pre-validated upstream as
      // `[a-zA-Z0-9_-]+` (assertValidGameId) so no regex metachars
      // are smuggled in here.
      if (gameId) {
        const matcher = new RegExp(`^gui-${gameId}-\\d+$`);
        if (!matcher.test(chunk.sessionId)) {
          return;
        }
      }
      // Bridge-3 — infrastructure-URL scrub. Codex round-1 HIGH:
      // the chat reducer's `error` branch stores `chunk.error`
      // verbatim, and `CrucibleErrorState` renders it in the
      // assistant bubble. A raw Furnace failure embedding
      // `forge-dev.volley-services.net` would otherwise leak the
      // hostname into the chat surface even while the new
      // `<FurnaceErrorBanner>` stayed clean.
      //
      // Scrub-level decision matrix (Codex round-5 fail-closed):
      //   - Status not yet resolved (undefined) → 'aggressive'.
      //     Fail closed — we don't know whether this turn was
      //     Furnace-routed; assume it might be and strip every
      //     URL. A benign local URL gets redacted in this
      //     loading window, but that's a brief, edge-case
      //     regression compared to leaking infrastructure URLs
      //     when status hadn't resolved.
      //   - In-cohort + flag-on → 'aggressive'.
      //   - Resolved + (flag-off OR cohort != 'in') →
      //     'banned-only'. We have a DEFINITIVE non-Furnace
      //     answer; preserve benign URLs (pre-Bridge-3 identity
      //     for flag-OFF / out-of-cohort engineers).
      const liveStatus = furnaceStatusRef.current;
      const isFurnaceRoutedNow =
        liveStatus !== undefined &&
        liveStatus.cloudVmV1Enabled === true &&
        liveStatus.cohort === 'in';
      const isDefinitelyLocal =
        liveStatus !== undefined &&
        (liveStatus.cloudVmV1Enabled === false || liveStatus.cohort !== 'in');
      const scrubLevel = isFurnaceRoutedNow || !isDefinitelyLocal ? 'aggressive' : 'banned-only';
      const scrubbedChunk: AgentStreamChunk =
        (chunk.type === 'error' || chunk.type === 'warning') && chunk.error !== undefined
          ? { ...chunk, error: scrubInfrastructureUrls(chunk.error, scrubLevel) }
          : chunk;

      if (chunk.type === 'error') {
        setInitialKickoff((current) => {
          if (
            current.status !== 'waiting-for-renderer-ready' &&
            current.status !== 'dispatching' &&
            current.status !== 'dispatched'
          ) {
            return current;
          }
          // Bridge-3 close-out (Bugbot Medium #1) — thread the
          // same `scrubLevel` the handler picked for the chunk
          // itself into the kickoff-failure detail so the two
          // user-facing surfaces (the ActivityBanner kickoff
          // detail + the chat-bubble error rendering of
          // `scrubbedChunk`) agree on the strip-aggressiveness.
          const failure = classifyKickoffFailure(chunk.error ?? 'Unknown error', scrubLevel);
          return {
            status: 'failed',
            reason: failure.reason,
            detail: failure.detail,
            startedAt: current.startedAt ?? null,
          };
        });
        // Bridge-3 — Furnace failure-latch. Classify the RAW error
        // (pre-scrub) so the classifier can match against the
        // original substrings; the classified copy is never built
        // from the raw text so no infrastructure URL leaks into the
        // user-facing surface anyway.
        //
        // Bridge-3 close-out — Bugbot Low #3 + Medium #2 stacked
        // gate. See `shouldLatchFurnaceFailure` JSDoc for the
        // full matrix; short version:
        //
        //   - During the loading window (`furnaceStatus === undefined`)
        //     we still latch eagerly so an in-cohort engineer whose
        //     first turn errors before `app:get-furnace-status`
        //     resolves still gets the indicator + banner
        //     retroactively (Codex round-1 MEDIUM by Agent N's
        //     original design intent). The clearing useEffect above
        //     mops up if status resolves to out-of-cohort instead —
        //     it depends on `furnaceStatus` so it fires on every
        //     status update, not just on `isFurnaceRouted`
        //     transitions (the Low #3 root cause).
        //
        //   - Once status resolves, we only latch when known
        //     in-cohort. A resolved-out latch never lands, so a
        //     subsequent cohort flip into 'in' can't resurface a
        //     stale failure (Bugbot Low #3 fix).
        //
        //   - Main now rejects sessionless terminal `agent:stream`
        //     chunks and pre-stream failures get a GUI-scoped
        //     `gui-<gameId>-<digits>` id, so the cross-game guard
        //     above can run for every chunk.
        //
        // The downstream banner mount-gate
        // (`furnaceBuildState.phase === 'failed' ? furnaceLastFailure : null`)
        // is the load-bearing visibility gate; the setter-level
        // gate above is the secondary one that keeps the latch
        // state honest.
        if (shouldLatchFurnaceFailure({ furnaceStatus: liveStatus })) {
          const view = classifyFurnaceError(chunk.error ?? '');
          setFurnaceLastFailure(view);
        }
      } else {
        setInitialKickoff((current) => {
          if (
            current.status !== 'waiting-for-renderer-ready' &&
            current.status !== 'dispatching' &&
            current.status !== 'dispatched'
          ) {
            return current;
          }
          return { status: 'idle', startedAt: null };
        });
      }
      setPinnedAskUserQuestion((pending) =>
        pending && shouldClearPinnedAskUserQuestion(pending.id, scrubbedChunk) ? null : pending,
      );
      if (
        fastAnswersSubmittedAtRef.current !== null &&
        (scrubbedChunk.type === 'done' ||
          scrubbedChunk.type === 'interrupted' ||
          scrubbedChunk.type === 'error')
      ) {
        if (!fastPlanReadyRef.current) setFastPlanMissingAt(Date.now());
        setFastAnswersSubmittedAt(null);
      }
      chatDispatch({ type: 'chunk/received', chunk: scrubbedChunk });
    });

    return unsub;
  }, [gameId, chatDispatch]);

  // Crucible #416 Chunk C — mirror chat messages into the in-session
  // phase-events buffer. The phase-detail panel filters this buffer
  // by `event.phaseSeq` to show "what happened during phase N". The
  // buffer is per-game (the hook scopes via `setGameId(gameId)`), and
  // the store's `recordEvent` tags every entry with the current
  // `phaseSeq` snapshot — see `stores/phase-events.ts`.
  //
  // We track the count rather than diffing the array because the
  // chat reducer's append-only invariant means new messages always
  // land at the end; replaying the entire array on every render
  // would re-buffer events the store already has. The ref keeps the
  // last-mirrored count between renders.
  //
  // Crucible #602 PR #C4 (Issue E) — Codex round-1 HIGH #2: the chat
  // reducer's `chat/checkpoint-received` action can INSERT a row at a
  // non-tail index (the action sorts by timestamp). When that happens
  // every row after the insertion point shifts forward by one and the
  // cursor walks back over rows we've already mirrored. The store's
  // `recordEvent` enforces id-dedupe (see `phase-events.ts:76`) so
  // duplicates are silently dropped, but we ALSO skip rows by id here
  // to avoid the redundant cross-call. The cursor stays index-based
  // (cheap; advancement is O(delta)); id-tracking is layered on top.
  const lastMirroredCount = useRef(0);
  const mirroredMessageIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (messages.length < lastMirroredCount.current) {
      // The reducer wiped the array (game switch / start over). Reset
      // our cursor + id-tracker so we start fresh.
      lastMirroredCount.current = 0;
      mirroredMessageIdsRef.current = new Set();
    }
    for (let i = lastMirroredCount.current; i < messages.length; i++) {
      const msg = messages[i];
      if (!msg) continue;
      // Crucible #602 PR #C1: `'inline-checkpoint'` is a reserved
      // role for system-milestone rows (PR #C3 emits them). They
      // are chrome, not chat — skip them from the phase-events
      // mirror so the phase strip's per-phase filter doesn't
      // attribute checkpoints to whatever phase happened to be
      // active when the checkpoint was emitted.
      if (msg.role === 'inline-checkpoint') continue;
      // Crucible #602 PR #C4 (Issue E) — Codex round-1 HIGH #2:
      // skip rows we've already mirrored. The reducer's by-timestamp
      // insertion of checkpoints can shift later user/assistant rows
      // back into the cursor's range; without this guard we'd call
      // `recordPhaseEvent` for the shifted row a second time (the
      // store's id-dedupe drops it, but we save the round-trip + a
      // potentially-wrong-phase first-record path).
      if (mirroredMessageIdsRef.current.has(msg.id)) continue;
      mirroredMessageIdsRef.current.add(msg.id);
      recordPhaseEvent({
        id: msg.id,
        role: msg.role,
        content: msg.content,
        timestamp: msg.timestamp,
      });
    }
    lastMirroredCount.current = messages.length;
  }, [messages, recordPhaseEvent]);

  // Install the effect interpreter — converts the reducer's
  // `ipc/agent-*` effects into real `client.invoke` calls.
  // Re-installs whenever the initial-kickoff readiness gate or
  // send invoker changes. The `setInterpreter` call is idempotent:
  // it replaces the previous closure and drains any effects that
  // fired before the very first installation (e.g. an autofire
  // `chat/loaded` effect dispatched synchronously inside a render
  // that beat this useEffect).
  useEffect(() => {
    setInterpreter((effect) => {
      switch (effect.type) {
        case 'ipc/agent-send': {
          const isInitialKickoff = effect.initialAutofire === true;
          if (isInitialKickoff) {
          }
          if (isInitialKickoff && shouldHoldInitialKickoff()) {
            pendingInitialAgentSendRef.current = effect;
            setInitialKickoff((current) => ({
              status: 'waiting-for-renderer-ready',
              startedAt: current.startedAt ?? Date.now(),
            }));
            return;
          }
          invokeAgentSendEffect(effect, isInitialKickoff);
          return;
        }
        case 'ipc/agent-interrupt':
          // Issue #140: fire-and-forget. The adapter emits an
          // `interrupted` chunk on `agent:stream` once the SDK
          // iterator unwinds — that's what stamps "(stopped)" on
          // the in-progress bubble (see the reducer's
          // `chunk: interrupted` branch). Errors here only surface
          // if the IPC bridge itself wedged.
          void client.invoke('agent:interrupt', { gameId: effect.gameId }).catch((err: unknown) => {
            console.error('[chat] agent:interrupt failed', err);
          });
          return;
        case 'ipc/agent-answer':
          // Reserved for the AskUserQuestion gate path. The
          // AskUserQuestionCard currently fires its own IPC directly
          // (see `sendAnswer` below) — this case lets a future
          // refactor route gate answers through the reducer for
          // testability without changing the wire.
          void client
            .invoke('agent:answer-user-question', {
              toolUseID: effect.toolUseID,
              answers: effect.answers,
            })
            .catch((err: unknown) => {
              console.error('[chat] agent:answer-user-question failed', err);
            });
          return;
        case 'ui/error':
          // Screenshot-paste MVP (#193): the reducer surfaces
          // attachment-cap rejections via this effect. A `console.warn`
          // for telemetry; the input-row banner reads directly from
          // `chat.lastAttachmentError` for the visible UI. (Setting
          // it back through dispatch would require yet another action
          // — for the MVP we just store it locally on the chat slice
          // when the reducer rejects the chip.)
          console.warn('[chat] ui/error', effect.message);
          setAttachmentError(effect.message);
          return;
        case 'ipc/deps-respond': {
          // Crucible #548 — fire `deps:respond` carrying the user's
          // decision. Approval path branches in main (drives pnpm and
          // synthesises the tool result); decline path simply resolves
          // the gate.
          //
          // #3059 — the reducer popped the modal in the same reduction
          // that emitted this effect, so a REJECTED IPC (transient bridge
          // hiccup; on the cloud path a WSS drop / pod roll mid-flight,
          // where BrowserCrucibleClient rejects pending invokes) would
          // strand the agent's held-open gate with zero user signal. On
          // failure, re-enqueue the original request so the modal reappears
          // and the user can retry — `deps/request` dedups by sessionId, so
          // a repeated failure never double-stacks.
          //
          // #3059 Bugbot (cross-game) — only re-enqueue when the user is STILL
          // on the request's game. A late rejection landing after a game
          // switch (A -> B) must NOT re-surface game A's modal on game B. Read
          // the LIVE gameId via gameIdRef (this closure has no live gameId of
          // its own); mirrors the deps:request push-drop guard.
          const reEnqueueOnFailure = (): void => {
            if (shouldReEnqueueDependencyRequest(effect.request.gameId, gameIdRef.current)) {
              chatDispatch({ type: 'deps/request', request: effect.request });
            } else {
              console.warn(
                '[chat] deps:respond failed after a game switch; dropping stale cross-game re-enqueue',
                { requestGameId: effect.request.gameId, activeGameId: gameIdRef.current },
              );
            }
          };
          if (effect.approved) {
            void client
              .invoke('deps:respond', {
                sessionId: effect.sessionId,
                approved: true,
                finalVersion: effect.finalVersion,
              })
              .catch((err: unknown) => {
                console.error('[chat] deps:respond (approve) failed', err);
                reEnqueueOnFailure();
              });
          } else {
            void client
              .invoke('deps:respond', {
                sessionId: effect.sessionId,
                approved: false,
                reason: effect.reason,
              })
              .catch((err: unknown) => {
                console.error('[chat] deps:respond (decline) failed', err);
                reEnqueueOnFailure();
              });
          }
          return;
        }
        case 'ipc/template-file-override-respond': {
          // Crucible #3491 — fire `template-file-override:respond`
          // carrying the user's decision. Mirrors `ipc/deps-respond`
          // exactly, including the #3059 re-enqueue-on-failure /
          // cross-game guard.
          const reEnqueueOnFailure = (): void => {
            if (
              shouldReEnqueueTemplateFileOverrideRequest(effect.request.gameId, gameIdRef.current)
            ) {
              chatDispatch({ type: 'template-file-override/request', request: effect.request });
            } else {
              console.warn(
                '[chat] template-file-override:respond failed after a game switch; dropping stale cross-game re-enqueue',
                { requestGameId: effect.request.gameId, activeGameId: gameIdRef.current },
              );
            }
          };
          if (effect.approved) {
            void client
              .invoke('template-file-override:respond', {
                sessionId: effect.sessionId,
                approved: true,
              })
              .catch((err: unknown) => {
                console.error('[chat] template-file-override:respond (approve) failed', err);
                reEnqueueOnFailure();
              });
          } else {
            void client
              .invoke('template-file-override:respond', {
                sessionId: effect.sessionId,
                approved: false,
                reason: effect.reason,
              })
              .catch((err: unknown) => {
                console.error('[chat] template-file-override:respond (decline) failed', err);
                reEnqueueOnFailure();
              });
          }
          return;
        }
      }
    });
  }, [
    setInterpreter,
    shouldHoldInitialKickoff,
    invokeAgentSendEffect,
    setAttachmentError,
    chatDispatch,
  ]);

  const handleInterviewRendererReady = useCallback(() => {
    setInterviewRendererReady(true);
  }, []);

  const handleSendMessage = useCallback(async (): Promise<void> => {
    // "Waiting on your input…" fix — when an AskUserQuestion gate is open,
    // a typed reply must reach the paused agent, not queue behind the
    // in-flight turn. Route it to the gate's answer (same IPC the card uses,
    // which resumes the turn — no new send, no #170 bounce) instead of the
    // `user/send` queue path. Scoped to a single-question gate; see
    // `decideComposerSend`.
    const gateDecision = decideComposerSend({ pinnedAskUserQuestion, text: chatInput });
    if (gateDecision.kind === 'answer-question') {
      // A route-less workspace cannot safely choose a Local or Cloud question gate.
      if (!gameId) {
        console.error('[chat] agent:answer-user-question missing gameId');
        return;
      }
      // #3060 — fresh attempt: clear any prior failure banner.
      setComposerAnswerError(null);
      try {
        const result = await client.invoke('agent:answer-user-question', {
          toolUseID: gateDecision.toolUseID,
          answers: gateDecision.answers,
        });
        if (!result.ok) {
          console.error(`[chat] agent:answer-user-question (composer) rejected: ${result.reason}`);
          // #3060 — the text is preserved (not cleared), but the user needs a
          // VISIBLE signal that the answer never landed and the gate is open.
          setComposerAnswerError(
            composerAnswerErrorMessage({ kind: 'rejected', reason: result.reason }),
          );
          return;
        }
        chatDispatch({ type: 'user/type', text: '' });
      } catch {
        console.error('[chat] agent:answer-user-question (composer) IPC failed');
        setComposerAnswerError(composerAnswerErrorMessage({ kind: 'threw' }));
      }
      return;
    }
    chatDispatch({ type: 'user/send' });
  }, [chatDispatch, chatInput, client, gameId, pinnedAskUserQuestion]);

  const handleStopMessage = useCallback(() => {
    chatDispatch({ type: 'user/stop' });
  }, [chatDispatch]);

  /**
   * #2575 — stable ChatPanel callbacks. These were previously inline
   * arrows in the `<ChatPanel>` JSX, so they got a fresh reference on
   * every Workspace render. Combined with ChatPanel now being wrapped in
   * `React.memo`, hoisting them here (all deps are stable: `chatDispatch`
   * is a fixed-identity `useCallback` from `useChatReducer`, the setters
   * are `useState` setters) lets ChatPanel — and its ChatBubble children —
   * skip re-rendering on Workspace renders that don't touch chat data.
   */
  const handleChatInputChange = useCallback(
    (v: string) => {
      chatDispatch({ type: 'user/type', text: v });
    },
    [chatDispatch],
  );

  const handleUnqueueMessage = useCallback(
    (idx: number) => {
      chatDispatch({ type: 'user/unqueue', index: idx });
    },
    [chatDispatch],
  );

  const handleDismissPersistError = useCallback(() => {
    setChatPersistError(null);
  }, [setChatPersistError]);

  const handleAddAttachment = useCallback(
    (a: AgentSendAttachment) => {
      setAttachmentError(null);
      chatDispatch({ type: 'user/add-attachment', attachment: a });
    },
    [chatDispatch, setAttachmentError],
  );

  const handleRemoveAttachment = useCallback(
    (idx: number) => {
      chatDispatch({ type: 'user/remove-attachment', index: idx });
    },
    [chatDispatch],
  );

  const handleDismissAttachmentError = useCallback(() => {
    setAttachmentError(null);
  }, [setAttachmentError]);

  // #3060 — stable dismiss for the composer answer-error banner (ChatPanel is
  // React.memo'd; an inline arrow would defeat it — see #2575).
  const handleDismissAnswerError = useCallback(() => {
    setComposerAnswerError(null);
  }, []);

  const handleCloseReviewModal = useCallback(() => {
    setShowReviewModal(false);
  }, [setShowReviewModal]);

  const handleAssetReviewDone = useCallback(() => {
    setAssetReviewCardGameId(null);
  }, [setAssetReviewCardGameId]);

  /**
   * Issue #369 — chat-bubble retry. Wired only when
   * `message.error.retryable === true` (currently emitted by PR #368
   * for the inFlight bouncer's `CRUCIBLE-FOUNDRY-TURN-IN-FLIGHT` case;
   * future retryable codes get the same affordance for free).
   *
   * Debounce window is sized to the agent-adapter bouncer's grace
   * timer (`agent-adapter.ts:2986-2996`, 1500ms): a rapid second click
   * inside that window would just hit the same bouncer again, and the
   * user has no way to tell that the first click took. The ref-based
   * debounce stops the second dispatch synchronously without burning
   * an IPC round-trip. We fall through to the reducer for the actual
   * "is a turn in flight" guard so the two checks compose cleanly.
   */
  const lastRetryAtRef = useRef(0);
  const handleRetry = useCallback(
    (message: ChatMessage) => {
      const now = Date.now();
      if (now - lastRetryAtRef.current < 1500) return;
      lastRetryAtRef.current = now;
      chatDispatch({ type: 'user/retry', messageId: message.id });
    },
    [chatDispatch],
  );

  /**
   * Issue #215 — fallback parser click handler. The bubble
   * component matched a `Question N of M:` markdown shape and the
   * user clicked one of the synthetic option buttons. Dispatch the
   * option text via the same `user/type` + `user/send` path the
   * textarea uses, so:
   *
   *   - Mid-turn semantics are identical to typing the answer
   *     (queue when busy, send directly otherwise — handled by the
   *     reducer).
   *   - Cross-game guards apply (the reducer checks `state.gameId`
   *     when building the IPC effect).
   *   - The user's answer appears as a normal user-bubble in the
   *     timeline, just like typing it would.
   *
   * Pre-set `chatInput` first so an in-flight Compose-state value
   * doesn't get clobbered if a click and a textarea-edit race;
   * `user/send` reads from the reducer's `chatInput` field, so the
   * sequence MUST be type-then-send.
   */
  const handleAnswerMarkdownQuestion = useCallback(
    (optionText: string) => {
      chatDispatch({ type: 'user/type', text: optionText });
      chatDispatch({ type: 'user/send' });
    },
    [chatDispatch],
  );

  // Track whether the interview overlay is currently showing a slot
  // card. Subscribed at Workspace scope so it can drive layout; the
  // overlay subscribes in parallel for its own state.
  useEffect(() => {
    setInterviewSlotActive(false);
  }, [gameId]);
  // Re-open the artifact pane when a new interview push arrives after a soft-close.
  useEffect(() => {
    if (interviewSlotActive) {
      setArtifactSoftClosed(false);
    }
  }, [interviewSlotActive]);
  // Note: the activity rail is now a pinned column whose visibility is
  // derived at render time (`rightPanelOpen && (buildView ||
  // !artifactPaneOpen)`) — when the artifact pane claims the right slot
  // (discovery / plan review) the rail yields to it, then returns once
  // the artifact pane closes. `rightPanelOpen` is therefore owned purely
  // by the header toggle; the old auto-open/auto-close effects (which
  // existed to manage the slide-in overlay) have been removed so they
  // don't fight the toggle or strand the rail closed after an artifact
  // pane session.
  // The legacy slot-interview engine's `interview:show-*`/`interview:complete`
  // push subscriptions (which used to drive `setInterviewSlotActive`) were
  // removed alongside the engine itself (crucible#3172-follow-on) — adaptive
  // soul-intake never fires those channels. `interviewSlotActive` now stays
  // permanently false; every consumer already degrades correctly for that.

  // #2058 — surface the in-chat steering / asset-review card.
  // When `interviewIntegrated` is ON, mount the steering nudge (ChatTvSteeringNudge
  // → TvSteeringSurface) inline in the chat stream instead of letting
  // BuildReviewOverlay show a modal.
  //
  // The panel must mount regardless of event timing. The build-time review fires
  // `asset-review:ready` ONE-SHOT, and in the normal auto-build ordering (create
  // → navigate → build kickoff fires the review) that push can land BEFORE the
  // Workspace subscribes — the event is then lost and the panel never mounts (the
  // #1 north-star bug). `useAssetReviewSurface` solves the race with BOTH a live
  // `asset-review:ready` subscription AND a mount probe of the queryable
  // `asset-review:active` channel, so the invariant holds: if a build-time review
  // is in flight for the focused game, the steering panel is mounted — no timing
  // dependency. We mirror its result into local state so `onDone` can dismiss the
  // card once the engine reports done. The active-review probe can still report
  // the same game for a tick after done, so the completed latch keeps the review
  // from remounting over the "review complete" handoff surface.
  // crucible#4051 — gated on flags.buildTimeAssetReview too (not just
  // flags.intake): the flag defaults OFF, and before this change the Develop
  // tab's build-time-review card/steering-surface mount was driven purely by
  // whether a review server happened to warm and push `asset-review:ready`
  // (art-review-warm-policy.ts's warm-on-dev-start path), independent of
  // whether the flag meant to allow the surface at all. This is the single
  // highest-leverage gate: `detectedAssetReviewGameId` staying null flows
  // into `assetReviewCardGameId`, which is the ONLY input to both
  // NorthStarConsumerSurface's render (below) and the legacy TvSteeringSurface
  // mount, so gating here covers both call sites with one change.
  const detectedAssetReviewGameId = useAssetReviewSurface(
    gameId,
    flags.intake && flags.buildTimeAssetReview,
    placement,
  );
  useEffect(() => {
    if (!flags.newUi || !flags.intake) return undefined;
    const unsub = client.subscribe('build-asset-review:gate-released', (event) => {
      if (event.gameId !== gameId) return;
      if (event.outcome !== 'approved' || event.turnReleased !== true) return;
      setNorthStarBuildGateReleasedGameId(event.gameId);
    });
    return unsub;
  }, [flags.newUi, flags.intake, gameId]);
  useEffect(() => {
    if (!detectedAssetReviewGameId) {
      setAssetReviewCardGameId(null);
      return;
    }
    if (northStarReviewCompleteGameId === detectedAssetReviewGameId) {
      setAssetReviewCardGameId(null);
      return;
    }
    setNorthStarReviewCompleteGameId(null);
    setNorthStarReviewSummary(null);
    setNorthStarBuildGateReleasedGameId(null);
    setAssetReviewCardGameId(detectedAssetReviewGameId);
  }, [detectedAssetReviewGameId, northStarReviewCompleteGameId]);
  useEffect(() => {
    if (!detectedAssetReviewGameId || !flags.newUi) return;
    // See workspaceModeSurvivesAssetReviewRedirect's docblock (crucible#2611
    // Task 4, crucible#2974) for why 'chat'/'preview'/'art'/'design'/'protohub'
    // are exempt from this force-navigate-to-chat.
    if (workspaceModeSurvivesAssetReviewRedirect(workspaceMode)) {
      return;
    }
    setWorkspaceMode('chat');
  }, [detectedAssetReviewGameId, flags.newUi, workspaceMode]);

  // #2058 — host the adaptive soul-intake interview INLINE in the Workspace chat.
  // Under newUi=1 the create flow (NewGame page) navigates here WHILE create is still
  // running the soul-intake, so the interview must surface in the Workspace (the Dashboard
  // overlay is left behind). Two triggers: the live `intake:ready` push AND a
  // canonical checkpoint probe that consults `intake:turn` only when there is
  // active/recoverable evidence. The probe catches a `ready` push that preceded
  // this mount without manufacturing intake errors for ordinary workspaces.
  useEffect(() => {
    if (!flags.intake || !gameId) return;
    let cancelled = false;
    let liveIntakeObserved = false;
    setSoulIntakeActive(false); // reset on game change
    setSoulInterviewFinalizing(false);
    setIntakeAuthorityMissingGameId((current) =>
      clearIntakeAuthorityMissingForGame(current, gameId),
    );
    const unsub = client.subscribe('intake:ready', (event) => {
      if (gameId && event.gameId !== gameId) return;
      if (!cancelled) {
        liveIntakeObserved = true;
        setSoulInterviewFinalizing(false);
        setSoulIntakeActive(true);
        setIntakeAuthorityMissingGameId((current) =>
          clearIntakeAuthorityMissingForGame(current, event.gameId),
        );
      }
    });
    void (async () => {
      try {
        const probe = await probeAdaptiveIntakeOnWorkspaceMount({
          client,
          gameId,
          explicitCreateHandoff: createRuntimePending,
          shouldStopProbing: () => cancelled || liveIntakeObserved,
        });
        if (cancelled || liveIntakeObserved) return;
        if (probe.state === 'missing-expected-authority') {
          setIntakeAuthorityMissingGameId(gameId);
          return;
        }
        if (probe.state === 'active') {
          const { turn } = probe;
          liveIntakeObserved = true;
          setIntakeAuthorityMissingGameId((current) =>
            clearIntakeAuthorityMissingForGame(current, gameId),
          );
          if (turn.done) {
            if (createHandoffMode === 'design') {
              setWorkspaceMode('design');
              // crucible#3516 — the "Build my game" affordance is NOT flipped
              // here. This is an IN-MEMORY probe (intake:turn → the
              // adaptive-intake session registry): it can only observe a
              // session that was opened, so it structurally CANNOT recover the
              // no-interview didn't-run path (no session ⇒ intake:turn throws
              // ⇒ this whole block is skipped) — Bugbot HIGH "No-interview
              // settle can miss subscribe". The durable
              // `create:deferred-design-status:get` mount read below is the
              // single mount-time flip authority: it covers EVERY terminal
              // outcome (interviewed / no-interview / failed) uniformly.
            }
          } else {
            setSoulInterviewFinalizing(false);
            setSoulIntakeActive(true);
          }
        }
      } catch {
        /* No active or recoverable run for this workspace. */
      }
    })();
    return () => {
      cancelled = true;
      unsub();
    };
  }, [createHandoffMode, createRuntimePending, deferredDesignHandoff, gameId, flags.intake]);

  // Issue #164 follow-up — reset chat state when the workspace
  // navigates between games. React Router reuses the same Workspace
  // component instance for `/workspace/:gameId` route changes, so
  // navigating from game A to game B does NOT unmount us. The reducer's
  // `game/changed` action wipes messages + queue + in-flight back to
  // initial-state for the new gameId. The chat:load effect re-runs on
  // gameId change and repopulates `messages` from the new game's
  // history file.
  useEffect(() => {
    if (!gameId) return;
    chatDispatch({ type: 'game/changed', gameId });
    setPinnedAskUserQuestion(null);
  }, [gameId, chatDispatch]);

  // Phase 4: subscribe to the SDK's `AskUserQuestion` push channel.
  // When the permission gate holds a tool call open, we inject a
  // synthetic tool-call entry via the reducer's `chunk/received`
  // action with a synthesised `tool_call` chunk — that runs through
  // the same upsert path as canonical SDK tool_call chunks, which
  // dedupes if the SDK has already streamed one for the same id.
  useEffect(() => {
    const unsub = client.subscribe('agent:ask-user-question', (push) => {
      if (gameId && push.gameId !== gameId) return;
      const toolCall = toolCallFromAskUserQuestionPush(push);
      setPinnedAskUserQuestion(toolCall);
      chatDispatch({
        type: 'chunk/received',
        chunk: {
          type: 'tool_call',
          // Push channel doesn't carry a sessionId — the reducer
          // doesn't read this field, but the AgentStreamChunk type
          // requires it. Tag the synthetic origin for forensic logs.
          sessionId: 'synthetic-ask-user-question',
          toolCall: {
            id: toolCall.id,
            name: toolCall.name,
            input: toolCall.input,
          },
        },
      });
    });
    return unsub;
  }, [gameId, chatDispatch]);

  // The legacy `foundry:ask-user-question:answer` CustomEvent
  // listener was removed alongside its sole dispatcher in
  // `AskUserQuestionCard.sendAnswer`. The dispatch was firing
  // `agent:send` while the SDK was still mid-turn waiting on the
  // gate's Promise resolution — bouncing off the in-flight bouncer.
  // The IPC gate path (`agent:answer-user-question`) is now the
  // single source of truth for delivering option-button answers.
  // See the comment in `sendAnswer` for the full retro.

  // Recover interactive ownership only after every question listener above is
  // installed. The main process replays an authoritative pending interaction
  // through its existing push channel, so AskUserQuestion and
  // the slot/review/takeover overlays keep one rendering path across Local and
  // Cloud placements.
  useEffect(() => {
    if (!gameId || !featureFlags.loaded) return;
    if (featureFlags.interviewEngineV1 && !interviewRendererReady) return;

    const projectKey = `${placement}:${gameId}`;
    const shouldHydratePendingInteraction =
      pendingInteractionHydrationKeyRef.current !== projectKey;
    const shouldRecoverStalledInterview =
      resumeBuildActionKind === 'retry-interview' &&
      automaticInterviewRecoveryKeyRef.current !== projectKey;
    if (!shouldHydratePendingInteraction && !shouldRecoverStalledInterview) return;

    if (shouldHydratePendingInteraction) {
      pendingInteractionHydrationKeyRef.current = projectKey;
    }
    if (shouldRecoverStalledInterview) {
      automaticInterviewRecoveryKeyRef.current = projectKey;
    }

    const controller = new AbortController();
    let recoverySettled = false;
    void client
      .invoke('interview:get-pending-interactions', { gameId })
      .then(async (snapshot) => {
        if (controller.signal.aborted || snapshot.kind === 'pending') return;
        if (!shouldRecoverStalledInterview) return;

        setChatTab('chat');
        setWorkspaceMode('chat');
        const result = await recoverInitialInterview('workspace-open', controller.signal);
        if (controller.signal.aborted) return;
        refreshInterviewRecoveryState();
        if (result.kind === 'replayed' || result.kind === 'started') {
          setPreviewToast({
            tone: 'info',
            message:
              result.kind === 'replayed'
                ? 'Interview question restored.'
                : 'Interview restarted from your saved answers.',
          });
        }
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        // Reopen both latches so a later render can retry the failed async recovery chain.
        pendingInteractionHydrationKeyRef.current = null;
        if (!shouldRecoverStalledInterview) {
          return;
        }
        automaticInterviewRecoveryKeyRef.current = null;
        setPreviewToast({
          tone: 'error',
          message: formatInterviewRecoveryError(error, 'Interview needs attention'),
        });
      })
      .finally(() => {
        recoverySettled = true;
      });

    /**
     * Stops this recovery generation and releases any unfinished latch claims.
     *
     * Args:
     *   None.
     *
     * Returns:
     *   Nothing.
     *
     * Raises:
     *   Never.
     */
    return () => {
      controller.abort(new Error('Workspace interaction recovery stopped'));
      if (recoverySettled) return;

      // Release only the hydration claim made by this unfinished generation.
      if (
        shouldHydratePendingInteraction &&
        pendingInteractionHydrationKeyRef.current === projectKey
      ) {
        pendingInteractionHydrationKeyRef.current = null;
      }

      // Preserve a newer project's claim if dependencies changed during recovery.
      if (
        shouldRecoverStalledInterview &&
        automaticInterviewRecoveryKeyRef.current === projectKey
      ) {
        automaticInterviewRecoveryKeyRef.current = null;
      }
    };
  }, [
    client,
    featureFlags.interviewEngineV1,
    featureFlags.loaded,
    gameId,
    interviewRendererReady,
    placement,
    recoverInitialInterview,
    refreshInterviewRecoveryState,
    resumeBuildActionKind,
  ]);

  // Crucible #548 — subscribe to the `deps:request` push channel. The
  // agent-adapter's `requestDependency` MCP tool handler pushes a
  // request whenever a specialist asks to add an npm dependency. The
  // reducer enqueues it; the modal mount further down reads the
  // first entry and surfaces it to the user. Drops the push silently
  // when the gameId doesn't match the active Workspace — same cross-
  // game guard the other push channels use.
  useEffect(() => {
    const unsub = client.subscribe('deps:request', (push) => {
      if (gameId && push.gameId !== gameId) return;
      chatDispatch({ type: 'deps/request', request: push });
    });
    return unsub;
  }, [gameId, chatDispatch]);

  // Crucible #3491 — subscribe to the `template-file-override:request`
  // push channel. Same shape as the `deps:request` subscription above.
  useEffect(() => {
    const unsub = client.subscribe('template-file-override:request', (push) => {
      if (gameId && push.gameId !== gameId) return;
      chatDispatch({ type: 'template-file-override/request', request: push });
    });
    return unsub;
  }, [gameId, chatDispatch]);

  // Preview start handler removed in alpha.50; the dev:start IPC
  // remains intact main-side for the eventual Preview restructure.

  // Codex P2 round 1: only arm the failure card when CI was actually
  // triggered for THIS deploy attempt. Pre-CI failures (no remote,
  // pre-flight refusal, push failure) MUST NOT mount the card —
  // `deploy:get-failure-context` would otherwise look up the latest
  // workflow run for the repo, which is from a PREVIOUS deploy and
  // unrelated to the failure the user just hit. The card would then
  // surface stale annotations and let the user paste irrelevant
  // errors into chat.
  //
  // Codex P2 round 2: the gate is keyed on `ci-triggered` ONLY.
  // The IPC handler in main overloads `tests` as the renderer step
  // for BOTH the CLI's pre-flight phase AND the CI's quality-gate
  // job, so `tests` is ambiguous: a pre-flight failure flips to
  // step `tests` BEFORE any push or workflow exists. The other
  // CI-phase steps (`build-image`, `deploy`, `verify`) come strictly
  // AFTER `ci-triggered` in the pipeline, so they're a valid
  // post-CI signal too — we include them so a deploy that races
  // past `ci-triggered` (rare but possible if the CI poll cadence
  // misses the intermediate event) still arms the card on later
  // failures. We deliberately exclude `tests` to avoid the
  // pre-flight / quality-gate ambiguity.
  const ciTriggeredThisDeployRef = useRef(false);
  const deployAttemptIdRef = useRef(0);
  const protoHubOpenedDeployAttemptRef = useRef<string | null>(null);
  const openProtoHubAfterDeploy = useCallback(
    (deployedGameId: string) => {
      const deployAttemptKey = `${gameId}:${deployAttemptIdRef.current}`;
      const openedForDeployAttemptKey = routePostDeployProtoHub({
        gameId: deployedGameId,
        workspaceGameId: gameId,
        deployAttemptKey,
        openedForDeployAttemptKey: protoHubOpenedDeployAttemptRef.current,
        nativeSimulatorAvailable,
        hostBridgeAvailable: !!hostBridge,
        openHostBridge: () => hostBridge?.requestProtoHubPreview(deployedGameId),
        openNative: () => openProtoHubSurface('post-deploy'),
      });
      if (openedForDeployAttemptKey !== protoHubOpenedDeployAttemptRef.current) {
        protoHubOpenedDeployAttemptRef.current = openedForDeployAttemptKey;
      }
    },
    [gameId, openProtoHubSurface, nativeSimulatorAvailable, hostBridge],
  );

  // Subscribe to publish progress events
  useEffect(() => {
    const unsub = client.subscribe('publish:progress', (event) => {
      applyWorkspacePublishProgress(event, gameId, {
        setMessage: setDeployMessage,
        logDeveloperMessage: (message) => console.debug('[publish:verbose]', message),
        setLocalFailedCheck,
        clearDeployFailureContext: () => {
          queryClient.removeQueries({
            queryKey: projectQueryKey(client, ['deploy:get-failure-context', gameId]),
          });
        },
        markPostCiTriggered: () => {
          ciTriggeredThisDeployRef.current = true;
        },
        isPostCiTriggered: () => ciTriggeredThisDeployRef.current,
        markResolved: () => {
          deployResolvedRef.current = true;
        },
        setDeployState,
        setDeployFailureCardEnabled,
        setDeployErrorCode,
        openProtoHub: openProtoHubAfterDeploy,
        invalidateDeployUpToDate: () => invalidateDeployUpToDate(queryClient, client, gameId),
        refetchDeployFailure,
      });
    });
    return unsub;
  }, [gameId, openProtoHubAfterDeploy, refetchDeployFailure]);

  // Subscribe to editor errors
  const editorErrorTimerRef = useRef<NodeJS.Timeout>(undefined);
  useEffect(() => {
    const unsub = client.subscribe('editor:error', (msg) => {
      setEditorError(msg);
      clearTimeout(editorErrorTimerRef.current);
      editorErrorTimerRef.current = setTimeout(() => setEditorError(null), 5000);
    });
    return () => {
      unsub();
      clearTimeout(editorErrorTimerRef.current);
    };
  }, []);

  // Issue #597 — banner trigger. Two paths feed the banner:
  //
  //   (1) `gameStatus.autoSeeded` on the IPC response — the
  //       load-bearing signal. Always-available because the renderer
  //       reads it from React Query state, no subscription timing.
  //       (Codex review round 1 finding: the push alone races the
  //       subscription registration.)
  //
  //   (2) `interview:auto-seeded` push — fires from `agent:send`'s
  //       belt-and-braces seed path too, so a user who slips past
  //       the `game:status` race still gets the banner the moment
  //       their first `/build-game` turn lands the seed.
  //
  // De-dupe by gameId so we don't double-paint if both paths fire
  // for the same seed. Auto-fades after 8s; non-blocking; "Prepared"
  // (not "Started") per Codex review — the seed is the precondition,
  // not the interview itself.
  const autoSeededTimerRef = useRef<NodeJS.Timeout>(undefined);
  const lastBannerKeyRef = useRef<string | null>(null);
  // Phase 0a e2e sweep — when the auto-seed lands AFTER the
  // renderer's first `build-game:get-interview-state` query has
  // already resolved (typical on slow disks / first-mount race),
  // React Query's cache holds `null` until a manual reload. The
  // Deploy gate then reports "Game has no build history" even
  // though the seed file is on disk. Invalidating the cached
  // queries on auto-seeded triggers a refetch and the gate
  // observes the fresh state. Pre-existing race exposed by the
  // Playwright workspace suite; fix is renderer-side.
  // Preview diagnostics (`game-state:get` refetch on ready-session /
  // phone-count change) now live in `usePreviewModule`; read the result via
  // `preview.derived.diagnostics`.
  const fireAutoSeededBanner = useCallback(
    (forGameId: string) => {
      const key = forGameId;
      // Always invalidate the interview-state queries even if we've
      // already painted the banner — a duplicate auto-seed push (path
      // 1 + path 2) is harmless to invalidate twice; missing the
      // refetch the second time leaves the gate stuck.
      void queryClient.invalidateQueries({
        queryKey: projectQueryKey(client, ['build-game:get-interview-state', forGameId]),
      });
      void queryClient.invalidateQueries({
        queryKey: projectQueryKey(client, ['build-game:interview-status', forGameId]),
      });
      if (lastBannerKeyRef.current === key) return; // de-dupe banner paint only
      lastBannerKeyRef.current = key;
      setAutoSeededBanner(
        'No build session found for this game — prepared a fresh quick-batch interview. Type /build-game to begin.',
      );
      clearTimeout(autoSeededTimerRef.current);
      autoSeededTimerRef.current = setTimeout(() => setAutoSeededBanner(null), 8000);
    },
    [queryClient],
  );

  // Path (1) — IPC response carries `autoSeeded: true`. Fires once
  // per game session because the React Query cache persists the
  // flag; the `lastBannerKeyRef` de-dupe prevents re-firing on
  // re-renders.
  useEffect(() => {
    if (!gameId) return;
    if (gameStatus?.autoSeeded) {
      fireAutoSeededBanner(gameId);
    }
  }, [gameId, gameStatus?.autoSeeded, fireAutoSeededBanner]);

  // Path (2) — push subscription. Belt-and-braces for the
  // `agent:send` seed call site. Gameid-scoped so a future
  // multi-window setup doesn't paint another window's seed event
  // on this one.
  useEffect(() => {
    const unsub = client.subscribe('interview:auto-seeded', (payload) => {
      if (payload.gameId !== gameId) return;
      fireAutoSeededBanner(payload.gameId);
    });
    return () => {
      unsub();
      clearTimeout(autoSeededTimerRef.current);
    };
  }, [gameId, fireAutoSeededBanner]);

  // Reset the de-dupe ref AND clear any stale banner state when the
  // user switches games. Bugbot round 2 MEDIUM: previously this
  // effect only cleared the ref, so a banner that was painted for
  // game A could persist into game B if the user switched within
  // the 8s timer window. Clearing the banner + the timer here
  // closes that gap. The new game's auto-seed (if any) will still
  // re-paint via the path (1) / path (2) effects above.
  useEffect(() => {
    lastBannerKeyRef.current = null;
    setAutoSeededBanner(null);
    clearTimeout(autoSeededTimerRef.current);
    // #3132 — a deploy confirm opened for game A must not linger into
    // game B if the user switches games while the modal is up.
    setDeployConfirmOpen(false);
  }, [gameId]);

  useEffect(() => {
    const unsub = client.subscribe('build:autoPipeline:progress', (payload) => {
      if (payload.gameId !== gameId) return;
      void queryClient.invalidateQueries({
        queryKey: projectQueryKey(client, ['build-game:interview-status', payload.gameId]),
      });
      void queryClient.invalidateQueries({
        queryKey: projectQueryKey(client, ['build-game:get-interview-state', payload.gameId]),
      });
    });
    return unsub;
  }, [gameId, queryClient]);

  // Deploy to TV via publish:run
  // Use a ref to avoid the race between publish:progress events and the
  // publish:run promise — if progress already resolved/errored, skip the catch.
  const deployResolvedRef = useRef(false);
  // Bugbot (PR #3237): SYNCHRONOUS in-flight latch for the async
  // confirm window. The old blocking window.confirm was itself a
  // re-entrancy barrier; the DOM modal is not. `deployState` only
  // flips to 'deploying' when React commits, so between the confirm
  // activation and that commit a second activation (double-click /
  // Enter repeat on the CTA — also latched component-side — or a
  // header Deploy click re-opening + re-confirming the modal) would
  // read the stale 'idle' closure and double-fire publish:run. The
  // ref is set before any await in handleDeployConfirmed and cleared
  // when the invoke settles, closing the pre-commit gap.
  const deployInFlightRef = useRef(false);
  // #3132 — apply the unit-pinned refusal update set
  // (refusedDeployStateUpdates) so a refused deploy invocation
  // surfaces on the pre-flight error banner with any stale failure
  // card yielded. Shared by the button-click gate and the confirm-time
  // re-check below.
  const surfaceDeployRefusal = useCallback((reason: DeployBlockReason) => {
    const refusal = refusedDeployStateUpdates(reason);
    setDeployState(refusal.deployState);
    setDeployMessage(refusal.deployMessage);
    setDeployErrorDismissed(refusal.deployErrorDismissed);
    setDeployFailureCardEnabled(refusal.deployFailureCardEnabled);
    // #3490 — headline the refusal's OWN plain copy. Also clear any
    // stale CrucibleError code AND the failed-step chip so a prior
    // failure can't shadow this refusal: the code drives the banner
    // headline, and localFailedCheck (newly set from a failed push
    // step this PR) drives the header "Failed step" chip — a refusal
    // must clear BOTH or the header keeps a stale chip from the last
    // attempt (Bugbot #3493).
    setDeployErrorCode(null);
    setLocalFailedCheck(null);
    setDeployRefusalCopy({ headline: refusal.bannerHeadline, nextStep: refusal.bannerNextStep });
  }, []);
  // #3494 — the first blocking precondition (build readiness OR a
  // git-derived cause like branch-mismatch), or null when ready. Drives
  // the belt-and-braces loud refusal in the click/confirm gates below,
  // matching the button's own disabled state (deployActionState).
  //
  // Bugbot round 4 on #3495 — "Post-deploy click refuses enabled
  // button": deriveDeployActionState scopes the readiness gate to
  // ONLY the idle/draft branch (deployed/deploying/error/starting all
  // bypass it, on purpose — redeploy/retry stay available regardless
  // of readiness). The belt-and-braces check below did NOT share that
  // scoping: once the #3495 invalidation correctly refreshes
  // `upToDate: true` right after a successful deploy,
  // `deployState === 'deployed'` + a stale-but-truthy precondition
  // would make a re-click of the (visually enabled, "Deployed")
  // button flip into an error-styled "already up to date" refusal —
  // a success surface silently turning into an error. Gate this
  // check on the SAME idle-only scope the classifier uses.
  // Evaluate the readiness precondition for `idle` AND `error`. `idle`
  // is the fresh-deploy path; `error` is the RETRY path (the
  // DeployFailureCard retry and the post-refusal "Retry deploy" button
  // both call handleDeploy while deployState === 'error'). Bugbot: an
  // earlier fix scoped this to idle-only to stop a `deployed` re-click
  // from flipping the success surface into an "already up to date"
  // refusal — but that also exempted `error`, so retry bypassed the
  // belt-and-braces check (not-built / wrong-branch / upToDate) entirely
  // and deployed regardless. Including `error` restores the check on
  // retry; `deployed`/`deploying`/`starting` stay exempt so a success or
  // in-flight surface never turns into a refusal.
  const deployPrecondition =
    deployState === 'idle' || deployState === 'error'
      ? evaluateDeployReadiness({
          buildReadiness: deployBuildReadiness,
          currentBranch: deployCurrentBranch,
          upToDate: deployUpToDate,
        })
      : null;
  const handleDeploy = useCallback(() => {
    // Re-entrancy guard: while 'deploying' the button is already
    // disabled and labelled "Deploying..." — a duplicate invocation
    // (double-click, stale UI) is visibly accounted for, not silent.
    // The ref covers the pre-commit window before 'deploying' lands.
    if (!gameId || deployState === 'deploying' || deployInFlightRef.current) return;
    // #3132 belt-and-braces: the button is disabled+explained on a
    // refused readiness state (deploy-action-state.ts), but other
    // invocation routes (keyboard focus on a stale render, the
    // DeployFailureCard retry, programmatic calls) must fail LOUD
    // rather than no-op — surface the refusal on the pre-flight error
    // banner instead of returning silently. The update set lives in
    // `refusedDeployStateUpdates` (unit-pinned): it also disables a
    // stale DeployFailureCard from a PRIOR CI failure, whose render
    // wins the banner mutex and would otherwise mask this refusal
    // entirely (Codex P1, PR #3237 round 1).
    if (deployPrecondition) {
      surfaceDeployRefusal(deployPrecondition.blockReason);
      return;
    }
    setDeployConfirmOpen(true);
  }, [gameId, deployState, deployPrecondition, surfaceDeployRefusal]);
  const handleDeployConfirmed = useCallback(async () => {
    setDeployConfirmOpen(false);
    // Synchronous re-entrancy latch — see deployInFlightRef above.
    if (deployInFlightRef.current) return;
    if (!gameId || deployState === 'deploying') return;
    // Bugbot (PR #3237): re-check readiness at CONFIRM time — the
    // interview status can flip while the modal is up (a rebuild
    // kicked off from chat, an auto-resume re-entering Phase B).
    // Routes to the same loud refusal surface as the button-click
    // gate; the useCallback dep keeps the closure's readiness current
    // as of the last render.
    if (deployPrecondition) {
      surfaceDeployRefusal(deployPrecondition.blockReason);
      return;
    }
    deployInFlightRef.current = true;
    deployAttemptIdRef.current += 1;
    protoHubOpenedDeployAttemptRef.current = null;
    deployResolvedRef.current = false;
    // Reset the CI-triggered gate at the start of every attempt
    // (Codex P2 round 1) — without this a previous attempt that
    // reached CI would leave the gate true, and a fresh attempt
    // that fails pre-CI (e.g. user revoked their PAT between
    // deploys) would still arm the card with stale annotations.
    ciTriggeredThisDeployRef.current = false;
    setDeployState('deploying');
    setDeployMessage('Preparing deployment...');
    // #2349 — a fresh deploy attempt clears a prior dismissal so the
    // pre-flight error banner re-surfaces if this attempt also fails.
    setDeployErrorDismissed(false);
    // Hide any stale failure card before kicking off a fresh deploy.
    // Without this the previous card would linger underneath the
    // LoadingOverlay and re-appear when the new deploy completes.
    setDeployFailureCardEnabled(false);
    // Clear the prior local pre-flight failure marker so an old
    // gate-failure chip doesn't linger underneath the fresh
    // deploy's LoadingOverlay (#415 Layer 3).
    setLocalFailedCheck(null);
    // #2080 — clear a prior failure's Crucible code so a stale
    // plain-language headline can't linger under the fresh deploy.
    setDeployErrorCode(null);
    // #3490 — a real deploy is now proceeding, so any prior readiness
    // refusal copy must yield; this attempt's success or failure owns
    // the banner from here.
    setDeployRefusalCopy(null);
    try {
      const result = await client.invoke('publish:run', { gameId });
      if (deployResolvedRef.current) return; // progress listener already handled it
      if (result.success) {
        setDeployState('deployed');
        setDeployMessage('Deployed successfully!');
        // #3495 Bugbot — invalidate the up-to-date cache on THIS success
        // path too, not only the publish:progress `complete` event
        // (line ~4642). publish:run can resolve success:true WITHOUT a
        // `done` progress event (e.g. the deployed-but-unreachable path
        // that emits a warning instead), which would otherwise leave a
        // stale verdict cached: a Workspace remount within the 30s TTL
        // would then misjudge readiness right after HEAD was pushed.
        invalidateDeployUpToDate(queryClient, client, gameId);
        openProtoHubAfterDeploy(gameId);
      } else {
        setDeployState('error');
        setDeployMessage(result.error ?? 'Deployment failed');
        // #277: arm the deploy-failure card on the publish:run promise
        // resolution path too — main may resolve with `success=false`
        // without ever firing a `publish:progress` error event (e.g.
        // a pre-flight refusal that returns a structured failure
        // payload rather than emitting). Gate on the CI-triggered
        // flag so pre-CI failures don't surface stale annotations
        // (Codex P2 round 1).
        if (ciTriggeredThisDeployRef.current) {
          setDeployFailureCardEnabled(true);
          refetchDeployFailure();
        }
      }
    } catch (err) {
      // #2080 — decode the CrucibleError ONCE and use it for BOTH the
      // banner-headline code AND the Technical-details message.
      // `unflattenIpcError` restores the structured shape Electron IPC
      // flattens into a `[CRUCIBLE_ERR_V1]` wire envelope; using the
      // decoded `.message` keeps that raw envelope out of the expander
      // (Bugbot #3493 — failed `step:push` deliberately omits a progress
      // `error`, so this catch owns the reason for wrong-branch / push
      // failures). A non-Crucible throw decodes to a clean plain message
      // with no code (generic headline). Set the code BEFORE the
      // deployResolvedRef guard: a pre-flight failure resolves via the
      // progress `error` event FIRST (setting deployResolvedRef) and
      // that event carries no code, so capturing it only after the guard
      // would lose it for every progress-resolved failure.
      const decoded = unflattenIpcError(err);
      setDeployErrorCode(decoded.code ?? null);
      if (deployResolvedRef.current) return; // progress listener already handled it
      setDeployState('error');
      setDeployMessage(decoded.message);
      // #277: thrown rejections (the common case for CI failures —
      // `publishGame` throws CRUCIBLE-501 when the workflow run
      // concludes failure) also need the card. Same gating as the
      // success-false branch above.
      if (ciTriggeredThisDeployRef.current) {
        setDeployFailureCardEnabled(true);
        refetchDeployFailure();
      }
    } finally {
      // Release the synchronous latch once the invoke settles — by now
      // deployState has committed ('deployed'/'error'), so the
      // state-based guards own any further re-entrancy; a NEW deploy
      // from here is legitimate (retry / redeploy).
      deployInFlightRef.current = false;
    }
  }, [
    gameId,
    deployState,
    deployPrecondition,
    surfaceDeployRefusal,
    openProtoHubAfterDeploy,
    refetchDeployFailure,
  ]);

  /**
   * Handler for the "Fix in agent" CTA on `<DeployFailureCard>`.
   * Receives the structured prompt from the IPC layer and dispatches
   * it through the chat reducer like the user typed it. The
   * dispatched user message goes through the same queue-while-busy
   * machinery any other user message does, so an in-flight agent
   * turn is correctly handled (the Fix-in-agent message gets
   * queued and drained when the active turn completes).
   */
  const handleFixInAgent = useCallback(
    (message: string) => {
      chatDispatch({ type: 'user/type', text: message });
      chatDispatch({ type: 'user/send' });
    },
    [chatDispatch],
  );

  const handleDismissDeployFailure = useCallback(() => {
    setDeployFailureCardEnabled(false);
  }, []);

  // Select-to-chat P1 (`handleAddSelectionToChat`), the "added to chat"
  // confirmation state (`selectionAddedHint` full-Preview tab pulse +
  // `attachmentPop` in-editor thumbnail pop), and the attachment-cap error
  // (`attachmentError`) now live in `useChatModule` (issue #2507); read them
  // from the chat-brain destructure above. Behaviour is unchanged — the
  // handler still routes a cropped selection through the paste pipeline
  // (`resizeImageFile` → `user/add-attachment`) and branches pulse-vs-pop on
  // the live `workspaceMode`.

  useEffect(() => {
    if (!isValidWorkspaceRouteGameId(gameId)) void navigate('/dashboard', { replace: true });
  }, [gameId, navigate]);

  if (!isValidWorkspaceRouteGameId(gameId)) return null;

  // Dedicated preview surface. In the new UI this is rendered only when
  // WorkspaceMode is "preview"; chat never owns preview frames or lifecycle
  // controls. The header action zone owns Stop / Reload / Restart.
  //
  // #2058 (Phase 2b) — THE TV-PREVIEW STEERING SURFACE. The art/asset approval
  // lives OFF the chat (Sean's surface model): during the build (game not built
  // yet), this preview pane is REPURPOSED to host the steering UI — the
  // moodboard pick-one + per-asset YES/NO — then flips back to the live game
  // preview once the game is built. `assetReviewCardGameId` is non-null exactly
  // while a review is in flight (set on `asset-review:ready`, cleared on done),
  // so we overlay `TvSteeringSurface` on the pane while it's set. It owns its
  // own snapshot poll; the chat shows only a small "action needed on the TV →"
  // nudge (the approval UI is NOT mirrored into the chat).
  // The preview VIEW (the `<PreviewOverlay>` composition) now lives in the
  // `PreviewModule` component (design doc §4 PR B). This closure keeps the
  // same signature + call sites (full-bleed Preview tab + the two north-star
  // override cases) so the render-tree position is unchanged; it just
  // delegates the ~85-line composition to the component.
  const renderPreviewSurface = (overrides?: PreviewModuleOverrides): React.JSX.Element => (
    <PreviewModule
      preview={preview}
      workspaceMode={workspaceMode}
      gameName={gameName}
      newUi={flags.newUi}
      overrides={overrides}
      onAddSelectionToChat={handleAddSelectionToChat}
      setPreviewToast={setPreviewToast}
      chatDispatch={chatDispatch}
    />
  );
  const previewSurfaceEl = renderPreviewSurface();
  // #2080 — plain-language headline for the pre-flight deploy-error
  // banner. A readiness REFUSAL (#3490) carries its own plain copy and
  // wins; otherwise the headline derives from the failure's
  // CrucibleError code. The raw `deployMessage` is preserved in the
  // banner's "Technical details" expander (for the code path), so this
  // is a friendly headline, not a replacement.
  const deployFailureBannerCopy =
    deployRefusalCopy ?? deployFailureCopy(deployErrorCode ?? undefined);
  // crucible#3501 / hw#2080 — "Report this problem" on the deploy-failure
  // banner. `buildDeployFailureBugReport` (pure, unit-tested) assembles the
  // same `buildGameBugReportUrl` input WorkspaceHeader's "Report a bug"
  // button uses, pre-filled with the CURRENT failure's step + error so the
  // reporter doesn't have to transcribe them by hand, and resolves `null`
  // on a readiness REFUSAL (guidance, not a bug — see the module doc).
  // `appFrame` / `deployReportFoundryVersion` are hook results computed
  // ABOVE the `isValidWorkspaceRouteGameId` early return (Bugbot round 1
  // on #3501 — hooks after an early return can change count between
  // renders); only the plain-value assembly lives here.
  const deployBugReport = buildDeployFailureBugReport({
    isReadinessRefusal: !!deployRefusalCopy,
    deployErrorCode,
    deployMessage,
    // Bugbot round 2 on #3501 — `visibleDeployFailureContext` is gated
    // on `deployFailureCardEnabled`, but this button lives in the
    // pre-flight BANNER, which only renders when that same flag is
    // FALSE (the two surfaces are mutually exclusive). The gated
    // variable is therefore always undefined right here; use the raw
    // `deployFailureContext` so the remote failing-step pre-fill is
    // actually reachable.
    failedStepFromContext: deployFailureContext?.failedJobs?.[0]?.failedStep,
    localFailedCheck,
    repoFullName: gameStatus?.githubRepository,
    gameId,
    gameName,
    foundryVersion:
      deployReportFoundryVersion === 'browser-spa-dev' ? undefined : deployReportFoundryVersion,
    gameVersion: gameStatus?.registry?.version,
    branch: deployCurrentBranch,
    placement: gameStatus?.placement,
    surface: appFrame === 'embedded' ? 'cloud' : 'desktop',
    workspaceMode,
    deployState,
  });
  const handleReportDeployProblem = (): void => {
    if (!deployBugReport) return;
    window.open(deployBugReport.url, '_blank', 'noopener,noreferrer');
  };
  const autoBuildProgressPreviewActive = autoBuildStatusFromRunState(workspaceRunState) !== null;
  // A ready Guided plan belongs to the editable approval surface, not the
  // preview shell. Dropping the create preview at this boundary also ensures
  // Consumer developer overrides cannot silently auto-dispatch the build.
  const northStarCreateHandoffActive = isNorthStarCreateHandoffActive(workspaceRunState.kind);
  const northStarCreatePreviewActive = shouldUseNorthStarCreatePreviewSurface({
    newUi: flags.newUi,
    northStar: flags.intake,
    hasGameId: !!gameId,
    soulIntakeActive,
    soulInterviewFinalizing,
    autoBuildProgressActive: autoBuildProgressPreviewActive,
    createHandoffActive: northStarCreateHandoffActive,
    reviewComplete: northStarReviewCompleteGameId === gameId,
  });
  // The "who owns the preview pane" predicate — deliberately narrower than
  // `northStarCreatePreviewActive` above. See `shouldUseNorthStarPreviewPaneSurface()`'s
  // doc comment (PR #4054 / incident #2314): every reader of "who owns the
  // preview pane" must derive from this value, NOT `northStarCreatePreviewActive`,
  // whose only remaining consumer is `DesignWorkspace`'s `consumerAutoBuild` prop.
  const northStarPreviewPaneActive = shouldUseNorthStarPreviewPaneSurface({
    newUi: flags.newUi,
    northStar: flags.intake,
    hasGameId: !!gameId,
    soulIntakeActive,
    soulInterviewFinalizing,
    reviewComplete: northStarReviewCompleteGameId === gameId,
  });
  const northStarCreateInterviewActive = shouldUseNorthStarInterviewSurface({
    soulIntakeActive,
    soulInterviewFinalizing,
    runStateKind: workspaceRunState.kind,
  });
  const northStarReviewComplete = northStarReviewCompleteGameId === gameId;
  const northStarBuildGateReleased = northStarBuildGateReleasedGameId === gameId;
  const northStarConsumerPreviewActive =
    assetReviewCardGameId !== null || northStarPreviewPaneActive;
  const workspacePreviewPaneActive = livePreviewSplitActive || northStarConsumerPreviewActive;

  const renderActivePreviewSurface = (railPanels?: NorthStarRailPanels): React.JSX.Element => {
    const withNorthStarRail = (driver: React.ReactNode): React.ReactNode =>
      railPanels ? <NorthStarRightRail driver={driver} {...railPanels} /> : undefined;

    if (assetReviewCardGameId) {
      return (
        <NorthStarConsumerSurface
          gameId={assetReviewCardGameId}
          placement={placement}
          onDone={(snapshot) => {
            setNorthStarReviewSummary(summarizeNorthStarReview(snapshot));
            setNorthStarReviewCompleteGameId(assetReviewCardGameId);
            setAssetReviewCardGameId(null);
          }}
          onOpenArt={() => setWorkspaceMode('art')}
          renderConsumerFrame={({ tv, phone }) =>
            renderPreviewSurface({
              displayOverride: tv,
              controllerOverride: railPanels ? undefined : phone,
              controllerPanelOverride: withNorthStarRail(phone),
            })
          }
        />
      );
    }

    if (northStarPreviewPaneActive && gameId) {
      if (!northStarReviewComplete && (northStarCreateInterviewActive || soulInterviewFinalizing)) {
        return (
          <div className="h-full min-h-0 w-full" data-testid="northstar-interview-desktop">
            <IntegratedSoulInterview
              gameId={gameId}
              onDone={handleSoulInterviewDone}
              onUnavailable={handleSoulInterviewUnavailable}
              onSnapshot={handleSoulInterviewSnapshot}
              surface="desktop"
            />
          </div>
        );
      }
      if (northStarReviewComplete) {
        const driver = (
          <NorthStarReviewCompletePhone
            buildGateReleased={northStarBuildGateReleased}
            summary={northStarReviewSummary}
          />
        );

        return renderPreviewSurface({
          displayOverride: (
            <NorthStarReviewCompleteTv
              buildGateReleased={northStarBuildGateReleased}
              summary={northStarReviewSummary}
            />
          ),
          controllerOverride: railPanels ? undefined : driver,
          controllerPanelOverride: withNorthStarRail(driver),
        });
      }
      // `northStarPreviewPaneActive` no longer includes auto-build-progress
      // or create-handoff (see `shouldUseNorthStarPreviewPaneSurface()`), so
      // this point is not reachable in practice today — the outer
      // `return previewSurfaceEl;` below already covers it.
    }

    return previewSurfaceEl;
  };
  const activePreviewSurfaceEl = renderActivePreviewSurface();

  // North-star uses the existing preview frame as the consumer surface. During
  // staged art review, the TV pane hosts build steering and the right rail hosts
  // the voice-first Driver/Foundry toggle. No separate left chat rail competes
  // with the TV proof.

  return (
    <div className="flex h-full flex-col">
      {/* `<WhatsNewCard />` is now mounted at the App shell (see App.tsx)
          so Dashboard-only users see release notes too. Issue #293. */}

      {/* #32 build-time asset review — create auto-navigates HERE (the workspace)
          and the build runs from here, so the stage-by-stage per-asset review must
          surface ON the workspace where the user actually is. Listens for
          `asset-review:ready` and overlays the panel. The Dashboard deliberately
          does not mount this workspace-only surface (crucible#3813).
          #2058 — suppressed when the integrated flag is ON; the in-chat
          ChatTvSteeringNudge (→ TvSteeringSurface) takes over the review
          surface in that case. */}
      {/* crucible#2611 Task 4: suppress the floating build-review modal
          whenever the Art tab is open — it already mounts its own in-place
          AssetReviewPanel for this game's review, so the modal popping up
          over it would be a redundant, conflicting second review surface. */}
      <BuildReviewOverlay suppressed={buildReviewOverlaySuppressed(flags.intake, workspaceMode)} />

      {/* Deploy loading overlay. #663 — pins crucible_bifrost.mp4 as the
          intro clip so it plays the moment the send-to-Bifrost starts
          (deployState flips to 'deploying' immediately before publish:run
          fires), then falls back to the loading loop for the rest of the
          deploy while the rolling deployMessage status renders over it. */}
      <LoadingOverlay
        message={deployMessage}
        isVisible={deployState === 'deploying'}
        introClip={crucibleBifrost}
      />

      {/* Preview overlay moved to the end of this tree — see the
          PreviewOverlay block right before the closing </div>. The
          earlier position here caused Electron's drag-region hit-test
          to award the WorkspaceHeader's `app-region-drag` over the
          overlay's `app-region-no-drag` because the OS compositor
          resolves overlapping regions by DOM order (later sibling
          wins), independent of visual z-index. Issue #1016 part C —
          recurring regression of #531 / #804. */}

      {/* Crucible #548 — user-gated dependency-add approval modal.
          Rendered above the workspace chrome whenever the
          `requestDependency` MCP tool has pushed a request (queued in
          `pendingDependencyRequests`). The modal blocks all chat
          interaction until the user picks Install / Decline because
          the agent's turn is stalled waiting on the gate. Multiple
          concurrent requests stack — the modal always shows the
          first entry; subsequent entries surface as that entry
          resolves. */}
      {pendingDependencyRequests.length > 0 && pendingDependencyRequests[0] && (
        <DependencyApprovalModal
          key={pendingDependencyRequests[0].sessionId}
          packageName={pendingDependencyRequests[0].package}
          requestedVersion={pendingDependencyRequests[0].version}
          reason={pendingDependencyRequests[0].reason}
          scope={pendingDependencyRequests[0].scope}
          requestedAt={pendingDependencyRequests[0].requestedAt}
          onApprove={(finalVersion) => {
            const session = pendingDependencyRequests[0];
            if (!session) return;
            chatDispatch({
              type: 'deps/approve',
              sessionId: session.sessionId,
              finalVersion,
            });
          }}
          onDecline={(reason) => {
            const session = pendingDependencyRequests[0];
            if (!session) return;
            chatDispatch({
              type: 'deps/decline',
              sessionId: session.sessionId,
              reason,
            });
          }}
        />
      )}

      {/* Crucible #3491 — user-gated template-file-override approval
          modal. Same mount pattern as the dependency-approval modal
          above (queued, first-entry-shown), but for delete requests
          against a template-managed file under packages/**. */}
      {pendingTemplateFileOverrideRequests.length > 0 && pendingTemplateFileOverrideRequests[0] && (
        <TemplateFileOverrideModal
          key={pendingTemplateFileOverrideRequests[0].sessionId}
          path={pendingTemplateFileOverrideRequests[0].path}
          reason={pendingTemplateFileOverrideRequests[0].reason}
          requestedAt={pendingTemplateFileOverrideRequests[0].requestedAt}
          onApprove={() => {
            const session = pendingTemplateFileOverrideRequests[0];
            if (!session) return;
            chatDispatch({
              type: 'template-file-override/approve',
              sessionId: session.sessionId,
            });
          }}
          onDecline={(reason) => {
            const session = pendingTemplateFileOverrideRequests[0];
            if (!session) return;
            chatDispatch({
              type: 'template-file-override/decline',
              sessionId: session.sessionId,
              reason,
            });
          }}
        />
      )}

      {/* Editor error toast */}
      {editorError && (
        <div className="fixed right-4 top-4 z-40 max-w-sm rounded-lg border border-red-500/30 bg-red-900/80 px-4 py-3 text-sm text-red-200 shadow-lg backdrop-blur-sm">
          {editorError}
        </div>
      )}

      {/* Issue #643 — preview recovery toast. Info/success auto-fade
          after 4s (cleared by the effect on `[previewToast]`).
          Error toasts are STICKY with a dismiss button — a 4s failure
          notice is too easy to miss when the user is staring at the
          overlay's centred error card (Codex review #1 BLOCK).
          Tone-coded so success / info / error read distinctly at a
          glance. Sits slightly lower than the editor-error toast so
          both can be on-screen at once without overlapping —
          vanishingly unlikely but harmless. */}
      {/* Issue #643 — preview recovery toast migrated to ui/InlineToast
          (#2404). Three-tier z-index plan remains unchanged:
            z-50    — PreviewOverlay (fullscreen iframe pane)
            z-[55]  — this preview-recovery toast (above overlay, below modals)
            z-[60]  — user-blocking modals (DependencyApprovalModal et al.) */}
      {previewToast && (
        <InlineToast
          tone={previewToast.tone}
          data-testid="preview-toast"
          data-tone={previewToast.tone}
          onDismiss={previewToast.tone === 'error' ? () => setPreviewToast(null) : undefined}
        >
          {previewToast.message}
        </InlineToast>
      )}

      {/* Issue #597 — auto-seed banner. Non-blocking informational
          toast that fires once on the Workspace-open IPC when main
          auto-seeds the interview state for a cloned game. Sky tone
          (not red) so the user reads it as "Foundry did the right
          thing for me" rather than a failure. Stacks below the
          editor-error toast on the chance both fire on the same
          mount (vanishingly unlikely but the `top-28` offset is
          deliberate to keep them from overlapping). Auto-fades after
          8s — no dismiss button by design. */}
      {autoSeededBanner && <AutoSeededToast message={autoSeededBanner} newUi={flags.newUi} />}

      {/* crucible#2715 — Art tab ruleset-protected-main branch+PR fallback
          notice. `top-40` keeps it clear of the editor-error (top-4),
          preview/plan-mode-error (top-16), and auto-seeded (top-28)
          toasts on the chance more than one fires at once.

          crucible#3497 review round 3 (MEDIUM) — `strandedOnBranch`
          dominates the tone/dismiss decision via `protectedMainFallbackIsError`
          regardless of `armed`; see that derived value's own docblock above. */}
      {protectedMainFallbackToast && protectedMainFallbackView && (
        <InlineToast
          tone={protectedMainFallbackView.tone}
          data-testid="art-deploy-fallback-pr-toast"
          data-tone={protectedMainFallbackView.tone}
          className="top-40"
          onDismiss={
            protectedMainFallbackView.dismissible
              ? () => setProtectedMainFallbackToast(null)
              : undefined
          }
        >
          {protectedMainFallbackView.message}{' '}
          <a
            href={protectedMainFallbackToast.url}
            target="_blank"
            rel="noreferrer"
            className="app-region-no-drag underline"
          >
            Open PR
          </a>
        </InlineToast>
      )}

      {/* PR #544 follow-up — Start Building / Back to plan IPC error
          toast. Rendered at page-chrome scope so it surfaces in BOTH
          plan-mode (full-screen `<PlanModeView>`) and build-mode (header
          Back-to-plan button). Stacks below the editor-error toast when
          both fire; the `top-16` offset is deliberate so they don't
          overlap. */}
      {planModeError && (
        <div
          data-testid="plan-mode-error-toast"
          role="alert"
          className="fixed right-4 top-16 z-40 max-w-sm rounded-lg border border-red-500/30 bg-red-900/80 px-4 py-3 text-sm text-red-200 shadow-lg backdrop-blur-sm"
        >
          {planModeError}
        </div>
      )}

      <UpdateTemplateModal
        gameId={gameId}
        open={showTemplateModal}
        onClose={() => setShowTemplateModal(false)}
        // Issue #391: when the user picks "Resolve with the agent" on
        // the conflict picker, dispatch the synthesised prompt through
        // the chat reducer the same way a typed user message would land
        // — `user/type` populates the composer, `user/send` consumes it
        // and emits `ipc/agent-send` via the effect interpreter. Going
        // through the reducer (instead of invoking `agent:send` directly
        // from here) means the user sees their request in the chat
        // timeline, the chat-history persists it, and the in-flight /
        // queue semantics behave the same as if they'd typed it.
        onResolveWithAgent={(conflictedFiles) => {
          const prompt = synthesiseConflictResolutionPrompt(conflictedFiles);
          chatDispatch({ type: 'user/type', text: prompt });
          chatDispatch({ type: 'user/send' });
        }}
      />

      {/* Header */}
      <WorkspaceHeader
        gameId={gameId}
        gameName={gameName}
        deployState={deployState}
        deployActionState={deployActionState}
        selectionAddedHint={selectionAddedHint}
        activeMode={workspaceMode}
        onChat={() => setWorkspaceMode('chat')}
        onDesign={designWorkspaceEnabled ? () => setWorkspaceMode('design') : undefined}
        onArt={
          flags.loaded &&
          flags.artTab &&
          flags.newUi &&
          !!gameId &&
          projectCapabilityAvailable('art') &&
          isProsumerTier
            ? () => setWorkspaceMode('art')
            : undefined
        }
        artDisabledReason={
          flags.loaded && flags.artTab && flags.newUi && !!gameId && isProsumerTier
            ? (projectCapability('art')?.reason ?? undefined)
            : undefined
        }
        designDisabledReason={
          flags.designTab && flags.newUi && !!gameId && isProsumerTier
            ? (projectCapability('design')?.reason ?? undefined)
            : undefined
        }
        editorDisabledReason={projectCapability('editor')?.reason ?? undefined}
        // #3144 / #3145 — the project-settings panel's rename + tile-upload
        // affordances have no cloud-runtime handler (`game.set-display-name@1`
        // / `game.set-tile-image@1` are unregistered on the pod). Gate them on
        // the same project-capability signal as Open-in-Cursor so cloud
        // placement surfaces a human reason instead of a silent revert (#3144)
        // or a raw wire error (#3145).
        renameDisabledReason={projectCapability('rename')?.reason ?? undefined}
        tileEditDisabledReason={projectCapability('tile-edit')?.reason ?? undefined}
        gameStatus={gameStatus}
        statusBadge={gameStatusLite.data?.statusBadge ?? 'draft'}
        repoUrl={clonedRepoFullName ? `https://github.com/${clonedRepoFullName}` : undefined}
        cloneDeployAccessWarning={cloneDeployAccessWarning}
        deployFailureContext={visibleDeployFailureContext}
        localFailedCheck={localFailedCheck}
        activitySnapshot={activitySnapshot}
        interviewStatus={interviewStatus}
        onPreviewTab={handlePreviewTabClick}
        onProtoHub={handleDisplayOnHub}
        onLivePreview={
          flags.newUi && flags.livePreview && !!gameId
            ? preview.handlers.activateDevelop
            : undefined
        }
        livePreviewSplitActive={livePreviewSplitActive}
        previewLoading={preview.derived.launchButtonLoading}
        previewMenuEnabled={preview.state.status === 'ready'}
        previewForceDisabled={previewForceDisabledResolved}
        previewDisabledReason={previewDisabledReasonResolved}
        previewAvailableTitle={previewAvailableTitle}
        inAppPairingAvailable={preview.derived.inAppPairingAvailable}
        inAppPairingNextLaunch={preview.derived.weekendPairingNextLaunch}
        onInAppPairingNextLaunchChange={preview.derived.setWeekendPairingNextLaunch}
        previewPhaseGate={
          phaseProgress?.kind === 'derived'
            ? {
                currentPhase: phaseProgress.derived.current,
                phaseLabel: phaseProgress.derived.label,
              }
            : undefined
        }
        onDeploy={handleDeploy}
        // WorkspaceRunState owns aggregate CTA visibility so the
        // header, banner, and plan/build chrome do not drift.
        showBackToPlanButton={workspaceRunState.showBackToPlan}
        backToPlanBusy={planModeBusy}
        onBackToPlanRequest={handleBackToPlanRequest}
        runState={workspaceRunState}
        cloudVmEnabled={featureFlags.loaded && featureFlags.cloudVmV1}
        maintainerMode={showVmDebugPane}
        contributors={gameContributors}
        rightPanelOpen={rightPanelOpen}
        // The rail can only occupy the right slot when the artifact pane
        // isn't already there (or we're in a build, where the artifact
        // pane isn't rendered). Pass `undefined` when the slot is taken so
        // the header hides the toggle entirely — otherwise the toggle's
        // accent/"Close" state would lie (claim the rail is open while the
        // artifact pane is actually filling the slot) and clicking it would
        // be a no-op. When the toggle IS shown, `rightPanelOpen` maps 1:1
        // to rail visibility. (Codex stop-review: "toggle state can lie".)
        onToggleRightPanel={
          workspaceMode === 'preview' || workspaceMode === 'protohub' || !artifactPaneOpen
            ? () => setRightPanelOpen((v) => !v)
            : undefined
        }
        furnaceBuildState={furnaceBuildState}
      />

      {placement === 'local' && localReadiness.state === 'degraded' && (
        <div
          role="status"
          data-testid="workspace-local-setup-banner"
          className="flex items-center justify-between gap-4 border-b border-amber-500/20 bg-amber-500/[0.07] px-5 py-2 text-xs text-amber-100/80"
        >
          <span>
            <strong className="font-medium text-amber-100">Setup needs attention.</strong> Existing
            files remain available; build, preview, sync, and publish actions are enabled only when
            their requirements are ready.
          </span>
          <div className="flex shrink-0 gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => navigate('/settings?tab=connections')}
            >
              Connections
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() =>
                navigate(
                  `/settings?tab=diagnostics&gameId=${encodeURIComponent(gameId ?? '')}&placement=${placement}`,
                )
              }
            >
              Diagnostics
            </Button>
          </div>
        </div>
      )}

      {/* Template update banner — below the header so the header stays flush
          with the top of the window (traffic lights align with the header). */}
      <TemplateUpdateBanner
        gameId={gameId}
        onUpdateClick={() => setShowTemplateModal(true)}
        newUi={flags.newUi}
        enabled={projectCapabilityAvailable('template-update')}
      />

      {/* crucible#3041/#3516 — the split-create Design lane's deferred
          "Build this" trigger. Rendered at the top-of-workspace band (like
          the banners around it) rather than inside any single workspaceMode
          arm, so it is reachable from every tab while the create is
          deferred — NO STRANDING is the whole point of this affordance. */}
      {gameId && showBuildThisDeferredDesign && (
        <DeferredDesignBuildBanner
          pending={deferredDesignBuildStatus === 'pending'}
          onBuildClick={() => void handleBuildThisDeferredDesign()}
        />
      )}

      {/* Preview/install recovery banner. Workspace owns the combined
          "retry install, then start preview" CTA so the old install chip does
          not compete with the dev-loop state spine. */}
      {preview.derived.devLoopState.kind === 'install-needed' && (
        <div className="px-6 pt-3">
          <PreviewDevLoopBanner
            state={preview.derived.devLoopState}
            onPrimary={preview.handlers.launch}
          />
        </div>
      )}

      {/* Issue #637 — ambient deploy-status card. Sits in the same
          top-of-workspace band as the deploy-failure card below, fed
          by the `deploy:status` push the main-side watcher emits.
          Suppressed when the deploy-failure card is showing (both
          would render a red "failed" block — the failure card is the
          richer surface with annotations + Fix-in-agent). Gated on the
          shared `deployStatusHasContent` predicate — NOT merely a truthy
          `deployStatus` — so the bordered wrapper never mounts for a
          quiet `idle` snapshot the card would render as null (Bugbot
          round 5 — empty bordered band; #E made idle more common).
          Flag-OFF (`CRUCIBLE_DEPLOY_WATCH` != '1') `deployStatus` is
          always undefined so nothing mounts. */}
      {gameId &&
        deployStatusHasContent(deployStatus) &&
        !(deployFailureCardEnabled && deployFailureContext?.status === 'failure') && (
          <div className="border-b border-white/10 bg-black/20 px-6 py-3">
            <DeployStatusCard status={deployStatus} />
          </div>
        )}

      {/* Issue #277 — `<DeployFailureCard>` renders OUTSIDE the
          ChatPanel's hasAnthropicKey gate (Cursor Bugbot round 1).
          Users without an Anthropic key still need to see deploy
          failures with the workflow run link + View on GitHub CTA;
          they just can't use Fix-in-agent. The card itself disables
          the Fix-in-agent button when no failedJobs are present —
          here the card mounts in a top-of-workspace band so it
          surfaces regardless of which centre column variant is
          showing.

          The "Fix in agent" CTA still dispatches into the chat
          reducer; on no-Anthropic-key workspaces the dispatch
          ends up queued in chatState but never consumed (no
          chat panel to render it). That's an acceptable
          degradation — the user without a key wouldn't be able
          to act on the prompt anyway. The other two CTAs
          (View on GitHub, Retry deploy) stay fully functional. */}
      {gameId && deployFailureCardEnabled && deployFailureContext?.status === 'failure' && (
        <div className="border-b border-white/10 bg-black/20 px-6 py-3">
          <DeployFailureCard
            gameId={gameId}
            context={deployFailureContext}
            actionState={deployActionState}
            hasAnthropicKey={hasAnthropicKey}
            onFixInAgent={handleFixInAgent}
            onDismiss={handleDismissDeployFailure}
            onRetry={handleDeploy}
          />
        </div>
      )}

      {/* #2349 — pre-flight / non-CI deploy-error banner. The
          `<DeployFailureCard>` above only arms once CI was triggered,
          and `deployMessage` otherwise only renders inside the
          LoadingOverlay (which hides the moment the deploy leaves the
          'deploying' state). So a deploy that fails BEFORE CI — an
          uncommitted-changes refusal (CRUCIBLE-202), a missing remote
          (CRUCIBLE-203), a local quality-gate failure (CRUCIBLE-210),
          or a thrown pre-flight error — used to surface nothing at all.
          This dismissable banner fills that gap: it renders the actual
          `deployMessage` text whenever the deploy is in the error state
          AND the failure card is NOT showing, so the two paths never
          double-render. Cleared on dismiss and on the next Deploy
          click (which resets `deployErrorDismissed`). */}
      {deployState === 'error' &&
        !deployErrorDismissed &&
        !(deployFailureCardEnabled && deployFailureContext?.status === 'failure') && (
          <div
            className="border-b px-6 py-3"
            style={{
              borderColor: 'var(--color-error-border)',
              background: 'var(--color-error-subtle)',
            }}
          >
            <div
              data-testid="deploy-preflight-error"
              role="alert"
              className="flex items-start gap-2 rounded-lg border px-4 py-3 text-sm"
              style={{
                borderColor: 'var(--color-error-border)',
                background: 'var(--color-error-subtle)',
                color: 'var(--color-error)',
              }}
            >
              <span className="flex-1">
                {/* #2080 — plain-language headline (no jargon), keyed
                    off the failure's CrucibleError code. The raw
                    technical message lives in the expander below so a
                    developer can still read the exact error. */}
                <span className="font-medium" data-testid="deploy-preflight-error-headline">
                  {deployFailureBannerCopy.headline}
                </span>
                {deployFailureBannerCopy.nextStep && (
                  <span
                    className="mt-1 block opacity-90"
                    data-testid="deploy-preflight-error-next-step"
                  >
                    {deployFailureBannerCopy.nextStep}
                  </span>
                )}
                {/* #3490 — for a readiness refusal the plain reason IS
                    the headline, so the raw `deployMessage` would just
                    duplicate it; only show the technical expander on the
                    code-driven failure path. */}
                {deployMessage && !deployRefusalCopy && (
                  <details className="mt-1.5" data-testid="deploy-preflight-error-technical">
                    <summary className="cursor-pointer text-xs opacity-70 hover:opacity-100">
                      Technical details
                    </summary>
                    <span className="mt-1 block whitespace-pre-wrap break-words font-mono text-xs opacity-80">
                      {deployErrorCode ? `${deployErrorCode}: ` : ''}
                      {deployMessage}
                    </span>
                  </details>
                )}
                {/* crucible#3501 / hw#2080 — `buildDeployFailureBugReport`
                    already resolves null on a readiness refusal (guidance,
                    not a bug) and when no GitHub origin is configured. */}
                {deployBugReport && (
                  <Button
                    onClick={handleReportDeployProblem}
                    data-testid="deploy-preflight-report-problem"
                    aria-label={`Report a bug for ${gameName}`}
                    variant="ghost"
                    size="sm"
                    className="mt-1.5 h-auto px-0 py-0 text-xs opacity-80 hover:bg-transparent hover:opacity-100"
                    style={{ color: 'var(--color-error)' }}
                  >
                    <Bug size={11} strokeWidth={1.8} />
                    Report this problem
                  </Button>
                )}
              </span>
              <Button
                onClick={() => setDeployErrorDismissed(true)}
                data-testid="deploy-preflight-error-dismiss"
                aria-label="Dismiss"
                variant="ghost"
                size="icon"
                className="-mt-0.5 -mr-1 h-6 w-6 shrink-0 p-0.5 opacity-70 hover:opacity-100"
                style={{ color: 'var(--color-error)' }}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  aria-hidden="true"
                >
                  <path d="M18 6L6 18" />
                  <path d="M6 6l12 12" />
                </svg>
              </Button>
            </div>
          </div>
        )}

      {/* Crucible #416 Chunk C — phase strip. Sticky above the
          chat scroll region; renders all seven phase chips per the
          v10 ASCII mockup, plus a click-to-expand detail panel.
          Stays mounted on every game with a workspace open (the
          hook degrades to a skeleton until the first IPC arrives)
          and tears down its watcher on game switch. */}
      {gameId && (
        <>
          {/* Crucible #519: sticky bail-out warning. Surfaces when the
              SKILL recorded a budget-bail-out as 'built-partial'. Renders
              above the strip so it can't get scrolled past. Null-render
              when no bail-out happened (no chrome cost).
              `key={gameId}` forces a fresh mount on game switch so a
              dismissal on game A doesn't suppress game B's banner —
              Workspace is reused across `/workspace/:gameId` route
              changes (see the route-keep-mounted comment above).
              Crucible #796: derive skipped sub-phase IDs from the phase
              progress wire so the banner can list them by name instead of
              asking the user to grep the JSON file. */}
          <BuildPartialBanner
            key={gameId}
            gameId={gameId}
            status={interviewStatus}
            skippedSubPhases={
              phaseProgress?.kind === 'derived'
                ? // Deduplicate by subPhase — the SKILL could theoretically
                  // emit two transitions with budgetSkipped for the same
                  // sub-phase (e.g. a retry that also bails). Preserve the
                  // first-seen order; Array.from(Set) is stable in JS.
                  Array.from(
                    new Set(
                      phaseProgress.derived.transitions
                        .filter((t) => t.budgetSkipped === true)
                        .map((t) => t.subPhase),
                    ),
                  )
                : undefined
            }
          />
        </>
      )}

      {/* Three-panel layout (two-panel when no Anthropic key — the
          AI chat panel depends on the Anthropic SDK and is useless
          without a key. We hide it entirely rather than render a
          disabled placeholder: cleaner for users who haven't opted
          in, and users who set a key later get the panel back on
          next workspace open. Settings surfaces a hint that the
          Anthropic key unlocks the in-app chat so the feature is
          discoverable). */}
      <div className="relative flex flex-1 overflow-hidden">
        {/* Legacy UI left column — Files OR VM Debug pane (B-1). When
            the engineer launched Foundry with `CRUCIBLE_MAINTAINER_MODE=1`
            AND the `app:is-maintainer` IPC has resolved, the left
            column renders the maintainer-only VM Debug pane in place
            of the files tree. Gate lives at this call site so the
            pane component itself stays SSR-friendly for tests that
            render `<Workspace>` without a `QueryClientProvider`.
            (A-1's lesson learned — see useMaintainerMode docstring.)
            Flag-OFF: the `<FilesPanel>` renders unchanged.
            crucible#3214 — the new UI hides this column entirely, and
            that's an intentional simplification (Sean, confirmed
            2026-07-16), not a gap: WorkspaceRightPanel carries no
            files/file-tree surface, and none is planned. */}
        {!flags.newUi &&
          (showVmDebugPane ? (
            <VmDebugPane />
          ) : projectCapabilityAvailable('files') ? (
            <FilesPanel
              gameId={gameId}
              collapsed={filesCollapsed}
              width={leftWidth}
              onToggleCollapse={() => setFilesCollapsed(!filesCollapsed)}
              onResize={setLeftWidth}
            />
          ) : (
            <div className="flex w-[260px] items-center border-r border-white/10 p-4 text-sm text-white/50">
              {projectCapability('files')?.reason ??
                'Files are unavailable for this project runtime.'}
            </div>
          ))}

        {/* Centre: AI Chat panel when an Anthropic key is configured;
            otherwise an inline placeholder pointing at Settings (audit §4
            / top-3 ranked fix — the previous behaviour silently
            disappeared the centre column when no key was set, leaving
            users with no clue why). While `tokens:get` is in flight we
            render an empty centre column so users with a key never
            briefly see the "needs setup" placeholder (the IPC almost
            always resolves in <50ms but the flicker is real on cold
            launch).

            #191 quick-batch: when the interview is in quick mode AND
            the SKILL has flipped subPhase to plan / refine /
            back-to-plan, replace the ChatPanel with a full-screen
            plan-mode view. Build / undefined subPhase falls through
            to the normal chat surface so the user always sees agent
            chatter once specialists start running.

            #671: the four mounts below are wrapped in
            `<CentreColumnWithBanner>` so the transparency banner
            band (TemplateTooOldBanner + ActivityBanner) sits above
            whichever mount is active. The shell is the canonical
            CRUCIBLE_AGENT_TRANSPARENCY flag-gate per design §12
            Issue E — when the flag is OFF, the shell renders just
            the children and the centre column behaves exactly as
            before. `key={gameId}` remounts the subtree on cross-
            game navigation as belt-and-braces for the snapshot
            hook's session-discard rules. `suppressBanner=
            {!tokensResolved}` keeps the loader fork visually clean
            per design plan §2 ("Tokens-not-resolved fork: the
            banner does NOT render"). */}
        {/* crucible#2990 — ONE error boundary around the ENTIRE centre column
            (all five mode arms). A render throw anywhere below must surface as
            a visible error card (renderCentreColumnFallback) instead of
            unmounting the Workspace or leaving a silent blank column. The
            Design/Art arms keep their own inner boundaries with
            surface-specific fallbacks; this outer boundary is the catch-all
            for the chat/plan/preview/protohub arms and anything the inner
            boundaries miss. `key={gameId}` remounts the boundary (and every
            inner boundary under it) on an in-place game switch — the
            Workspace persists across route-param changes, so an unkeyed
            boundary would stay latched on `hasError` and one broken game's
            error card would poison sibling workspaces (Codex P2 on this
            PR). */}
        <ChatErrorBoundary key={gameId} gameId={gameId} renderFallback={renderCentreColumnFallback}>
          {designContentVisible ? (
            <div className="relative min-w-0 flex-1 overflow-hidden">
              <ChatErrorBoundary gameId={gameId} renderFallback={renderDesignWorkspaceFallback}>
                <DesignWorkspace
                  gameId={gameId ?? ''}
                  buildPlan={designBuildPlan}
                  onSentToBuild={handleDesignStartBuild}
                  onSendUpdate={handleSendDesignUpdate}
                  autoBuildStatus={autoBuildState?.status ?? null}
                  consumerAutoBuild={northStarCreatePreviewActive}
                  buildActivity={{
                    runState: workspaceRunState,
                    phaseProgress,
                    activitySnapshot,
                  }}
                  onGoToArt={
                    flags.loaded && flags.artTab && flags.newUi && !!gameId && isProsumerTier
                      ? () => setWorkspaceMode('art')
                      : undefined
                  }
                  levelDesignPro={flags.loaded && flags.levelDesignPro}
                />
              </ChatErrorBoundary>
            </div>
          ) : previewModeVisible ? (
            <div className="relative min-w-0 flex-1 overflow-hidden">
              {previewPaneCopy ? (
                <PreviewBlockedPane
                  headline={previewPaneCopy.headline}
                  body={previewPaneCopy.body}
                />
              ) : (
                <>
                  {activePreviewSurfaceEl}
                  {/* Live-preview scaffold-first (A2.3b/c): the one-tap genre chip,
                      overlaid on the preview. Only when scaffold-first is active AND
                      an inferred-genre outcome is available — durably from
                      crucible.json (game:status) or the create-session route state.
                      The "change" action is a LIGHTWEIGHT start-over: navigate to New
                      Game pre-filled with this game's original prompt (cached at
                      create) so the user re-describes to get a different genre. The
                      explicit recast marker means a missing / garbage cached prompt
                      still starts over from a BLANK form — never an unrelated stored
                      draft. (The heavier in-place re-cast — interrupt + re-overlay —
                      is deferred.) */}
                  {shouldShowGenreChip(flags.scaffoldFirstActive, previewGenreInference) ? (
                    <div className="pointer-events-none absolute left-3 top-3 z-10">
                      <div className="pointer-events-auto">
                        <ScaffoldFirstGenreChip
                          inference={previewGenreInference}
                          onChange={() => {
                            void navigate('/new-game', {
                              state: buildRecastRouteState(readCreatePrompt(gameId)),
                            });
                          }}
                        />
                      </div>
                    </div>
                  ) : null}
                </>
              )}
            </div>
          ) : protoHubModeVisible ? (
            <div className="relative min-w-0 flex-1 overflow-hidden">
              <ProtoHubSimulatorSurface
                target={placement}
                source={protoHubSource}
                gameId={gameId}
                onClose={() => setWorkspaceMode('chat')}
              />
            </div>
          ) : artModeVisible ? (
            <div className="relative min-w-0 flex-1 overflow-hidden">
              <ChatErrorBoundary gameId={gameId} renderFallback={renderArtWorkspaceFallback}>
                <ArtWorkspace gameId={gameId ?? ''} />
              </ChatErrorBoundary>
            </div>
          ) : (
            <CentreColumnWithBanner
              gameId={gameId}
              key={gameId}
              suppressBanner={!tokensResolved}
              chatAgents={agents}
              chatMainAgent={mainAgent}
              runState={workspaceRunState}
            >
              {/* Bridge-3 — actionable Furnace failure banner. Gate
                on `phase === 'failed'`, NOT on
                `routedToFurnace && latch !== null`. The build-state
                derivation already prioritises an active turn over a
                stale latch (see `computeFurnaceBuildState` rule 2);
                keying the banner off the SAME phase the indicator
                reads keeps the two surfaces in lock-step. Without
                this, a queued message that auto-drains after an
                error (chat reducer's queue-drain on `chunk: error`)
                can flash the failure banner over the next live
                turn between "isAiTyping flips true" and "passive
                latch-reset effect fires" — Codex round-2 follow-up. */}
              <FurnaceErrorBanner
                view={furnaceBuildState.phase === 'failed' ? furnaceLastFailure : null}
                onPrimaryCta={(kind) => {
                  switch (kind) {
                    case 'retry': {
                      // Re-dispatch the most-recent errored turn. Find
                      // the latest assistant message that carries an
                      // `error` and replay against its id — the chat
                      // reducer's `user/retry` action resolves the
                      // preceding user message and re-sends. Clear the
                      // failure latch FIRST so the banner unmounts even
                      // if the retry fails to fire (defence-in-depth
                      // against a stale-error UX).
                      setFurnaceLastFailure(null);
                      const erroredMessage = [...messages]
                        .reverse()
                        .find((m) => m.role === 'assistant' && m.error !== undefined);
                      if (erroredMessage) {
                        handleRetry(erroredMessage);
                      }
                      return;
                    }
                    case 'open-diagnostics':
                    case 'check-cohort':
                    case 'check-connections':
                      // All three CTAs route to the Settings page —
                      // the engineer lands on the appropriate section
                      // (Diagnostics / Advanced / Connections) but the
                      // navigation entry-point is the same. Future
                      // anchor links can deep-link more precisely.
                      void navigate('/settings');
                      return;
                  }
                }}
                onDismiss={() => setFurnaceLastFailure(null)}
              />
              {tokensResolved &&
                hasAnthropicKey &&
                planModeVisible &&
                (flags.newUi ? (
                  <PlanWorkspace
                    gameId={gameId}
                    gameName={gameName}
                    plan={interviewPlan}
                    answeredCount={interviewStatusQuery.data?.answeredCount}
                    totalCount={interviewStatusQuery.data?.totalCount}
                    planRevision={interviewState?.planRevision}
                    planHash={interviewState?.planHash}
                    planUpdatedAt={interviewState?.planUpdatedAt}
                    planSource={interviewState?.planSource}
                    lastBuiltPlanRevision={interviewState?.lastBuiltPlanRevision}
                    lastBuiltPlanHash={interviewState?.lastBuiltPlanHash}
                    buildComplete={workspaceRunState.kind === 'done'}
                    buildCompletionUnverified={buildCompletionUnverified}
                    buildRequestPending={workspaceRunState.planRevisionLocked}
                    busy={isAiTyping || planModeBusy}
                    effectiveModel={workspaceConfig?.claudeModel}
                    onEditModel={() => navigate('/settings')}
                    onSavePlan={handleSavePlan}
                    onStartBuilding={handleStartBuilding}
                    onRebuildFromPlan={handleRebuildFromPlan}
                    onBackToChat={() => setWorkspaceMode('chat')}
                    onSubmitRefinement={handlePlanRefinement}
                    onStartOver={handlePlanStartOver}
                  />
                ) : showPlanModeView ? (
                  <PlanModeView
                    gameId={gameId}
                    plan={interviewPlan}
                    subPhase={
                      interviewSubPhase === 'plan' ||
                      interviewSubPhase === 'refine' ||
                      interviewSubPhase === 'back-to-plan'
                        ? interviewSubPhase
                        : 'plan'
                    }
                    refinementBusy={isAiTyping || planModeBusy}
                    startBuildingBusy={planModeBusy}
                    draftingStartedAt={draftingStartedAt}
                    draftingStoppedAt={draftingStoppedAt}
                    effectiveModel={workspaceConfig?.claudeModel}
                    onEditModel={() => navigate('/settings')}
                    onSubmitRefinement={handlePlanRefinement}
                    onStartBuilding={handleStartBuilding}
                    onStartOver={handlePlanStartOver}
                  />
                ) : null)}
              {tokensResolved &&
                hasAnthropicKey &&
                chatModeVisible &&
                (() => {
                  /* Codex review fix — flag-OFF DOM parity: with
                   `CRUCIBLE_LIVE_PREVIEW` unset, ChatPanel renders UNWRAPPED,
                   byte-identical to the pre-split tree. Flag-ON gets the
                   wrapper: `display: contents` while the split is closed (no
                   box, same DOM parent across split toggles — no remount, no
                   IPC resubscribe), the flex dock constraint while open. */
                  const chatPanelEl = (
                    <ChatPanel
                      gameId={gameId}
                      gameName={gameName}
                      messages={messages}
                      chatInput={chatInput}
                      isAiTyping={isAiTyping}
                      runState={workspaceRunState}
                      activitySnapshot={activitySnapshot}
                      phaseProgress={phaseProgress}
                      buildLiveness={buildLiveness}
                      onInputChange={handleChatInputChange}
                      onSend={handleSendMessage}
                      onStop={handleStopMessage}
                      queuedMessages={queuedMessages}
                      onUnqueueMessage={handleUnqueueMessage}
                      persistError={chatPersistError}
                      onDismissPersistError={handleDismissPersistError}
                      hydrationStatus={hydrationStatus}
                      hydrationError={hydrationError}
                      hydrationLongRunning={hydrationLongRunning}
                      onRetryHydration={handleRetryHydration}
                      pendingAttachments={pendingAttachments}
                      attachmentPop={attachmentPop}
                      onAddAttachment={handleAddAttachment}
                      onRemoveAttachment={handleRemoveAttachment}
                      attachmentError={attachmentError}
                      onDismissAttachmentError={handleDismissAttachmentError}
                      answerError={composerAnswerError}
                      onDismissAnswerError={handleDismissAnswerError}
                      lastTurnSpend={lastTurnSpend}
                      showCostInChat={showCostInChat}
                      onAnswerMarkdownQuestion={handleAnswerMarkdownQuestion}
                      onRetryMessage={handleRetry}
                      onCancelAutoBuild={handleCancelAutoBuild}
                      onRetryAutoBuild={handleRetryAutoBuild}
                      pinnedAskUserQuestion={pinnedAskUserQuestion}
                      pendingPhaseBDecision={pendingPhaseBDecision}
                      interviewSlotActive={interviewSlotActive}
                      onInterviewRendererReady={handleInterviewRendererReady}
                      interviewReadyHandshakeAttempt={interviewReadyHandshakeAttempt}
                      localSidecarsAvailable={localFilesystemEffectsAvailable}
                      // In the build view the artifact pane is not rendered, so
                      // it is never "active" there regardless of interview status.
                      isArtifactPaneActive={
                        artifactPaneOpen &&
                        workspaceMode !== 'preview' &&
                        !workspacePreviewPaneActive
                      }
                      showReviewModal={showReviewModal}
                      onCloseReviewModal={handleCloseReviewModal}
                      // #2058 — in-chat asset review card. Non-null when the
                      // integrated flag is ON and asset-review:ready fired.
                      assetReviewCardGameId={flags.intake ? assetReviewCardGameId : null}
                      onAssetReviewDone={handleAssetReviewDone}
                      // #2058 — in-chat adaptive interview. Non-null while a soul-intake run
                      // is active; hosts IntegratedSoulInterview inline in the chat stream.
                      soulInterviewGameId={
                        flags.intake && soulIntakeActive && gameId && !northStarPreviewPaneActive
                          ? gameId
                          : null
                      }
                      onSoulInterviewDone={handleSoulInterviewDone}
                      northStarCreatePreviewActive={northStarPreviewPaneActive}
                      northStarCreateInterviewActive={northStarCreateInterviewActive}
                      // Develop side-by-side: the chat lives in a narrow panel,
                      // so drop the wide centred max-width and tighten padding.
                      compact={workspacePreviewPaneActive}
                    />
                  );
                  const statePanelEl = (
                    <WorkspaceRightPanel
                      variant="pinned"
                      open
                      onClose={NOOP}
                      phaseProgress={phaseProgress}
                      activitySnapshot={activitySnapshot}
                      runState={workspaceRunState}
                      interviewState={interviewState}
                      interviewSummary={interviewStatusQuery.data}
                      buildLiveness={buildLiveness}
                      onResumeBuild={
                        resumeBuildActionKind === 'retry-interview'
                          ? handleRetryInitialInterview
                          : resumeBuildActionKind === 'start-building'
                            ? handleResumeBuildKickoff
                            : resumeBuildActionKind === 'retry-auto-build'
                              ? handleRetryAutoBuild
                              : resumeBuildActionKind === 'rebuild-from-plan'
                                ? handleRebuildFromPlan
                                : undefined
                      }
                      onEditDiscovery={() => {
                        setArtifactSoftClosed(false);
                        setChatTab('chat');
                      }}
                    />
                  );
                  const diagnosticsPanelEl = (
                    <DiagnosticsTab diagnostics={preview.derived.diagnostics} />
                  );
                  const chatPreviewSurfaceEl = renderActivePreviewSurface({
                    chatPanel: chatPanelEl,
                    statePanel: statePanelEl,
                    diagnosticsPanel: diagnosticsPanelEl,
                    chatTab,
                    onChatTabChange: setChatTab,
                  });
                  return (
                    <WorkspaceChatSurface
                      flagsLoaded={flags.loaded}
                      newUi={flags.newUi}
                      livePreview={flags.livePreview}
                      chatPanel={chatPanelEl}
                      chatTab={chatTab}
                      onChatTabChange={setChatTab}
                      statePanel={statePanelEl}
                      diagnosticsPanel={diagnosticsPanelEl}
                      previewSurface={chatPreviewSurfaceEl}
                      previewSurfaceActive={northStarConsumerPreviewActive}
                      previewSurfaceOwnsSecondary={northStarConsumerPreviewActive}
                      livePreviewSplitActive={livePreviewSplitActive}
                      livePreviewChatWidth={livePreviewChatWidth}
                      onLivePreviewChatWidthChange={setLivePreviewChatWidthForGame}
                    />
                  );
                })()}
              {tokensResolved && !hasAnthropicKey && (
                <ChatPanelPlaceholder
                  onOpenSettings={() => navigate('/settings?tab=connections')}
                  // Codex review (2026-05-01): chatPersistError must surface
                  // even in the no-Anthropic-key branch. A board member can
                  // hit chat-history corruption (CHAT-CORRUPT / CHAT-SHAPE)
                  // before they've ever pasted a key — a previous app run
                  // might have errored out, and the next launch must show
                  // them the recovery card and the backup path. The earlier
                  // gating left them with a blank "needs setup" placeholder
                  // and no clue why.
                  persistError={chatPersistError}
                  onDismissPersistError={() => setChatPersistError(null)}
                />
              )}
              {/* crucible#2990 — this fork used to render an EMPTY section (an
                  anti-flicker choice from when tokens:get was a <50ms local
                  IPC). In cloud mode it is a wire RPC that can take seconds —
                  or retry through failures — and the invisible fork read as a
                  silently blank centre column. The spinner + label reveal
                  after a 400ms CSS delay, so the fast desktop path stays
                  flicker-free while a genuinely slow load is VISIBLY loading,
                  never blank. */}
              {!tokensResolved && (
                <section
                  className="flex flex-1 items-center justify-center overflow-hidden"
                  aria-busy="true"
                  aria-label="Loading Build panel"
                  data-testid="workspace-tokens-loading"
                >
                  <div className="centre-column-loading-reveal flex flex-col items-center gap-3">
                    <div className="h-6 w-6 animate-spin rounded-full border-2 border-white/30 border-t-crucible-accent" />
                    <p className="text-sm text-white/55">Loading the Build panel…</p>
                  </div>
                </section>
              )}
            </CentreColumnWithBanner>
          )}
        </ChatErrorBoundary>

        {/* Artifact split-pane — new UI only. Persists mounted so CSS
            width-transition can animate smoothly. */}
        {flags.newUi && workspaceMode !== 'preview' && !workspacePreviewPaneActive && gameId && (
          <ArtifactPane
            isOpen={artifactPaneOpen}
            onClose={() => setArtifactSoftClosed(true)}
            title={interviewStatus === 'complete' ? 'Game Plan' : 'Discovery'}
          >
            {artifactPaneOpen && interviewStatus === 'complete' && (
              <GamePlanView
                gameName={gameName}
                answeredCount={interviewStatusQuery.data?.answeredCount ?? 0}
                totalCount={interviewStatusQuery.data?.totalCount ?? 0}
                plan={interviewStateQuery.data?.plan ?? null}
              />
            )}
          </ArtifactPane>
        )}

        {/* The pinned activity rail (progress + live sub-agent panel) moved
            INTO the chat panel as the "State" tab (see the ChatTabBar in the
            chat-mode render above). It is no longer a standalone right
            column. */}

        {/* #2058 (Phase 2b) — THE TV-PREVIEW STEERING SURFACE. The art/asset
            approval lives OFF the chat and INTO the existing TV/mobile preview
            frame. New UI routes through `activePreviewSurfaceEl`; this legacy
            fallback exists only for old flag paths that lack PreviewOverlay in
            chat mode. */}
        {assetReviewCardGameId && !flags.newUi && (
          <div className="absolute inset-0 z-10 overflow-hidden" data-testid="tv-steering-mount">
            <TvSteeringSurface
              gameId={assetReviewCardGameId}
              placement={placement}
              onDone={(snapshot) => {
                setNorthStarReviewSummary(summarizeNorthStarReview(snapshot));
                setAssetReviewCardGameId(null);
              }}
            />
          </div>
        )}
      </div>

      {/* #191 quick-batch — confirmation modal for the back-to-plan
          escape hatch. Mounted unconditionally; `open` controls
          visibility so the modal's keydown listener is only active
          when the modal is open (`useEffect` early-returns on
          `!open`). */}
      <BackToPlanConfirmModal
        open={backToPlanModalOpen}
        onCancel={handleBackToPlanCancel}
        onConfirm={handleBackToPlanConfirm}
      />

      {/* #3132 — in-app Deploy-to-TV confirmation (replaces the native
          window.confirm that CDP-driven flows auto-dismissed into a
          silent decline). Mounted unconditionally; `open` controls
          visibility, same as BackToPlanConfirmModal above. */}
      <DeployConfirmModal
        open={deployConfirmOpen}
        onCancel={() => setDeployConfirmOpen(false)}
        onConfirm={() => void handleDeployConfirmed()}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Files Panel (left, collapsible)
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Chat Panel (centre, primary)
// ---------------------------------------------------------------------------

// ─────────────────────────────────────────────────────────────────────────────
// Game Plan view — shown in the ArtifactPane once the interview is complete.
// Renders a structured design-spec document derived from the interview answers.
// ─────────────────────────────────────────────────────────────────────────────

function GamePlanSection({ heading, body }: { heading: string; body: string }): React.JSX.Element {
  return (
    <div>
      <p
        className="mb-1 text-[10px] font-semibold uppercase tracking-[0.12em]"
        style={{ color: 'var(--text-3)' }}
      >
        {heading}
      </p>
      <p className="text-[13px] leading-relaxed" style={{ color: 'var(--text-1)' }}>
        {body}
      </p>
    </div>
  );
}

function GamePlanView({
  gameName,
  answeredCount,
  totalCount,
  plan,
}: {
  gameName: string;
  answeredCount: number;
  totalCount: number;
  /** Markdown plan written by the SKILL after quick-batch discovery.
   *  Null for the legacy conversational flow which produces no plan doc. */
  plan: string | null;
}): React.JSX.Element {
  return (
    <div className="space-y-5">
      <div>
        <p className="text-[10px] uppercase tracking-[0.12em]" style={{ color: 'var(--text-3)' }}>
          Game Design Spec
        </p>
        <h2 className="mt-1 text-base font-medium" style={{ color: 'var(--text-1)' }}>
          {gameName}
        </h2>
      </div>
      <div style={{ height: '1px', background: 'var(--border-alpha-1)' }} />
      {plan ? (
        <div className="text-[13px] leading-relaxed" style={{ color: 'var(--text-1)' }}>
          <MessageContent content={plan} />
        </div>
      ) : (
        <p className="text-[13px] leading-relaxed" style={{ color: 'var(--text-2)' }}>
          Discovery complete. The full game design spec will be visible here once the plan is
          drafted.
        </p>
      )}
      <div style={{ height: '1px', background: 'var(--border-alpha-1)' }} />
      <p className="text-[11px]" style={{ color: 'var(--text-3)' }}>
        Discovery complete — {answeredCount}/{totalCount} questions answered
      </p>
    </div>
  );
}

type NorthStarReviewSummary = {
  approved: number;
  reviewable: number;
  stageCount: number;
  complete: boolean;
  finished: boolean;
  fullReviewComplete: boolean;
  previews: Array<{
    id: string;
    label: string;
    imageUrl: string;
  }>;
};

function reviewAssetLabel(asset: AssetReviewItem): string {
  const words = asset.id
    .split(/[\s_-]+/g)
    .filter(Boolean)
    .filter((word) => !['keyframe', 'sprite', 'portrait', 'image', 'asset'].includes(word))
    .slice(0, 2);
  if (words.length === 0) return asset.category;
  return words.map((word) => `${word.slice(0, 1).toUpperCase()}${word.slice(1)}`).join(' ');
}

function summarizeNorthStarReview(snapshot: AssetReviewSnapshot): NorthStarReviewSummary {
  const approvedAssets = snapshot.assets.filter((asset) => asset.status === 'approved');
  const imageAssets = approvedAssets.filter(
    (asset) => asset.imageUrl && asset.category !== 'moodboard',
  );
  const previews = (imageAssets.length > 0 ? imageAssets : approvedAssets)
    .filter((asset): asset is AssetReviewItem & { imageUrl: string } => Boolean(asset.imageUrl))
    .slice(0, 4)
    .map((asset) => ({
      id: asset.id,
      label: reviewAssetLabel(asset),
      imageUrl: asset.imageUrl,
    }));
  return {
    approved: snapshot.approved,
    reviewable: snapshot.reviewable,
    stageCount: snapshot.stages?.length ?? 0,
    complete: snapshot.complete,
    finished: snapshot.finished,
    fullReviewComplete: snapshot.complete && snapshot.reviewable > 0,
    previews,
  };
}

function NorthStarReviewCompleteTv({
  buildGateReleased,
  summary,
}: {
  buildGateReleased: boolean;
  summary: NorthStarReviewSummary | null;
}): React.JSX.Element {
  const fullReviewComplete = summary?.fullReviewComplete === true;
  return (
    <section
      className="flex h-full w-full flex-col justify-center overflow-hidden p-8"
      data-testid="northstar-review-complete-tv"
      data-build-gate-released={buildGateReleased ? 'true' : 'false'}
      style={{ background: 'var(--color-crucible-bg)' }}
    >
      <div className="max-w-4xl">
        <p className="text-[13px] font-semibold uppercase tracking-[0.18em] text-crucible-accent">
          {buildGateReleased
            ? 'Build gate released'
            : fullReviewComplete
              ? 'Art review complete'
              : 'Review proof captured'}
        </p>
        <h1 className="mt-4 text-4xl font-light leading-tight text-white/92">
          {buildGateReleased
            ? 'Approved art is cleared for the build'
            : fullReviewComplete
              ? 'All generated art has been reviewed'
              : 'Representative generated art has been reviewed'}
        </h1>
        <p className="mt-4 max-w-2xl text-lg leading-relaxed text-white/56">
          {buildGateReleased
            ? fullReviewComplete
              ? 'The held build turn released after every generated look was approved.'
              : 'The held build turn released after representative Asset Forge evidence was approved and exported. Full-manifest review still needs a dedicated run.'
            : fullReviewComplete
              ? 'The build is finishing with the approved art choices. The game preview will return when the build is ready.'
              : 'The proof approved representative Asset Forge output and released the build gate after export evidence. A full-manifest review still needs a dedicated run.'}
        </p>
        {summary && (
          <div
            className="mt-8 flex max-w-5xl items-center gap-5 border-l border-crucible-accent/70 pl-5"
            data-testid="northstar-review-evidence"
          >
            <div className="shrink-0">
              <p className="text-[12px] font-semibold uppercase tracking-[0.18em] text-white/38">
                Approved art
              </p>
              <p
                className="mt-1 text-2xl font-semibold text-white/90"
                data-testid="northstar-review-approved-count"
              >
                {summary.approved} approved
              </p>
              <p className="mt-1 text-sm text-white/48" data-testid="northstar-review-stage-count">
                {summary.stageCount} stages cleared
              </p>
            </div>
            {summary.previews.length > 0 && (
              <div
                className="grid min-w-0 flex-1 grid-cols-4 gap-3"
                data-testid="northstar-review-approved-previews"
              >
                {summary.previews.map((asset) => (
                  <figure
                    key={asset.id}
                    className="min-w-0 overflow-hidden border border-white/10 bg-white/[0.035]"
                    data-testid={`northstar-review-preview-${asset.id}`}
                  >
                    <div className="aspect-video bg-black/25">
                      <img
                        src={asset.imageUrl}
                        alt={`${asset.label} approved art`}
                        className="h-full w-full object-contain"
                        draggable={false}
                      />
                    </div>
                    <figcaption className="truncate px-2 py-1.5 text-[12px] font-semibold text-white/72">
                      {asset.label}
                    </figcaption>
                  </figure>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

function NorthStarReviewCompletePhone({
  buildGateReleased,
  summary,
}: {
  buildGateReleased: boolean;
  summary: NorthStarReviewSummary | null;
}): React.JSX.Element {
  const fullReviewComplete = summary?.fullReviewComplete === true;
  return (
    <section
      className="flex h-full w-full flex-col justify-center overflow-hidden px-4 py-5"
      data-testid="northstar-review-complete-phone"
      data-build-gate-released={buildGateReleased ? 'true' : 'false'}
      style={{ background: 'var(--color-crucible-bg)' }}
    >
      <div className="rounded-2xl border border-white/10 bg-white/[0.035] px-4 py-5">
        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-crucible-accent">
          {buildGateReleased ? 'Released' : fullReviewComplete ? 'Done' : 'Proof'}
        </p>
        <p className="mt-3 text-lg font-semibold leading-snug text-white/88">
          {buildGateReleased
            ? 'Build gate released'
            : fullReviewComplete
              ? 'Art review complete'
              : 'Review proof captured'}
        </p>
        <p className="mt-2 text-sm leading-snug text-white/50">Foundry is finishing the build.</p>
        {summary && (
          <div
            className="mt-4 grid grid-cols-2 gap-2"
            data-testid="northstar-review-phone-evidence"
          >
            <div className="border-l border-crucible-accent/60 pl-2">
              <p className="text-[10px] uppercase tracking-[0.14em] text-white/36">Approved</p>
              <p className="mt-1 text-sm font-semibold text-white/84">{summary.approved}</p>
            </div>
            <div className="border-l border-white/15 pl-2">
              <p className="text-[10px] uppercase tracking-[0.14em] text-white/36">Stages</p>
              <p className="mt-1 text-sm font-semibold text-white/84">{summary.stageCount}</p>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Chat surface + North Star right rail
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Resize Handle (shared)
// ---------------------------------------------------------------------------
